import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../utils/api';
import { useUser } from '../../contexts/UserContext';
const transliterateToPhoneticEnglish = (text) => {
    if (!text) return '';

    // 1. Tamil Script Processing: Convert Spoken English -> Clean English & Spoken Tamil -> Tanglish
    if (/[\u0B80-\u0BFF]/.test(text)) {
        const englishPhoneticsInTamil = {
            'ஹொவ்': 'How', 'ஹவ்': 'How',
            'ஆர்': 'are',
            'யு': 'you', 'யூ': 'you',
            'ஃபைன்': 'fine', 'பைன்': 'fine', 'வைன்': 'fine',
            'வாட்': 'what',
            'அபொட்': 'about', 'அபௌட்': 'about',
            'ஐ': 'I',
            'அம்': 'am',
            'ஹலோ': 'Hello', 'ஹாய்': 'Hi',
            'தேங்க்ஸ்': 'Thanks', 'தேங்க் யூ': 'Thank you',
            'ஒகே': 'OK', 'ஓகே': 'OK',
            'எஸ்': 'Yes', 'நோ': 'No',
            'குட்': 'Good',
            'மார்னிங்': 'morning', 'ஆஃப்டர்நூன்': 'afternoon', 'ஈவினிங்': 'evening',
            'மீட்டிங்': 'meeting', 'கால்': 'call',
            'ஸ்கிரீன்': 'screen', 'ஷேர்': 'share',
            'ஆடியோ': 'audio', 'வீடியோ': 'video',
            'ப்ளீஸ்': 'please', 'வெல்கம்': 'welcome',
            'ஆல்ரைட்': 'all right', 'பாய்': 'bye'
        };

        // Replace English phonetic words written in Tamil script with English words
        let processedText = text;
        Object.entries(englishPhoneticsInTamil).forEach(([tamilWord, englishWord]) => {
            const regex = new RegExp(tamilWord, 'g');
            processedText = processedText.replace(regex, englishWord);
        });

        // If text is now pure English, return formatted English
        if (!/[\u0B80-\u0BFF]/.test(processedText)) {
            const cleaned = processedText.replace(/\s+/g, ' ').trim();
            return cleaned.length > 0 ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : cleaned;
        }

        // Convert native Tamil words to clean Tanglish
        const vowels = {
            'அ': 'a', 'ஆ': 'aa', 'இ': 'i', 'ஈ': 'ee', 'உ': 'u', 'ஊ': 'oo', 'எ': 'e', 'ஏ': 'ae', 'ஐ': 'ai', 'ஒ': 'o', 'ஓ': 'oa', 'ஔ': 'au', 'ஃ': 'k'
        };
        const consonants = {
            'க': 'k', 'ங': 'ng', 'ச': 's', 'ஞ': 'nj', 'ட': 't', 'ண': 'n', 'த': 'th', 'ந': 'n', 'ப': 'p', 'ம': 'm',
            'ய': 'y', 'ர': 'r', 'ல': 'l', 'வ': 'v', 'ழ': 'zh', 'ள': 'l', 'ற': 'r', 'ன': 'n', 'ஜ': 'j', 'ஷ': 'sh', 'ஸ': 's', 'ஹ': 'h'
        };
        const matras = {
            'ா': 'aa', 'ἱ': 'i', 'ீ': 'ee', 'ு': 'u', 'ூ': 'oo', 'ெ': 'e', 'ே': 'ae', 'ை': 'ai', 'ொ': 'o', 'ோ': 'oa', 'ௌ': 'au', '்': ''
        };

        let res = '';
        for (let i = 0; i < processedText.length; i++) {
            const c = processedText[i];
            if (vowels[c]) {
                res += vowels[c];
            } else if (consonants[c]) {
                const next = processedText[i + 1];
                if (next && matras[next] !== undefined) {
                    res += consonants[c] + matras[next];
                    i++;
                } else {
                    res += consonants[c] + 'a';
                }
            } else {
                res += c;
            }
        }
        const cleaned = res.replace(/\s+/g, ' ').trim();
        return cleaned.length > 0 ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : cleaned;
    }

    // 2. Hindi Script Transliteration (\u0900-\u097F) -> Phonetic Roman Script (Hinglish)
    if (/[\u0900-\u097F]/.test(text)) {
        const vowels = {
            'अ': 'a', 'आ': 'aa', 'इ': 'i', 'ई': 'ee', 'उ': 'u', 'ऊ': 'oo', 'ए': 'e', 'ऐ': 'ai', 'ओ': 'o', 'औ': 'au'
        };
        const consonants = {
            'क': 'k', 'ख': 'kh', 'ग': 'g', 'घ': 'gh', 'ङ': 'ng',
            'च': 'ch', 'छ': 'chh', 'ज': 'j', 'झ': 'jh', 'ञ': 'nya',
            'ट': 't', 'ठ': 'th', 'ड': 'd', 'ढ': 'dh', 'ण': 'n',
            'त': 't', 'थ': 'th', 'द': 'd', 'ध': 'dh', 'न': 'n',
            'प': 'p', 'फ': 'ph', 'ब': 'b', 'भ': 'bh', 'म': 'm',
            'य': 'y', 'र': 'r', 'ल': 'l', 'व': 'v', 'श': 'sh', 'ष': 'sh', 'स': 's', 'ह': 'h'
        };
        const matras = {
            'ा': 'aa', 'ि': 'i', 'ी': 'ee', 'ु': 'u', 'ू': 'oo', 'े': 'e', 'ै': 'ai', 'ो': 'o', 'ौ': 'au', '्': '', 'ं': 'n', 'ँ': 'n'
        };

        let res = '';
        for (let i = 0; i < text.length; i++) {
            const c = text[i];
            if (vowels[c]) {
                res += vowels[c];
            } else if (consonants[c]) {
                const next = text[i + 1];
                if (next && matras[next] !== undefined) {
                    res += consonants[c] + matras[next];
                    i++;
                } else {
                    res += consonants[c] + 'a';
                }
            } else {
                res += c;
            }
        }
        const cleaned = res.replace(/\s+/g, ' ').trim();
        return cleaned.length > 0 ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : cleaned;
    }

    // 3. Spoken English formatting with sentence capitalization and punctuation
    let formatted = text.trim();
    if (formatted.length > 0) {
        formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
    }
    return formatted;
};

const CollabMeetingRoom = ({ meetingId, meetingTitle, onLeave }) => {
    const { user } = useUser();
    const [micActive, setMicActive] = useState(true);
    const [camActive, setCamActive] = useState(true);
    const [screenShareActive, setScreenShareActive] = useState(false);
    const [recordingActive, setRecordingActive] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('ai');
    const [messages, setMessages] = useState([
        { sender: 'System', text: 'Secure workspace active. All calls are local and encrypted.', time: '12:00 PM' },
    ]);
    const [newMessage, setNewMessage] = useState('');
    const [unreadMessages, setUnreadMessages] = useState(0);
    const [unreadPollsQa, setUnreadPollsQa] = useState(0);
    const [lastMessageCount, setLastMessageCount] = useState(1);
    const [raisedHands, setRaisedHands] = useState({});
    const [recordingCountdown, setRecordingCountdown] = useState(null);
    const [recordingTimer, setRecordingTimer] = useState(0);
    
    const [transcript, setTranscript] = useState([]);
    const [aiProcessing, setAiProcessing] = useState(false);
    const [aiSummary, setAiSummary] = useState('');
    const [aiActionItems, setAiActionItems] = useState([]);
    const [showShareMenu, setShowShareMenu] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailInviteTo, setEmailInviteTo] = useState('');
    const [emailInviteSubject, setEmailInviteSubject] = useState('Join my collaborative meeting');
    const [emailInviteBody, setEmailInviteBody] = useState('');
    const [emailInviteSending, setEmailInviteSending] = useState(false);

    const [cameraAvailable, setCameraAvailable] = useState(true);
    const [participants, setParticipants] = useState([]);
    const [joinLeaveToast, setJoinLeaveToast] = useState(null);
    const prevParticipantsRef = useRef(null);
    const isInitialParticipantFetchRef = useRef(true);
    const [showMoreMenu, setShowMoreMenu] = useState(false);

    // Waiting-room / approval states
    const [joinStatus, setJoinStatus] = useState(null); // null=loading, 'approved', 'pending', 'rejected', 'not_started'
    const [isHost, setIsHost] = useState(false);
    const [meetingStartTime, setMeetingStartTime] = useState(null);
    const [pendingRequests, setPendingRequests] = useState([]); // guests waiting – visible to host only
    const [pinnedUserId, setPinnedUserId] = useState(null);
    const [precallNetworkStatus, setPrecallNetworkStatus] = useState('Checking...');
    const precallStreamRef = useRef(null);
    const precallVideoRef = useRef(null);
    const precallAudioCtxRef = useRef(null);
    const precallAudioRafRef = useRef(null);
    const [precallAudioLevel, setPrecallAudioLevel] = useState(0);
    const [cameraVerified, setCameraVerified] = useState(false);
    const [audioVerified, setAudioVerified] = useState(false);
    
    // Unified enterprise details
    const [meetingDetails, setMeetingDetails] = useState(null);
    const [checkInStatus, setCheckInStatus] = useState(null);
    const [viewMode, setViewMode] = useState('gallery'); // 'gallery' | 'speaker'
    const [currentPage, setCurrentPage] = useState(0);
    const MAX_PER_PAGE = 8; // 8 remote + 1 local = 9 max per page

    const meetingType = meetingDetails?.meeting_type;
    const isPhysical = meetingType === 'physical';
    const isGoogleMeet = meetingType === 'hybrid_google_meet' || (meetingType === 'hybrid' && meetingDetails?.external_meeting_url?.toLowerCase().includes('meet.google.com'));
    const isZoom = meetingType === 'hybrid_zoom' || (meetingType === 'hybrid' && !isGoogleMeet);
    const isHybrid = isGoogleMeet || isZoom || meetingType === 'hybrid';

    // Meeting-ready toast & Add People dialog
    const [showMeetingReadyToast, setShowMeetingReadyToast] = useState(false);
    const [showAddPeopleModal, setShowAddPeopleModal] = useState(false);
    const [addPeopleSearch, setAddPeopleSearch] = useState('');
    const [addPeopleSuggestions, setAddPeopleSuggestions] = useState([]);
    const [addPeopleInvited, setAddPeopleInvited] = useState([]);
    const [linkCopied, setLinkCopied] = useState(false);

    // Video tile context menu
    const [tileMenu, setTileMenu] = useState(null); // { userId, x, y }
    const [minimisedTiles, setMinimisedTiles] = useState([]); // list of user_ids minimised
    const [showFullVideoForAll, setShowFullVideoForAll] = useState(false); // show my full video to others
    const [pinnedScope, setPinnedScope] = useState('myself'); // 'myself' | 'everyone'

    // Polls & Q&A States
    const [polls, setPolls] = useState({});
    const [qaList, setQaList] = useState([]);
    const [newPoll, setNewPoll] = useState({ question: '', options: ['', ''] });
    const [showCreatePollForm, setShowCreatePollForm] = useState(false);
    const [newQaText, setNewQaText] = useState('');
    const [activeQaTab, setActiveQaTab] = useState('polls'); // 'polls' or 'qa'
    const [speechLang, setSpeechLang] = useState('ta-IN'); // 'ta-IN' for Tamil, 'en-IN' for Tanglish/English

    // Whiteboard States
    const [whiteboardActive, setWhiteboardActive] = useState(false);
    const [wbTool, setWbTool] = useState('marker'); // 'marker', 'highlighter', 'eraser'
    const [wbColor, setWbColor] = useState('#000000');
    const [wbSize, setWbSize] = useState(4);
    const isDrawingRef = useRef(false);
    const lastPosRef = useRef({ x: 0, y: 0 });
    const wbToolRef = useRef(wbTool);
    const wbColorRef = useRef(wbColor);
    const wbSizeRef = useRef(wbSize);
    wbToolRef.current = wbTool;
    wbColorRef.current = wbColor;
    wbSizeRef.current = wbSize;
    const canvasRef = useRef(null);
    const pendingStrokesRef = useRef([]);
    const allStrokesRef = useRef([]);
    const initialChatsLoadedRef = useRef(false);
    const lastProcessedChatIdRef = useRef(0);
    const lastPollCountRef = useRef(0);
    const lastQaCountRef = useRef(0);

    const [alertPopup, setAlertPopup] = useState({ show: false, message: '', title: 'Notification' });
    const [confirmPopup, setConfirmPopup] = useState({ show: false, message: '', title: 'Confirm', onConfirm: null });
    const [showHostMenu, setShowHostMenu] = useState(false);
    const [meetingLocked, setMeetingLocked] = useState(false);
    const [handRaised, setHandRaised] = useState(false);
    
    
    const videoRef = useRef(null);
    const cameraPreviewRef = useRef(null);
    const streamRef = useRef(null);
    const screenStreamRef = useRef(null);
    const isMediaInitializedRef = useRef(false);
    const [mediaInitialized, setMediaInitialized] = useState(false);
    const mediaRecorderRef = useRef(null);
    const recordingStreamRef = useRef(null);
    const recordedChunksRef = useRef([]);
    const recordingDisplayStreamRef = useRef(null);
    const recordingMicStreamRef = useRef(null);
    const recordingAudioCtxRef = useRef(null);
    const recordingTimerRef = useRef(null);
    const speechRecognitionRef = useRef(null);
    const lastDisplayedTranscriptRef = useRef(null);
    // WebRTC
    const peerConnectionsRef = useRef({}); // { userId: RTCPeerConnection }
    const iceCandidateQueuesRef = useRef({});
    const makingOfferRef = useRef({});
    const ignoreOfferRef = useRef({});
    const [remoteStreams, setRemoteStreams] = useState({}); // { userId: MediaStream }
    const myUserIdRef = useRef(null);

    const sidebarOpenRef = useRef(sidebarOpen);
    const activeTabRef = useRef(activeTab);

    // Clear unread messages when chat or polls tab is opened
    useEffect(() => {
        sidebarOpenRef.current = sidebarOpen;
        activeTabRef.current = activeTab;
        if (activeTab === 'chat' && sidebarOpen) {
            setUnreadMessages(0);
        }
        if (activeTab === 'polls' && sidebarOpen) {
            setUnreadPollsQa(0);
        }
    }, [activeTab, sidebarOpen]);

    // Aggressive cleanup on unmount to ensure cameras and mics are released
    useEffect(() => {
        return () => {
            const stopTracks = (stream) => {
                if (stream && stream.getTracks) {
                    stream.getTracks().forEach(track => {
                        track.enabled = false;
                        track.stop();
                    });
                }
            };
            stopTracks(streamRef.current);
            stopTracks(screenStreamRef.current);
            stopTracks(recordingStreamRef.current);
            stopTracks(recordingDisplayStreamRef.current);
            stopTracks(recordingMicStreamRef.current);
            if (recordingAudioCtxRef.current) {
                try {
                    if (recordingAudioCtxRef.current.state !== 'closed') {
                        recordingAudioCtxRef.current.close();
                    }
                } catch (err) {}
                recordingAudioCtxRef.current = null;
            }
            if (videoRef.current && videoRef.current.srcObject) {
                stopTracks(videoRef.current.srcObject);
                videoRef.current.srcObject = null;
            }
            if (cameraPreviewRef.current && cameraPreviewRef.current.srcObject) {
                stopTracks(cameraPreviewRef.current.srcObject);
                cameraPreviewRef.current.srcObject = null;
            }
            // Ensure no pending WebRTC connections remain open
            Object.values(peerConnectionsRef.current).forEach(pc => {
                if (pc && typeof pc.close === 'function') pc.close();
            });
        };
    }, []);

    const fetchAiMetadata = useCallback(async () => {
        try {
            const res = await api.get(`/collab-meetings/meeting/${meetingId}/ai-metadata`);
            if (res.data?.status === 'success') {
                const metaData = res.data.data?.metadata || res.data;
                const meetingData = res.data.data?.meeting;
                const summaryRaw = res.data.summary || meetingData?.ai_summary;
                const actionsRaw = res.data.action_items || metaData?.action_items;

                if (summaryRaw) {
                    setAiSummary(String(summaryRaw).replace(/loc_\d+_[a-z0-9]+/gi, '').trim());
                }
                if (Array.isArray(actionsRaw)) {
                    setAiActionItems(actionsRaw.map(a => String(a).replace(/loc_\d+_[a-z0-9]+/gi, '').trim()));
                }
            }
        } catch (err) {
            console.error("Failed to fetch AI metadata", err);
        }
    }, [meetingId]);

    const handleTriggerAiProcessing = async () => {
        if (transcript.length === 0) {
            setAlertPopup({
                show: true,
                title: 'No Transcript Yet',
                message: 'No speech has been recorded in this meeting yet. Speak into your microphone to generate transcripts.'
            });
            return;
        }

        setAiProcessing(true);
        try {
            const fullText = transcript.map(t => `${t.speaker}: ${t.text}`).join('\n');
            const res = await api.post(`/collab-meetings/meeting/${meetingId}/generate-summary`, {
                transcript: fullText
            });

            if (res.data?.status === 'success') {
                setAiSummary(res.data.summary || 'Summary generated successfully.');
                setAiActionItems(res.data.action_items || []);
            } else {
                setAiSummary('Summary generated: ' + (res.data?.message || 'Done'));
            }
        } catch (err) {
            console.error("AI Minutes generation error:", err);
            const speakers = [...new Set(transcript.map(t => t.speaker))];
            setAiSummary(`Meeting attended by ${speakers.join(', ')}. Discussion covered ${transcript.length} spoken key points.`);
            setAiActionItems(transcript.slice(-3).map(t => `${t.speaker}: Follow up on "${t.text}"`));
        } finally {
            setAiProcessing(false);
        }
    };

    const handleDownloadAiMinutes = () => {
        if (transcript.length === 0) return;
        const textContent = [
            `=========================================`,
            `MEETING MINUTES & TRANSCRIPT`,
            `Meeting Title: ${meetingTitle || 'Collaboration Session'}`,
            `Date: ${new Date().toLocaleDateString()}`,
            `=========================================`,
            ``,
            `AI SUMMARY:`,
            aiSummary || 'N/A',
            ``,
            `ACTION ITEMS:`,
            aiActionItems.length > 0 ? aiActionItems.map(a => `- ${a}`).join('\n') : 'None',
            ``,
            `FULL TRANSCRIPT BY SPEAKER:`,
            transcript.map(t => `[${t.time}] ${t.speaker}: ${t.text}`).join('\n')
        ].join('\n');

        const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Meeting_Minutes_${meetingId}_${Date.now()}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const fetchChats = useCallback(async () => {
        if (!meetingId) return;
        try {
            const res = await api.get(`/collab-meetings/meeting/${meetingId}/chats?t=${Date.now()}`);
            if (res.data?.status === 'success') {
                const fetchedChats = res.data.data || [];
                
                const parsedTranscripts = [];
                const regularChats = [];
                const currentHands = {};
                const currentPolls = {};
                const currentQaList = [];
                
                fetchedChats.forEach(c => {
                    const msgText = c.text || c.message || '';
                    if (msgText.startsWith('[TRANSCRIPT]:')) {
                        const parts = msgText.split(':');
                        let rawSpeaker = '';
                        let clientId = null;
                        let textContent = '';

                        if (parts.length >= 4) {
                            rawSpeaker = parts[1] ? parts[1].trim() : '';
                            clientId = parts[2] ? parts[2].trim() : null;
                            textContent = parts.slice(3).join(':');
                        } else if (parts.length === 3) {
                            rawSpeaker = parts[1] ? parts[1].trim() : '';
                            textContent = parts[2];
                        } else {
                            textContent = msgText.slice('[TRANSCRIPT]:'.length);
                        }

                        // Determine clean display speaker name (ignoring machine IDs like web_... or stream_...)
                        let displaySpeaker = rawSpeaker;
                        if (!displaySpeaker || displaySpeaker.startsWith('web_') || displaySpeaker.startsWith('stream_') || displaySpeaker.startsWith('loc_') || displaySpeaker === 'You') {
                            displaySpeaker = (c.sender_name && c.sender_name !== 'You') ? c.sender_name : (c.sender || getSpeakerName() || 'Participant');
                        }

                        let text = textContent;
                        let translatedText = '';
                        if (textContent.includes('||')) {
                            const split = textContent.split('||');
                            text = split[0];
                            translatedText = split[1];
                        }

                        parsedTranscripts.push({
                            id: c.id || clientId,
                            clientId,
                            speaker: displaySpeaker,
                            text: transliterateToPhoneticEnglish(text),
                            translatedText: translatedText ? transliterateToPhoneticEnglish(translatedText) : '',
                            time: c.time || new Date(c.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        });
                    } else if (msgText.startsWith('[SIGNAL]:HAND_RAISE_')) {
                        const parts = msgText.split(':');
                        if (parts.length >= 3) {
                            const action = parts[1];
                            const uid = parts[2];
                            const uname = parts[3] || c.sender_name || 'Participant';
                            if (action === 'HAND_RAISE_ON') currentHands[uid] = uname;
                            if (action === 'HAND_RAISE_OFF') delete currentHands[uid];
                        }
                    } else if (msgText.startsWith('[SIGNAL]:POLL_CREATE:')) {
                        try {
                            const payload = JSON.parse(msgText.slice('[SIGNAL]:POLL_CREATE:'.length));
                            currentPolls[payload.id] = payload;
                            if (initialChatsLoadedRef.current && c.id > lastProcessedChatIdRef.current) {
                                setUnreadPollsQa(prev => prev + 1);
                            }
                        } catch(e) {}
                    } else if (msgText.startsWith('[SIGNAL]:POLL_VOTE:')) {
                        try {
                            const payload = JSON.parse(msgText.slice('[SIGNAL]:POLL_VOTE:'.length));
                            if (currentPolls[payload.pollId]) {
                                if (!currentPolls[payload.pollId].votes) currentPolls[payload.pollId].votes = {};
                                currentPolls[payload.pollId].votes[payload.user] = payload.optIdx;
                            }
                        } catch(e) {}
                    } else if (msgText.startsWith('[SIGNAL]:POLL_END:')) {
                        try {
                            const payload = JSON.parse(msgText.slice('[SIGNAL]:POLL_END:'.length));
                            if (currentPolls[payload.pollId]) {
                                currentPolls[payload.pollId].status = 'closed';
                            }
                        } catch(e) {}
                    } else if (msgText.startsWith('[SIGNAL]:POLL_DELETE:')) {
                        try {
                            const payload = JSON.parse(msgText.slice('[SIGNAL]:POLL_DELETE:'.length));
                            delete currentPolls[payload.pollId];
                        } catch(e) {}
                    } else if (msgText.startsWith('[SIGNAL]:QA_NEW:')) {
                        try {
                            const payload = JSON.parse(msgText.slice('[SIGNAL]:QA_NEW:'.length));
                            currentQaList.push(payload);
                            if (initialChatsLoadedRef.current && c.id > lastProcessedChatIdRef.current) {
                                setUnreadPollsQa(prev => prev + 1);
                            }
                        } catch(e) {}
                    } else if (msgText.startsWith('[SIGNAL]:WB_DRAW:')) {
                        // Prevent repeated drawing of already processed strokes
                        if (initialChatsLoadedRef.current && c.id <= lastProcessedChatIdRef.current) {
                            return;
                        }
                        try {
                            const payload = JSON.parse(msgText.slice('[SIGNAL]:WB_DRAW:'.length));
                            
                            // Auto-open whiteboard for others if a NEW drawing arrives live
                            if (initialChatsLoadedRef.current && c.id > lastProcessedChatIdRef.current) {
                                setWhiteboardActive(true);
                            }

                            if (Array.isArray(payload)) {
                                payload.forEach(stroke => {
                                    allStrokesRef.current.push(stroke);
                                });
                                if (canvasRef.current) {
                                    const ctx = canvasRef.current.getContext('2d');
                                    payload.forEach(stroke => {
                                        ctx.save();
                                        if (stroke.tool === 'eraser' || stroke.isEraser) {
                                            ctx.globalCompositeOperation = 'destination-out';
                                            ctx.lineWidth = stroke.lineWidth || 20;
                                            ctx.lineCap = 'round';
                                            ctx.lineJoin = 'round';
                                        } else if (stroke.tool === 'highlighter') {
                                            ctx.globalCompositeOperation = 'source-over';
                                            ctx.globalAlpha = stroke.opacity || 0.35;
                                            ctx.strokeStyle = stroke.color || '#facc15';
                                            ctx.lineWidth = stroke.lineWidth || 18;
                                            ctx.lineCap = 'square';
                                            ctx.lineJoin = 'miter';
                                        } else {
                                            ctx.globalCompositeOperation = 'source-over';
                                            ctx.globalAlpha = stroke.opacity || 1.0;
                                            ctx.strokeStyle = stroke.color || '#000000';
                                            ctx.lineWidth = stroke.lineWidth || 4;
                                            ctx.lineCap = 'round';
                                            ctx.lineJoin = 'round';
                                        }
                                        ctx.beginPath();
                                        ctx.moveTo(stroke.x0, stroke.y0);
                                        ctx.lineTo(stroke.x1, stroke.y1);
                                        ctx.stroke();
                                        ctx.closePath();
                                        ctx.restore();
                                    });
                                }
                            }
                        } catch(e) {}
                    } else if (msgText === '[SIGNAL]:WB_OPEN') {
                        if (initialChatsLoadedRef.current && c.id <= lastProcessedChatIdRef.current) return;
                        setWhiteboardActive(true);
                    } else if (msgText === '[SIGNAL]:WB_CLOSE') {
                        if (initialChatsLoadedRef.current && c.id <= lastProcessedChatIdRef.current) return;
                        setWhiteboardActive(false);
                    } else if (msgText === '[SIGNAL]:WB_CLEAR') {
                        if (initialChatsLoadedRef.current && c.id <= lastProcessedChatIdRef.current) return;
                        allStrokesRef.current = [];
                        pendingStrokesRef.current = [];
                        if (canvasRef.current) {
                            const ctx = canvasRef.current.getContext('2d');
                            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                            ctx.fillStyle = '#ffffff';
                            ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                        }
                    } else if (msgText === '[SIGNAL]:MUTE_ALL') {
                        if (initialChatsLoadedRef.current && c.id <= lastProcessedChatIdRef.current) return;
                        // Force mute if not host
                        if (!isHost && micActive) {
                            handleToggleMic();
                            setAlertPopup({ show: true, title: 'Microphone Muted', message: 'The host has muted all participants.' });
                        }
                    } else if (msgText.startsWith('[SIGNAL]:MUTE_USER:')) {
                        if (initialChatsLoadedRef.current && c.id <= lastProcessedChatIdRef.current) return;
                        const targetUserId = msgText.replace('[SIGNAL]:MUTE_USER:', '').trim();
                        if (targetUserId === String(user?.user_id || user?.id) && micActive) {
                            handleToggleMic();
                            setAlertPopup({ show: true, title: 'Microphone Muted', message: 'The host has muted your microphone.' });
                        }
                    } else if (msgText.startsWith('[SIGNAL]:KICK_USER:')) {
                        if (initialChatsLoadedRef.current && c.id <= lastProcessedChatIdRef.current) return;
                        const targetUserId = msgText.replace('[SIGNAL]:KICK_USER:', '').trim();
                        if (targetUserId === String(user?.user_id || user?.id)) {
                            handleLeaveMeeting();
                            setJoinStatus('kicked');
                            setAlertPopup({ show: true, title: 'Removed from Meeting', message: 'You have been removed from this meeting by the host.' });
                        }
                    } else if (msgText.startsWith('[SIGNAL]:JOIN_APPROVED:')) {
                        const targetUserId = msgText.replace('[SIGNAL]:JOIN_APPROVED:', '').trim();
                        const myId = String(user?.user_id || user?.id || '');
                        if (targetUserId === myId || targetUserId === 'ALL') {
                            setJoinStatus('approved');
                            fetchParticipants();
                            fetchAiMetadata();
                        }
                    } else {
                        regularChats.push(c);
                    }
                });

                // Check for new chat messages for unread badge
                if (initialChatsLoadedRef.current) {
                    const newChatsCount = regularChats.filter(c => c.id > lastProcessedChatIdRef.current).length;
                    if (newChatsCount > 0 && (!sidebarOpenRef.current || activeTabRef.current !== 'chat')) {
                        setUnreadMessages(prev => prev + newChatsCount);
                    }
                }

                if (fetchedChats.length > 0) {
                    lastProcessedChatIdRef.current = Math.max(...fetchedChats.map(ch => ch.id));
                    
                    if (initialChatsLoadedRef.current) {
                        const currentPollCount = Object.keys(currentPolls).length;
                        if (currentPollCount > lastPollCountRef.current) {
                            if (!sidebarOpenRef.current || activeTabRef.current !== 'polls') {
                                setUnreadPollsQa(prev => prev + (currentPollCount - lastPollCountRef.current));
                            }
                            setAlertPopup({
                                show: true,
                                title: '📊 New Poll Launched!',
                                message: 'A new poll has been created by the host. Click the Polls icon at the bottom to vote.'
                            });
                        }
                        
                        const currentQaCount = currentQaList.length;
                        if (currentQaCount > lastQaCountRef.current) {
                            if (!sidebarOpenRef.current || activeTabRef.current !== 'polls') {
                                setUnreadPollsQa(prev => prev + (currentQaCount - lastQaCountRef.current));
                            }
                        }
                    }
                    initialChatsLoadedRef.current = true;
                }
                lastPollCountRef.current = Object.keys(currentPolls).length;
                lastQaCountRef.current = currentQaList.length;

                setRaisedHands(currentHands);
                setPolls(currentPolls);
                setQaList(currentQaList);
                setMessages([
                    { sender: 'System', text: 'Secure workspace active. All chats are synchronized.', time: '' },
                    ...regularChats
                ]);

                // Efficient incremental transcript update preserving Map references
                setTranscript(prev => {
                    if (!parsedTranscripts.length) return prev;
                    const map = new Map(
                        prev.map(item => [item.clientId || item.id || `${item.speaker}-${item.time}-${item.text}`, item])
                    );
                    parsedTranscripts.forEach(item => {
                        const key = item.clientId || item.id || `${item.speaker}-${item.time}-${item.text}`;
                        map.set(key, item);
                    });
                    return [...map.values()];
                });
            }
        } catch (err) {
            console.error("Failed to fetch chats", err);
        }
    }, [meetingId]);

    const fetchParticipants = useCallback(async () => {
        if (!meetingId) return;
        try {
            const res = await api.get(`/collab-meetings/meeting/${meetingId}/participants?t=${Date.now()}`);
            if (res.data?.status === 'success') {
                if (res.data.meeting_status === 'completed') {
                    setJoinStatus('completed');
                    return;
                }
                const list = res.data.data || [];
                
                if (prevParticipantsRef.current !== null && !isInitialParticipantFetchRef.current) {
                    const prevList = prevParticipantsRef.current;
                    const getPKey = p => String(p.user_id || p.id || p.guest_email || p.email || p.name || '');
                    const getPName = p => p.guest_name || p.user_name || p.name || p.first_name || p.email || 'Someone';

                    const newlyJoined = list.filter(p => getPKey(p) && !prevList.some(prev => getPKey(prev) === getPKey(p)));
                    if (newlyJoined.length > 0) {
                        const firstJoiner = newlyJoined[0];
                        const name = getPName(firstJoiner);
                        setJoinLeaveToast({ name, type: 'joined' });
                        setTimeout(() => setJoinLeaveToast(null), 4000);
                    } else {
                        const newlyLeft = prevList.filter(prev => getPKey(prev) && !list.some(p => getPKey(p) === getPKey(prev)));
                        if (newlyLeft.length > 0) {
                            const firstLeaver = newlyLeft[0];
                            const name = getPName(firstLeaver);
                            setJoinLeaveToast({ name, type: 'left' });
                            setTimeout(() => setJoinLeaveToast(null), 4000);
                        }
                    }
                } else if (list.length > 0) {
                    isInitialParticipantFetchRef.current = false;
                }

                prevParticipantsRef.current = list;
                setParticipants(list);
                
                // Security Check: If I am not in the list (and I'm not the host), I was removed or meeting ended.
                // Guard: only eject if the list is non-empty (empty = API still loading) and user is confirmed inside.
                const myId = String(user?.user_id || user?.id || '');
                const amIStillThere = list.find(p => String(p.user_id) === myId);
                if (!amIStillThere && myId && !isHost && list.length > 0) {
                    setJoinStatus('rejected');
                    setAlertPopup({
                        show: true,
                        title: 'Meeting Ended',
                        message: 'You have been removed from the meeting, or the meeting has ended.'
                    });
                    // Auto-leave after showing popup
                    setTimeout(() => { if(onLeave) onLeave(); }, 3000);
                }
            }
        } catch (err) {
            console.error("Failed to fetch participants", err);
        }
    }, [meetingId, user, isHost, onLeave]);

    // Background sync for whiteboard strokes using small splice batching
    useEffect(() => {
        const interval = setInterval(() => {
            if (pendingStrokesRef.current.length > 0) {
                const strokes = pendingStrokesRef.current.splice(0, 100);
                if (strokes.length > 0) {
                    api.post(`/collab-meetings/meeting/${meetingId}/chat`, {
                        sender_name: user?.first_name || 'User',
                        message: `[SIGNAL]:WB_DRAW:${JSON.stringify(strokes)}`
                    }).catch(() => {});
                }
            }
        }, 500);
        return () => clearInterval(interval);
    }, [meetingId, user]);

    const fetchPendingRequests = useCallback(async () => {
        if (!meetingId) return;
        try {
            const res = await api.get(`/collab-meetings/meeting/${meetingId}/pending-requests?t=${Date.now()}`);
            if (res.data?.status === 'success') {
                setPendingRequests(res.data.data || []);
            }
        } catch (err) {
            // Non-host users will get a 403 - ignore silently
        }
    }, [meetingId]);

    // Handle approve / reject actions from host
    const handleApproveJoin = async (guestUserId, action = 'approve') => {
        try {
            await api.post(`/collab-meetings/meeting/${meetingId}/approve-join`, {
                user_id: guestUserId,
                action
            });
            if (action === 'approve') {
                api.post(`/collab-meetings/meeting/${meetingId}/chat`, {
                    sender_name: getSpeakerName(),
                    message: `[SIGNAL]:JOIN_APPROVED:${guestUserId}`
                }).catch(() => {});
            }
            // Refresh pending list and participant list immediately
            fetchPendingRequests();
            fetchParticipants();
        } catch (err) {
            console.error('Approval action failed:', err);
        }
    };

    const handleApproveAllJoin = async () => {
        try {
            const guestUserIds = pendingRequests.map(r => r.user_id);
            await api.post(`/collab-meetings/meeting/${meetingId}/approve-join`, {
                user_id: guestUserIds,
                action: 'approve'
            });
            api.post(`/collab-meetings/meeting/${meetingId}/chat`, {
                sender_name: getSpeakerName(),
                message: `[SIGNAL]:JOIN_APPROVED:ALL`
            }).catch(() => {});
            fetchPendingRequests();
            fetchParticipants();
        } catch (err) {
            console.error('Approve all failed:', err);
        }
    };

    // Handle mute / unmute participant from host
    const handleMuteParticipant = async (targetUserId, shouldMute) => {
        try {
            await api.post(`/collab-meetings/meeting/${meetingId}/mute-participant`, {
                user_id: targetUserId,
                is_muted: shouldMute
            });
            fetchParticipants();
        } catch (err) {
            console.error('Mute action failed:', err);
        }
    };

    // Handle Mute All participants from host
    const handleMuteAllParticipants = async () => {
        try {
            const nonHostParticipants = participants.filter(p => String(p.user_id) !== String(user?.user_id || user?.id));
            for (const p of nonHostParticipants) {
                await api.post(`/collab-meetings/meeting/${meetingId}/mute-participant`, {
                    user_id: p.user_id,
                    is_muted: 1
                });
            }
            api.post(`/collab-meetings/meeting/${meetingId}/chat`, { message: '[SIGNAL]:MUTE_ALL' }).catch(() => {});
            fetchParticipants();
            setAlertPopup({ show: true, title: 'Participants Muted', message: 'All participants have been muted.' });
        } catch (err) {
            console.error('Mute All action failed:', err);
        }
    };

    // Handle remove participant from host
    const handleRemoveParticipant = (targetUserId) => {
        setConfirmPopup({
            show: true,
            title: 'Remove Participant',
            message: 'Are you sure you want to remove this participant? They will be ejected from the meeting.',
            onConfirm: async () => {
                setConfirmPopup({ show: false, message: '', title: '', onConfirm: null });
                try {
                    await api.post(`/collab-meetings/meeting/${meetingId}/remove-participant`, {
                        user_id: targetUserId
                    });
                    fetchParticipants();
                } catch (err) {
                    console.error('Remove action failed:', err);
                }
            }
        });
    };



    const loadMeetingData = useCallback(async () => {
        if (!meetingId) return;
        try {
            // Fetch complete details
            const detailsRes = await api.get(`/collab-meetings/meeting/${meetingId}`);
            if (detailsRes.data?.status === 'success') {
                const data = detailsRes.data.data;
                setMeetingDetails(data);
                setCheckInStatus(data.check_in_status);
                
                const isHybrid = data.meeting_type === 'hybrid_zoom' || data.meeting_type === 'hybrid_google_meet' || (data.meeting_type === 'hybrid');
                if (isHybrid) {
                    if (data.external_meeting_url) {
                        window.open(data.external_meeting_url, '_blank');
                    } else {
                        setAlertPopup({
                            show: true,
                            title: 'No Meeting Link',
                            message: `No ${data.meeting_type === 'hybrid_zoom' ? 'Zoom' : 'Google Meet'} link configured for this meeting.`
                        });
                    }
                    if (onLeave) onLeave();
                    return;
                }

                if (data.status === 'completed') {
                    setJoinStatus('completed');
                    return;
                }
                
                // Pre-meeting check: Enforce scheduled start time (no buffer, applies to all users)
                const startTimeStr = data.start_time;
                if (startTimeStr) {
                    const startTimeMs = new Date(startTimeStr.replace(/-/g, "/")).getTime();
                    setMeetingStartTime(startTimeStr);
                    if (Date.now() < startTimeMs) {
                        setJoinStatus('not_started');
                        return;
                    }
                }
                
                // If the meeting has started, send the user to the Pre-Call (Green Room) screen
                setJoinStatus('precall');
            }
        } catch (err) {
            console.error("Failed to load meeting room details", err);
            setJoinStatus('rejected');
            setAlertPopup({
                show: true,
                title: 'Error',
                message: err.response?.data?.message || 'Failed to load the meeting room.'
            });
        }
    }, [meetingId]);

    const executeJoinMeeting = async () => {
        try {
            // Register / re-register current user
            const joinRes = await api.post(`/collab-meetings/meeting/${meetingId}/join`);
            let status = joinRes.data?.join_status || 'pending';
            const host = joinRes.data?.is_host || false;
            const userIsAdmin = Boolean(user?.role_id == 1 || user?.role_id == 2 || user?.user_id == 1 || user?.user_role_id == 1 || user?.user_role_id == 2 || (meetingDetails?.created_by && meetingDetails.created_by == (user?.user_id || user?.id)));

            const effectiveIsHost = host || userIsAdmin;
            if (effectiveIsHost) {
                status = 'approved';
            }

            setJoinStatus(status);
            setIsHost(effectiveIsHost);

            // Show "Your meeting's ready" toast for host in online meetings
            if (effectiveIsHost && status === 'approved') {
                setTimeout(() => setShowMeetingReadyToast(true), 800);
                setTimeout(() => setShowMeetingReadyToast(false), 14000);
            }

            if (status === 'approved') {
                await fetchParticipants();
                fetchAiMetadata();
            }
            if (host) {
                fetchPendingRequests();
            }
        } catch (err) {
            console.error("Failed to execute join meeting", err);
            setJoinStatus('rejected');
            setAlertPopup({
                show: true,
                title: 'Error',
                message: err.response?.data?.message || 'Failed to join the meeting room.'
            });
        }
    };

    // Initial join + join-status check
    useEffect(() => {
        loadMeetingData();
    }, [meetingId, loadMeetingData]);

    // Pre-call network check
    useEffect(() => {
        if (joinStatus === 'precall') {
            const checkNetwork = async () => {
                try {
                    const start = Date.now();
                    await api.get(`/collab-meetings/meeting/${meetingId}`);
                    const latency = Date.now() - start;
                    if (latency < 400) setPrecallNetworkStatus('Good (Low Latency)');
                    else if (latency < 1200) setPrecallNetworkStatus('Fair (Moderate Latency)');
                    else setPrecallNetworkStatus('Poor (High Latency)');
                } catch (err) {
                    setPrecallNetworkStatus('Offline / Error');
                }
            };
            checkNetwork();
        }
    }, [joinStatus, meetingId]);

    // Pre-call Media Stream & Audio Meter
    useEffect(() => {
        if (joinStatus !== 'precall') return;

        let activeStream = null;

        const acquireMedia = async () => {
            try {
                if (!camActive) setCameraVerified(false);
                if (!micActive) setAudioVerified(false);

                if (precallStreamRef.current) {
                    precallStreamRef.current.getTracks().forEach(t => t.stop());
                }
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: camActive,
                    audio: micActive
                });
                activeStream = stream;
                precallStreamRef.current = stream;
                
                if (camActive && precallVideoRef.current) {
                    precallVideoRef.current.srcObject = stream;
                    precallVideoRef.current.onloadedmetadata = () => {
                        setCameraVerified(true);
                    };
                }

                if (micActive && stream.getAudioTracks().length > 0) {
                    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
                    precallAudioCtxRef.current = audioCtx;
                    const source = audioCtx.createMediaStreamSource(stream);
                    const analyser = audioCtx.createAnalyser();
                    analyser.fftSize = 256;
                    source.connect(analyser);
                    const dataArray = new Uint8Array(analyser.frequencyBinCount);
                    
                    const updateAudioLevel = () => {
                        analyser.getByteFrequencyData(dataArray);
                        let sum = 0;
                        let max = 0;
                        for (let i = 0; i < dataArray.length; i++) {
                            sum += dataArray[i];
                            if (dataArray[i] > max) max = dataArray[i];
                        }
                        const avg = sum / dataArray.length;
                        
                        // Require a noticeable peak (max > 180) and decent overall volume (avg > 45) 
                        // to ensure it's a person speaking, not just static background noise.
                        // (High ambient noise or auto-gain can sometimes push lower thresholds artificially high).
                        if (avg > 45 && max > 180) {
                            setAudioVerified(true);
                        }
                        setPrecallAudioLevel(Math.min(100, Math.floor((avg / 255) * 100)));
                        precallAudioRafRef.current = requestAnimationFrame(updateAudioLevel);
                    };
                    updateAudioLevel();
                } else {
                    setPrecallAudioLevel(0);
                }

            } catch (err) {
                console.warn("Precall media error:", err);
                if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
                    setAlertPopup({
                        show: true,
                        title: 'Camera/Mic In Use',
                        message: 'Your camera or microphone is being used by another application or browser tab. Please close it and try again.'
                    });
                }
                if (camActive) setCamActive(false);
                if (micActive) setMicActive(false);
                setPrecallAudioLevel(0);
            }
        };

        acquireMedia();

        return () => {
            if (precallAudioRafRef.current) cancelAnimationFrame(precallAudioRafRef.current);
            if (precallAudioCtxRef.current && precallAudioCtxRef.current.state !== 'closed') {
                precallAudioCtxRef.current.close().catch(e => {});
            }
            if (activeStream) {
                activeStream.getTracks().forEach(t => t.stop());
            }
        };
    }, [joinStatus, camActive, micActive]);

    const [isSyncing, setIsSyncing] = useState(false);

    const handleSyncMeetingData = async () => {
        setIsSyncing(true);
        try {
            const res = await api.post(`/collab-meetings/meeting/${meetingId}/sync`);
            if (res.data?.status === 'success') {
                setAlertPopup({
                    show: true,
                    title: 'Sync Complete',
                    message: res.data.message || 'External Zoom/Meet data successfully synchronized.'
                });
                await loadMeetingData();
            } else {
                setAlertPopup({
                    show: true,
                    title: 'Sync Failed',
                    message: res.data?.message || 'Failed to sync meeting data.'
                });
            }
        } catch (err) {
            console.error("Failed to sync meeting data", err);
            setAlertPopup({
                show: true,
                title: 'Sync Error',
                message: err.response?.data?.message || 'An error occurred during synchronization.'
            });
        } finally {
            setIsSyncing(false);
        }
    };

    // Periodically check if meeting time has arrived
    useEffect(() => {
        if (joinStatus !== 'not_started' || !meetingStartTime) return;
        const interval = setInterval(() => {
            const startTimeMs = new Date(meetingStartTime.replace(/-/g, "/")).getTime();
            if (Date.now() >= startTimeMs) {
                // Time arrived, let them enter the precall room
                setJoinStatus('precall');
            }
        }, 5000); // check every 5 seconds
        return () => clearInterval(interval);
    }, [joinStatus, meetingStartTime]);

    // Guest polls join-status every 1s until approved/rejected for instant room entry
    useEffect(() => {
        if (joinStatus !== 'pending') return;
        const myId = String(user?.user_id || user?.id || '');
        const interval = setInterval(async () => {
            try {
                const res = await api.get(`/collab-meetings/meeting/${meetingId}/join-status?user_id=${myId}&t=${Date.now()}`);
                const newStatus = res.data?.join_status;
                if (newStatus === 'approved') {
                    setJoinStatus('approved');
                    fetchParticipants();
                    fetchAiMetadata();
                    clearInterval(interval);
                } else if (newStatus === 'rejected') {
                    setJoinStatus('rejected');
                    clearInterval(interval);
                }
            } catch (err) { /* silent */ }
        }, 1000);
        return () => clearInterval(interval);
    }, [joinStatus, meetingStartTime, meetingId, fetchParticipants, user]);

    const prevServerMuteState = useRef(false);

    // Check if host muted me
    useEffect(() => {
        if (isHost) return; // The host shouldn't be muted by themselves

        const myId = String(user?.user_id || user?.id || '');
        if (!myId || !participants.length) return;
        
        const me = participants.find(p => String(p.user_id) === myId);
        const isServerMuted = me && (me.is_muted == 1 || me.is_muted === '1');

        if (isServerMuted && !prevServerMuteState.current) {
            // Server just flipped from unmuted to muted
            if (micActive) {
                setMicActive(false);
                setAlertPopup({
                    show: true,
                    title: 'Muted by Host',
                    message: 'The host has muted your microphone.'
                });
            }
        }
        
        prevServerMuteState.current = isServerMuted;
    }, [participants, isHost, user, micActive]);

    // Host polls for pending requests every 1.5s
    useEffect(() => {
        if (!isHost) return;
        fetchPendingRequests();
        const interval = setInterval(fetchPendingRequests, 1500); // 1.5s for fast host admission feedback
        return () => clearInterval(interval);
    }, [isHost, fetchPendingRequests]);

    // Periodic synchronization once inside the room
    useEffect(() => {
        if (joinStatus !== 'approved') return;

        fetchParticipants();
        fetchChats();

        const participantInterval = setInterval(fetchParticipants, 1500);  // 1.5s for instant video/participant updates
        const chatInterval = setInterval(fetchChats, 1200);                 // 1.2s for real-time signaling

        return () => {
            clearInterval(participantInterval);
            clearInterval(chatInterval);
        };
    }, [joinStatus, fetchParticipants, fetchChats]);

    // Unread message badge logic
    useEffect(() => {
        if (sidebarOpen && activeTab === 'chat') {
            setUnreadMessages(0);
            setLastMessageCount(messages.length);
        } else if (messages.length > lastMessageCount) {
            setUnreadMessages(prev => prev + (messages.length - lastMessageCount));
            setLastMessageCount(messages.length);
        }
    }, [messages, sidebarOpen, activeTab, lastMessageCount]);

    // Unread polls & Q&A badge logic
    useEffect(() => {
        if (sidebarOpen && activeTab === 'polls') {
            setUnreadPollsQa(0);
        }
    }, [sidebarOpen, activeTab]);

    // ── WebRTC helpers ──────────────────────────────────────────────────────────
    const ICE_SERVERS = [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
    ];

    const getSender = (pc, kind) => (
        pc.getSenders().find(sender => sender.track?.kind === kind)
        || pc.getTransceivers().find(transceiver => transceiver.receiver?.track?.kind === kind)?.sender
    );

    const publishTrackToPeers = (kind, track) => {
        Object.entries(peerConnectionsRef.current).forEach(([pid, pc]) => {
            const sender = getSender(pc, kind);
            if (sender) {
                sender.replaceTrack(track || null).then(() => {
                    if (track && pc.signalingState === 'stable' && !makingOfferRef.current[pid]) {
                        renegotiate(pid, pc);
                    }
                }).catch(err => {
                    console.warn(`Failed to publish ${kind} track to ${pid}:`, err);
                });
            }
        });
    };

    const drainCandidates = useCallback(async (remoteUserId, pc) => {
        const queue = iceCandidateQueuesRef.current[remoteUserId] || [];
        iceCandidateQueuesRef.current[remoteUserId] = [];
        for (const cand of queue) {
            try {
                await pc.addIceCandidate(new RTCIceCandidate(cand));
            } catch (e) { /* ignore */ }
        }
    }, []);

    const createPeerConnection = useCallback((remoteUserId) => {
        if (peerConnectionsRef.current[remoteUserId]) {
            return peerConnectionsRef.current[remoteUserId];
        }
        const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

        if (!pc.getTransceivers().some(t => t.receiver?.track?.kind === 'audio')) {
            pc.addTransceiver('audio', { direction: 'sendrecv' });
        }
        if (!pc.getTransceivers().some(t => t.receiver?.track?.kind === 'video')) {
            pc.addTransceiver('video', { direction: 'sendrecv' });
        }

        const audioTrack = streamRef.current?.getAudioTracks().find(t => t.readyState === 'live') || null;
        const videoTrack = streamRef.current?.getVideoTracks().find(t => t.readyState === 'live') || null;

        if (audioTrack) {
            getSender(pc, 'audio')?.replaceTrack(audioTrack).catch(() => {});
        }
        if (videoTrack) {
            getSender(pc, 'video')?.replaceTrack(videoTrack).catch(() => {});
        }

        // When remote track arrives, store stream robustly by accumulating tracks
        pc.ontrack = (event) => {
            setRemoteStreams(prev => {
                const existingStream = prev[remoteUserId];
                let stream;
                if (existingStream) {
                    stream = existingStream;
                } else {
                    stream = new MediaStream();
                }
                if (!stream.getTracks().some(t => t.id === event.track.id)) {
                    stream.addTrack(event.track);
                }
                return { ...prev, [remoteUserId]: new MediaStream(stream.getTracks()) };
            });
            event.track.onended = () => {
                setRemoteStreams(prev => {
                    const existingStream = prev[remoteUserId];
                    if (!existingStream) return prev;
                    const tracks = existingStream.getTracks().filter(track => track.id !== event.track.id);
                    const next = { ...prev };
                    if (tracks.length) {
                        next[remoteUserId] = new MediaStream(tracks);
                    } else {
                        delete next[remoteUserId];
                    }
                    return next;
                });
            };
            event.track.onmute = () => setRemoteStreams(prev => ({ ...prev }));
            event.track.onunmute = () => setRemoteStreams(prev => ({ ...prev }));
        };

        // ICE candidate handler – send via signaling
        pc.onicecandidate = async (event) => {
            if (event.candidate) {
                try {
                    await api.post(`/collab-meetings/meeting/${meetingId}/signal`, {
                        to_user_id: remoteUserId,
                        type: 'candidate',
                        payload: event.candidate
                    });
                } catch (e) { /* silent */ }
            }
        };

        // Perfect negotiation handler - only run when connection is stable
        pc.onnegotiationneeded = async () => {
            // Guard: skip if already negotiating or connection not in stable state
            if (makingOfferRef.current[remoteUserId]) return;
            if (pc.signalingState !== 'stable') return;
            try {
                makingOfferRef.current[remoteUserId] = true;
                const offer = await pc.createOffer();
                // Re-check state after async createOffer; another negotiation may have started
                if (pc.signalingState !== 'stable') return;
                await pc.setLocalDescription(offer);
                await api.post(`/collab-meetings/meeting/${meetingId}/signal`, {
                    to_user_id: remoteUserId,
                    type: 'offer',
                    payload: pc.localDescription
                });
            } catch (err) {
                if (err.name !== 'InvalidStateError') {
                    console.warn("Negotiation needed failed:", err.message);
                }
            } finally {
                makingOfferRef.current[remoteUserId] = false;
            }
        };

        pc.onconnectionstatechange = () => {
            if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
                setRemoteStreams(prev => {
                    const next = { ...prev };
                    delete next[remoteUserId];
                    return next;
                });
                if (peerConnectionsRef.current[remoteUserId]) {
                    try {
                        peerConnectionsRef.current[remoteUserId].close();
                    } catch (e) {}
                    delete peerConnectionsRef.current[remoteUserId];
                }
            }
        };

        peerConnectionsRef.current[remoteUserId] = pc;
        return pc;
    }, [meetingId]);

    const renegotiate = useCallback(async (remoteUserId, pc) => {
        // Guard: only negotiate when stable and not already in a negotiation
        if (!pc || pc.signalingState !== 'stable' || makingOfferRef.current[remoteUserId]) {
            return;
        }
        try {
            makingOfferRef.current[remoteUserId] = true;
            const offer = await pc.createOffer();
            if (pc.signalingState !== 'stable') return;
            await pc.setLocalDescription(offer);
            await api.post(`/collab-meetings/meeting/${meetingId}/signal`, {
                to_user_id: remoteUserId,
                type: 'offer',
                payload: pc.localDescription
            });
        } catch (e) {
            if (e.name !== 'InvalidStateError') {
                console.warn(`Renegotiation failed for peer ${remoteUserId}:`, e.message);
            }
        } finally {
            makingOfferRef.current[remoteUserId] = false;
        }
    }, [meetingId]);

    // Initiator: called after participants list updates with new users
    const initiateCallsRef = useRef(null);
    initiateCallsRef.current = async (participantList) => {
        const myId = String(user?.user_id || user?.id || '');
        myUserIdRef.current = myId;
        for (const p of participantList) {
            const pid = String(p.user_id);
            if (pid === myId) continue;
            if (peerConnectionsRef.current[pid]) continue; // already connected
            // Lower user_id initiates offer
            if (parseInt(myId) < parseInt(pid)) {
                try {
                    const pc = createPeerConnection(pid);
                    const offer = await pc.createOffer();
                    await pc.setLocalDescription(offer);
                    await api.post(`/collab-meetings/meeting/${meetingId}/signal`, {
                        to_user_id: pid,
                        type: 'offer',
                        payload: pc.localDescription
                    });
                } catch (e) { console.error('Offer failed:', e); }
            }
        }
    };

    // Poll for incoming WebRTC signals every 2s when approved
    useEffect(() => {
        if (joinStatus !== 'approved' || !user) return;
        const myId = String(user.user_id || user.id || '');
        const interval = setInterval(async () => {
            try {
                const res = await api.get(`/collab-meetings/meeting/${meetingId}/signals?t=${Date.now()}`);
                const signals = res.data?.data || [];
                for (const sig of signals) {
                    const fromId = String(sig.from_user_id);
                    const pc = createPeerConnection(fromId);

                    const isPolite = parseInt(myId || '0') > parseInt(fromId || '0');

                    if (sig.type === 'offer') {
                        const offerCollision = makingOfferRef.current[fromId] || pc.signalingState === 'have-local-offer';

                        ignoreOfferRef.current[fromId] = !isPolite && offerCollision;
                        if (ignoreOfferRef.current[fromId]) {
                            console.log("Impolite peer ignoring offer from:", fromId);
                            continue;
                        }

                        if (offerCollision && isPolite) {
                            console.log("Polite peer rolling back for:", fromId);
                            await pc.setLocalDescription({ type: 'rollback' });
                        }

                        await pc.setRemoteDescription(new RTCSessionDescription(sig.payload));
                        const answer = await pc.createAnswer();
                        await pc.setLocalDescription(answer);
                        await api.post(`/collab-meetings/meeting/${meetingId}/signal`, {
                            to_user_id: fromId,
                            type: 'answer',
                            payload: pc.localDescription
                        });
                        
                        await drainCandidates(fromId, pc);
                    } else if (sig.type === 'answer') {
                        if (pc.signalingState !== 'stable') {
                            await pc.setRemoteDescription(new RTCSessionDescription(sig.payload));
                            await drainCandidates(fromId, pc);
                        }
                    } else if (sig.type === 'candidate') {
                        const readyForCandidate = pc.remoteDescription !== null;
                        if (readyForCandidate) {
                            try {
                                await pc.addIceCandidate(new RTCIceCandidate(sig.payload));
                            } catch (e) { /* ignore */ }
                        } else {
                            if (!iceCandidateQueuesRef.current[fromId]) {
                                iceCandidateQueuesRef.current[fromId] = [];
                            }
                            iceCandidateQueuesRef.current[fromId].push(sig.payload);
                        }
                    }
                }
            } catch (e) { /* silent */ }
        }, 3000); // reduced: 3s (was 2s) — signals only needed during active WebRTC signaling
        return () => clearInterval(interval);
    }, [joinStatus, meetingId, createPeerConnection, user]);

    // Re-initiate calls and clean up disconnected peers when participant list changes
    useEffect(() => {
        if (joinStatus === 'approved' && participants.length > 0 && user && mediaInitialized) {
            initiateCallsRef.current(participants);
        }

        // Clean up connections for peers who have left
        if (joinStatus === 'approved' && user) {
            const currentIds = participants.map(p => String(p.user_id));
            Object.keys(peerConnectionsRef.current).forEach(pid => {
                if (!currentIds.includes(pid)) {
                    try {
                        peerConnectionsRef.current[pid].close();
                    } catch (e) {}
                    delete peerConnectionsRef.current[pid];
                    setRemoteStreams(prev => {
                        const next = { ...prev };
                        delete next[pid];
                        return next;
                    });
                }
            });
        }
    }, [participants, joinStatus, user, mediaInitialized]);

    // Cleanup WebRTC on unmount
    useEffect(() => {
        return () => {
            Object.values(peerConnectionsRef.current).forEach(pc => pc.close());
            peerConnectionsRef.current = {};
        };
    }, []);

    // Initialize media stream once approved
    useEffect(() => {
        if (joinStatus !== 'approved' || !meetingDetails) return;
        if (meetingDetails.meeting_type !== 'online') return;

        const initMedia = async () => {
            setCameraAvailable(true);

            if (camActive || micActive) {
                try {
                    const activeStream = await navigator.mediaDevices.getUserMedia({
                        video: camActive,
                        audio: micActive
                    });
                    
                    streamRef.current = new MediaStream(activeStream.getTracks());

                    if (videoRef.current && !screenShareActive && camActive) {
                        videoRef.current.srcObject = streamRef.current;
                        videoRef.current.onloadedmetadata = () => { videoRef.current?.play().catch(() => {}); };
                        videoRef.current.play().catch(() => {});
                    }

                    // Multi-phase retries to guarantee local video binds & plays immediately when DOM video node mounts
                    [100, 300, 600].forEach(delay => {
                        setTimeout(() => {
                            if (videoRef.current && streamRef.current && !screenShareActive && camActive) {
                                if (videoRef.current.srcObject !== streamRef.current) {
                                    videoRef.current.srcObject = streamRef.current;
                                }
                                videoRef.current.onloadedmetadata = () => { videoRef.current?.play().catch(() => {}); };
                                videoRef.current.play().catch(() => {});
                            }
                        }, delay);
                    });
                } catch (err) {
                    console.error("Failed to initialize active media tracks:", err);
                    if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
                        setAlertPopup({
                            show: true,
                            title: 'Camera/Mic In Use',
                            message: 'Your camera or microphone is being used by another application. Please close it and try again.'
                        });
                        setCamActive(false);
                        setMicActive(false);
                    }
                    streamRef.current = new MediaStream();
                }
            } else {
                streamRef.current = new MediaStream();
            }

            // After stream is initialized, bind any tracks to existing peer connections
            publishTrackToPeers('audio', streamRef.current.getAudioTracks()[0] || null);
            publishTrackToPeers('video', streamRef.current.getVideoTracks()[0] || null);
            
            isMediaInitializedRef.current = true;
            setMediaInitialized(true);
        };

        initMedia();

        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(t => t.stop());
                streamRef.current = null;
            }
        };
    }, [joinStatus, meetingDetails]);

    // Handle track enable state changes locally
    useEffect(() => {
        const toggleCamera = async () => {
            if (!streamRef.current || !isMediaInitializedRef.current) return;
            
            if (camActive) {
                const videoTracks = streamRef.current.getVideoTracks();
                if (videoTracks.length === 0 || videoTracks[0].readyState === 'ended') {
                    try {
                        const newStream = await navigator.mediaDevices.getUserMedia({ video: true });
                        const newTrack = newStream.getVideoTracks()[0];
                        streamRef.current.addTrack(newTrack);
                        publishTrackToPeers('video', newTrack);
                        
                        if (videoRef.current && !screenShareActive) {
                            videoRef.current.srcObject = streamRef.current;
                        }
                    } catch (err) {
                        console.error("Failed to restart camera", err);
                    }
                } else {
                    videoTracks.forEach(t => t.enabled = true);
                }
            } else {
                streamRef.current.getVideoTracks().forEach(t => {
                    t.enabled = false;
                    t.stop();
                    streamRef.current.removeTrack(t);
                });
                publishTrackToPeers('video', null);
            }
        };
        toggleCamera();
    }, [camActive]);

    useEffect(() => {
        const toggleMicrophone = async () => {
            if (!streamRef.current || !isMediaInitializedRef.current) return;
            
            if (micActive) {
                const audioTracks = streamRef.current.getAudioTracks();
                if (audioTracks.length === 0 || audioTracks[0].readyState === 'ended') {
                    try {
                        const newStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                        const newTrack = newStream.getAudioTracks()[0];
                        streamRef.current.addTrack(newTrack);
                        publishTrackToPeers('audio', newTrack);
                    } catch (err) {
                        console.error("Failed to restart microphone", err);
                    }
                } else {
                    audioTracks.forEach(t => t.enabled = true);
                }
            } else {
                streamRef.current.getAudioTracks().forEach(t => {
                    t.enabled = false;
                    t.stop();
                    streamRef.current.removeTrack(t);
                });
                publishTrackToPeers('audio', null);
            }
        };
        toggleMicrophone();
    }, [micActive]);

    // Sync media status to server database for participant tracking
    useEffect(() => {
        if (joinStatus !== 'approved' || !meetingId) return;
        const syncStatus = async () => {
            try {
                await api.post(`/collab-meetings/meeting/${meetingId}/participant-status`, {
                    is_muted: !micActive,
                    is_camera_on: camActive,
                    is_screen_sharing: screenShareActive
                });
            } catch (err) {
                console.error("Failed to sync participant status", err);
            }
        };
        syncStatus();
    }, [micActive, camActive, screenShareActive, joinStatus, meetingId]);

    const getSpeakerName = () => {
        const fullName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
        if (fullName) return fullName;
        if (user?.name) return user.name;
        if (user?.username) return user.username;
        if (user?.email) return user.email.split('@')[0];
        if (localName && localName !== 'You') return localName;
        return 'Participant';
    };

    // Dual Desktop & Mobile Speech Recognition Engine:
    // On Desktop: Uses native browser SpeechRecognition for 100% free STT.
    // On Mobile (Android/iOS): WebRTC locks the mic, so it records 3s audio chunks directly from the active WebRTC stream.
    useEffect(() => {
        if (!micActive || joinStatus !== 'approved') return;

        let active = true;
        let recognition = null;
        let mediaRecorder = null;
        let dedicatedMicStream = null;
        let audioCtx = null;
        let retryTimer = null;

        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

        if (SpeechRecognition) {
            try {
                recognition = new SpeechRecognition();
                recognition.continuous = true;
                recognition.interimResults = true;
                recognition.lang = speechLang; // Dynamic: 'ta-IN' for Tamil, 'en-IN' for Tanglish/English, etc.

                recognition.onresult = (event) => {
                    if (!active || !micActive) return;
                    for (let i = event.resultIndex; i < event.results.length; i++) {
                        if (event.results[i].isFinal) {
                            const rawText = event.results[i][0].transcript.trim();
                            const text = transliterateToPhoneticEnglish(rawText);
                            if (text) {
                                const clientId = `web_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
                                
                                // Broadcast transcript chunk to all room members
                                api.post(`/collab-meetings/meeting/${meetingId}/chat`, {
                                    sender_name: getSpeakerName(),
                                    message: `[TRANSCRIPT]:${getSpeakerName()}:${clientId}:${text}`
                                }).catch(() => {});

                                setTranscript(prev => {
                                    const exists = prev.some(p => p.clientId === clientId);
                                    if (exists) return prev;
                                    return [...prev, {
                                        clientId,
                                        speaker: getSpeakerName(),
                                        text,
                                        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                    }];
                                });
                            }
                        }
                    }
                };

                recognition.onerror = (e) => {
                    console.warn("Free Web Speech API notice:", e.error);
                    if (e.error === 'not-allowed') {
                        setAlertPopup({
                            show: true,
                            title: 'Microphone Access Required',
                            message: 'Speech recognition requires microphone permissions. Please allow mic access in your browser address bar.'
                        });
                    }
                };

                recognition.onend = () => {
                    if (active && micActive) {
                        try { recognition.start(); } catch (e) {}
                    }
                };

                try {
                    recognition.start();
                } catch (e) {}
            } catch (err) {
                console.warn("Native SpeechRecognition initialization error:", err);
            }
        }

        // Audio chunk streaming for Mobile devices (or browsers without native Web Speech API)
        const startLocalMicStreaming = async () => {
            if (SpeechRecognition) return; // Desktop/supported browsers use native Web Speech API

            try {
                const activeAudioTracks = streamRef.current ? streamRef.current.getAudioTracks().filter(t => t.readyState === 'live') : [];
                
                // If media stream is still initializing on mobile, retry after 500ms
                if (activeAudioTracks.length === 0 && !dedicatedMicStream) {
                    retryTimer = setTimeout(() => {
                        if (active && micActive) startLocalMicStreaming();
                    }, 500);
                    return;
                }

                if (activeAudioTracks.length > 0) {
                    dedicatedMicStream = new MediaStream([activeAudioTracks[0]]);
                } else if (!dedicatedMicStream) {
                    dedicatedMicStream = await navigator.mediaDevices.getUserMedia({ audio: true }).catch(() => null);
                }

                if (!active || !micActive || !dedicatedMicStream) {
                    if (dedicatedMicStream && !streamRef.current) {
                        dedicatedMicStream.getTracks().forEach(t => t.stop());
                    }
                    return;
                }

                let targetStream = dedicatedMicStream;
                try {
                    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
                    if (AudioContextClass) {
                        audioCtx = new AudioContextClass();
                        if (audioCtx.state === 'suspended') {
                            await audioCtx.resume().catch(() => {});
                        }
                        const source = audioCtx.createMediaStreamSource(dedicatedMicStream);
                        const destination = audioCtx.createMediaStreamDestination();
                        source.connect(destination);
                        targetStream = destination.stream;
                    }
                } catch (e) {
                    console.warn("WebAudio context isolation notice:", e);
                }

                if (!targetStream) return;

                let mimeType = 'audio/webm';
                if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported) {
                    if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
                        mimeType = 'audio/webm;codecs=opus';
                    } else if (MediaRecorder.isTypeSupported('audio/webm')) {
                        mimeType = 'audio/webm';
                    } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
                        mimeType = 'audio/mp4';
                    } else if (MediaRecorder.isTypeSupported('audio/aac')) {
                        mimeType = 'audio/aac';
                    }
                }

                mediaRecorder = new MediaRecorder(targetStream, { mimeType });

                mediaRecorder.ondataavailable = async (e) => {
                    if (e.data && e.data.size > 50 && micActive && active) {
                        try {
                            const reader = new FileReader();
                            reader.readAsDataURL(e.data);
                            reader.onloadend = async () => {
                                const base64data = reader.result?.split(',')[1];
                                if (!base64data || !active || !micActive) return;

                                const res = await api.post('/collab-meetings/stt-chunk', {
                                    audio_base64: base64data,
                                    mime_type: mimeType,
                                    speaker: getSpeakerName()
                                }).catch(() => null);

                                if (res && res.data && res.data.status === 'success' && res.data.text && active) {
                                    const text = res.data.text.trim();
                                    if (!text) return;
                                    const clientId = `stream_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

                                    api.post(`/collab-meetings/meeting/${meetingId}/chat`, {
                                        sender_name: getSpeakerName(),
                                        message: `[TRANSCRIPT]:${getSpeakerName()}:${clientId}:${text}`
                                    }).catch(() => {});

                                    setTranscript(prev => {
                                        const exists = prev.some(p => p.clientId === clientId);
                                        if (exists) return prev;
                                        return [...prev, {
                                            clientId,
                                            speaker: getSpeakerName(),
                                            text,
                                            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                        }];
                                    });
                                }
                            };
                        } catch (err) {
                            console.warn("Local mic STT chunk error:", err);
                        }
                    }
                };

                // Native 3s timesliced chunk recording for seamless mobile & desktop speech streaming
                if (mediaRecorder.state === 'inactive') {
                    mediaRecorder.start(3000);
                }

            } catch (err) {
                console.warn("Failed to start local mic MediaRecorder:", err);
            }
        };

        startLocalMicStreaming();

        return () => {
            active = false;
            if (retryTimer) clearTimeout(retryTimer);
            if (recognition) {
                try { recognition.stop(); } catch (e) {}
            }
            if (mediaRecorder && mediaRecorder.state !== 'inactive') {
                try { mediaRecorder.stop(); } catch (e) {}
            }
            if (dedicatedMicStream) {
                try { dedicatedMicStream.getTracks().forEach(t => t.stop()); } catch (e) {}
            }
            if (audioCtx) {
                try { if (audioCtx.state !== 'closed') audioCtx.close(); } catch (e) {}
            }
        };
    }, [micActive, joinStatus, meetingId, mediaInitialized, speechLang]);

    // Enterprise Google Meet / Zoom Remote Stream Transcription Engine (Multi-Track Mobile Resilient)
    // Captures incoming remote WebRTC audio streams (e.g. Mobile phone attendees) and transcribes their speech cleanly
    useEffect(() => {
        if (joinStatus !== 'approved' || !remoteStreams || Object.keys(remoteStreams).length === 0) return;

        let active = true;
        const remoteRecorders = {}; // { userId: { recorder, audioCtx } }

        Object.entries(remoteStreams).forEach(([remoteUserId, stream]) => {
            if (!stream || !active) return;
            
            const getLiveAudioTrack = (s) => {
                if (!s) return null;
                const tracks = s.getAudioTracks().filter(t => t.readyState === 'live' && !t.muted);
                return tracks.length > 0 ? tracks[tracks.length - 1] : (s.getAudioTracks()[0] || null);
            };

            const liveTrack = getLiveAudioTrack(stream);
            if (!liveTrack) {
                console.warn(`[STT DEBUG] No live audio track for remote user ${remoteUserId}`);
                return;
            }

            // Find speaker name for this remote participant
            const partObj = participants.find(p => String(p.user_id) === String(remoteUserId));
            const remoteSpeakerName = (partObj?.name?.trim()) || partObj?.guest_email || partObj?.email || `Participant ${remoteUserId}`;
            const deviceType = partObj?.user_img?.includes('mobile') ? 'Mobile' : 'Remote';

            console.log(`[STT DEBUG] Initializing Remote STT for ${remoteSpeakerName} (ID: ${remoteUserId})`, {
                trackId: liveTrack.id,
                readyState: liveTrack.readyState,
                enabled: liveTrack.enabled,
                muted: liveTrack.muted
            });

            const setupRemoteRecorderForUser = () => {
                if (!active) return;
                const currentTrack = getLiveAudioTrack(stream);
                if (!currentTrack) return;

                try {
                    let mimeType = 'audio/webm';
                    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported) {
                        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
                            mimeType = 'audio/webm;codecs=opus';
                        } else if (MediaRecorder.isTypeSupported('audio/webm')) {
                            mimeType = 'audio/webm';
                        } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
                            mimeType = 'audio/mp4';
                        }
                    }

                    const rawStream = new MediaStream([currentTrack]);
                    let targetStream = rawStream;
                    let audioCtx = null;

                    // WebAudio Gain Node (2.2x Volume Boost for Mobile Mics)
                    try {
                        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
                        if (AudioContextClass) {
                            audioCtx = new AudioContextClass();
                            if (audioCtx.state === 'suspended') {
                                audioCtx.resume().catch(() => {});
                            }
                            const source = audioCtx.createMediaStreamSource(rawStream);
                            const gainNode = audioCtx.createGain();
                            gainNode.gain.value = 2.2; // Normalize low-volume mobile mic levels
                            const compressor = audioCtx.createDynamicsCompressor();
                            
                            const destination = audioCtx.createMediaStreamDestination();
                            source.connect(gainNode);
                            gainNode.connect(compressor);
                            compressor.connect(destination);
                            targetStream = destination.stream;
                        }
                    } catch (e) {
                        console.warn("[STT DEBUG] AudioContext isolation fallback:", e);
                    }

                    const recorder = new MediaRecorder(targetStream, { mimeType });
                    let lastChunkBlob = null;

                    recorder.ondataavailable = async (e) => {
                        if (e.data && e.data.size > 50 && active) {
                            try {
                                // Overlap Buffering: Combine 1.0s overlap from previous chunk to preserve Tamil word boundaries
                                let finalBlob = e.data;
                                if (lastChunkBlob) {
                                    finalBlob = new Blob([lastChunkBlob, e.data], { type: mimeType });
                                }
                                lastChunkBlob = e.data.slice(Math.max(0, e.data.size - 15000)); // Retain tail for next boundary

                                const reader = new FileReader();
                                reader.readAsDataURL(finalBlob);
                                reader.onloadend = async () => {
                                    const base64data = reader.result?.split(',')[1];
                                    if (!base64data || !active) return;

                                    console.log(`[STT DEBUG] Uploading Chunk for ${remoteSpeakerName} (${deviceType})`, {
                                        size: finalBlob.size,
                                        mimeType,
                                        timestamp: new Date().toISOString()
                                    });

                                    const res = await api.post('/collab-meetings/stt-chunk', {
                                        audio_base64: base64data,
                                        mime_type: mimeType,
                                        speaker: remoteSpeakerName
                                    }).catch(() => null);

                                    if (res && res.data && res.data.status === 'success' && res.data.text && active) {
                                        const text = res.data.text.trim();
                                        if (!text) return;

                                        console.log(`[STT DEBUG] Gemini Response for ${remoteSpeakerName}: "${text}" (Engine: ${res.data.engine || 'gemini'})`);

                                        const clientId = `remote_${remoteUserId}_${Date.now()}_${Math.random().toString(36).substring(2, 5)}`;

                                        api.post(`/collab-meetings/meeting/${meetingId}/chat`, {
                                            sender_name: remoteSpeakerName,
                                            message: `[TRANSCRIPT]:${remoteSpeakerName}:${clientId}:${text}`
                                        }).catch(() => {});

                                        setTranscript(prev => {
                                            const exists = prev.some(p => p.clientId === clientId);
                                            if (exists) return prev;
                                            return [...prev, {
                                                clientId,
                                                speaker: remoteSpeakerName,
                                                text,
                                                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                            }];
                                        });
                                    }
                                };
                            } catch (err) {
                                console.warn("[STT DEBUG] Remote STT chunk error:", err);
                            }
                        }
                    };

                    // Listen for mobile track state changes (unmute, reconnect)
                    currentTrack.onunmute = () => {
                        console.log(`[STT DEBUG] Remote track unmuted for ${remoteSpeakerName}`);
                        if (recorder.state === 'inactive') {
                            try { recorder.start(3000); } catch (e) {}
                        }
                    };

                    currentTrack.onended = () => {
                        console.warn(`[STT DEBUG] Remote track ended for ${remoteSpeakerName}`);
                        if (recorder.state !== 'inactive') {
                            try { recorder.stop(); } catch (e) {}
                        }
                    };

                    if (recorder.state === 'inactive') {
                        recorder.start(3000);
                        console.log(`[STT DEBUG] Started MediaRecorder (3s timeslice) for ${remoteSpeakerName}`);
                    }

                    remoteRecorders[remoteUserId] = { recorder, audioCtx };
                } catch (err) {
                    console.warn(`[STT DEBUG] Failed to initialize remote STT for user ${remoteUserId}:`, err);
                }
            };

            setupRemoteRecorderForUser();
        });

        return () => {
            active = false;
            Object.values(remoteRecorders).forEach(item => {
                if (item?.recorder && item.recorder.state !== 'inactive') {
                    try { item.recorder.stop(); } catch (e) {}
                }
                if (item?.audioCtx) {
                    try { if (item.audioCtx.state !== 'closed') item.audioCtx.close(); } catch (e) {}
                }
            });
        };
    }, [remoteStreams, joinStatus, meetingId, participants]);

    // Sync picture-in-picture presenter face bubble
    useEffect(() => {
        if (screenShareActive && camActive && streamRef.current && cameraPreviewRef.current) {
            cameraPreviewRef.current.srcObject = streamRef.current;
        }
    }, [screenShareActive, camActive, cameraAvailable]);

    // Sync local video element srcObject on every render to ensure newly mounted video tags get the stream
    useEffect(() => {
        if (videoRef.current) {
            const expectedStream = screenShareActive ? screenStreamRef.current : streamRef.current;
            if (videoRef.current.srcObject !== expectedStream) {
                videoRef.current.srcObject = expectedStream;
            }
        }
    });

    // Auto-open sidebar when screen share starts (either local or remote) to balance the layout
    // Only auto-opens on desktop (width >= 1024px) so that the presentation is not hidden on mobile.
    useEffect(() => {
        const hasRemotePresenter = participants.some(p => 
            String(p.user_id) !== String(user?.user_id || user?.id) && 
            (p.is_screen_sharing == 1 || p.is_screen_sharing === '1')
        );
        if ((screenShareActive || hasRemotePresenter) && window.innerWidth >= 1024) {
            setSidebarOpen(true);
        }
    }, [screenShareActive, participants, user]);

    // Functional Screen Sharing via WebRTC
    const handleToggleScreenShare = async () => {
        if (!screenShareActive) {
            try {
                const displayStream = await navigator.mediaDevices.getDisplayMedia({ 
                    video: { displaySurface: 'monitor' }, 
                    audio: { systemAudio: 'include' },
                    systemAudio: 'include'
                });
                screenStreamRef.current = displayStream;
                if (videoRef.current) {
                    videoRef.current.srcObject = displayStream;
                }
                setScreenShareActive(true);
                
                // Replace video track on all peer connections
                const screenTrack = displayStream.getVideoTracks()[0];
                replaceVideoTrackOnPeers(screenTrack);

                displayStream.getVideoTracks()[0].onended = () => {
                    stopScreenShare();
                };
            } catch (err) {
                console.warn("Screen share cancelled or failed: ", err);
            }
        } else {
            stopScreenShare();
        }
    };

    const stopScreenShare = () => {
        if (screenStreamRef.current) {
            screenStreamRef.current.getTracks().forEach(track => track.stop());
            screenStreamRef.current = null;
        }

        // Put the camera track back on peer connections
        const cameraTrack = streamRef.current ? streamRef.current.getVideoTracks()[0] : null;
        replaceVideoTrackOnPeers(cameraTrack);

        if (videoRef.current) {
            videoRef.current.srcObject = streamRef.current;
        }
        setScreenShareActive(false);
    };

    const replaceVideoTrackOnPeers = (newTrack) => {
        Object.entries(peerConnectionsRef.current).forEach(([remoteUserId, pc]) => {
            const sender = getSender(pc, 'video');
            if (sender) {
                sender.replaceTrack(newTrack || null).catch(err => {
                    console.warn("Failed to replace track on peer:", err);
                });
            }
        });
    };

    const getSupportedRecordingMimeType = () => {
        if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
            return '';
        }

        return [
            'video/webm;codecs=vp9,opus',
            'video/webm;codecs=vp8,opus',
            'video/webm;codecs=h264,opus',
            'video/webm'
        ].find(type => MediaRecorder.isTypeSupported(type)) || '';
    };

    const stopRecordingCapture = () => {
        if (recordingStreamRef.current) {
            recordingStreamRef.current.getTracks().forEach(track => track.stop());
            recordingStreamRef.current = null;
        }
        if (recordingDisplayStreamRef.current) {
            recordingDisplayStreamRef.current.getTracks().forEach(track => track.stop());
            recordingDisplayStreamRef.current = null;
        }
        if (recordingMicStreamRef.current) {
            recordingMicStreamRef.current.getTracks().forEach(track => track.stop());
            recordingMicStreamRef.current = null;
        }
        if (recordingAudioCtxRef.current) {
            try {
                if (recordingAudioCtxRef.current.state !== 'closed') {
                    recordingAudioCtxRef.current.close();
                }
            } catch (err) {
                console.error("Failed to close audio context:", err);
            }
            recordingAudioCtxRef.current = null;
        }
    };

    // Records the selected meeting tab/window so the saved video contains the full meeting UI.
    const handleToggleRecording = async () => {
        if (!recordingActive) {
            if (!navigator.mediaDevices?.getDisplayMedia || typeof MediaRecorder === 'undefined') {
                setAlertPopup({
                    show: true,
                    title: 'Recording Not Supported',
                    message: 'Your browser does not support full meeting screen recording.'
                });
                return;
            }

            recordedChunksRef.current = [];

            try {
                const displayStream = await navigator.mediaDevices.getDisplayMedia({
                    video: {
                        displaySurface: 'monitor',
                        cursor: 'always'
                    },
                    audio: {
                        systemAudio: 'include',
                        echoCancellation: true,
                        noiseSuppression: true
                    },
                    systemAudio: 'include',
                    surfaceSwitching: 'include',
                    preferCurrentTab: false
                });

                if (!displayStream.getVideoTracks().length) {
                    throw new Error('No display video track was selected.');
                }

                recordingDisplayStreamRef.current = displayStream;

                // Setup audio mixer
                let mixedStream = displayStream;
                let hasAudio = false;

                const AudioContextClass = window.AudioContext || window.webkitAudioContext;
                if (AudioContextClass) {
                    try {
                        const audioCtx = new AudioContextClass();
                        recordingAudioCtxRef.current = audioCtx;
                        const dest = audioCtx.createMediaStreamDestination();

                        // 1. System/Tab audio
                        const displayAudioTracks = displayStream.getAudioTracks();
                        if (displayAudioTracks.length > 0) {
                            const displayAudioSource = audioCtx.createMediaStreamSource(new MediaStream([displayAudioTracks[0]]));
                            displayAudioSource.connect(dest);
                            hasAudio = true;
                        }

                        // 2. Microphone audio (user's voice)
                        let micStream = null;
                        try {
                            micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                            recordingMicStreamRef.current = micStream;
                            const micSource = audioCtx.createMediaStreamSource(micStream);
                            micSource.connect(dest);
                            hasAudio = true;
                        } catch (micErr) {
                            console.warn("Could not capture microphone for recording:", micErr);
                        }

                        if (audioCtx.state === 'suspended') {
                            await audioCtx.resume();
                        }

                        if (hasAudio) {
                            const tracks = [displayStream.getVideoTracks()[0], ...dest.stream.getAudioTracks()];
                            mixedStream = new MediaStream(tracks);
                        }
                    } catch (audioErr) {
                        console.error("Audio mixing initialization failed:", audioErr);
                    }
                }

                recordingStreamRef.current = mixedStream;
                const mimeType = getSupportedRecordingMimeType();
                const recorder = new MediaRecorder(mixedStream, mimeType ? { mimeType } : undefined);

                recorder.ondataavailable = (event) => {
                    if (event.data && event.data.size > 0) {
                        recordedChunksRef.current.push(event.data);
                    }
                };

                recorder.onstop = () => {
                    if (recordingTimerRef.current) {
                        clearInterval(recordingTimerRef.current);
                        recordingTimerRef.current = null;
                    }
                    setRecordingTimer(0);

                    stopRecordingCapture();
                    setRecordingActive(false);
                    mediaRecorderRef.current = null;

                    if (!recordedChunksRef.current.length) {
                        setAlertPopup({
                            show: true,
                            title: 'Recording Empty',
                            message: 'No meeting recording data was captured.'
                        });
                        return;
                    }

                    const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
                    
                    // Upload audio blob to server for AI processing
                    const uploadAudio = async () => {
                        try {
                            const formData = new FormData();
                            formData.append('audio', blob, `meeting-${meetingId}.webm`);
                            formData.append('meeting_id', meetingId);
                            
                            const response = await api.post(`/collab-meetings/meeting/${meetingId}/upload-audio`, formData, {
                                headers: {
                                    'Content-Type': 'multipart/form-data'
                                }
                            });
                            
                            if (response.data?.status === 'success') {
                                console.log('Audio uploaded successfully for AI processing');
                            }
                        } catch (err) {
                            console.error('Failed to upload audio to server:', err);
                        }
                    };
                    
                    uploadAudio();
                    
                    // Download locally for user backup
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.style.display = 'none';
                    a.href = url;
                    a.download = `meeting-room-${meetingId}-record.webm`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                };

                recorder.onerror = (event) => {
                    console.error('Meeting recording failed:', event.error || event);
                    setAlertPopup({
                        show: true,
                        title: 'Recording Failed',
                        message: 'Unable to continue recording the selected meeting screen.'
                    });
                };

                displayStream.getVideoTracks().forEach(track => {
                    track.onended = () => {
                        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                            mediaRecorderRef.current.stop();
                        } else {
                            if (recordingTimerRef.current) {
                                clearInterval(recordingTimerRef.current);
                                recordingTimerRef.current = null;
                            }
                            setRecordingTimer(0);
                            stopRecordingCapture();
                            setRecordingActive(false);
                        }
                    };
                });

                mediaRecorderRef.current = recorder;
                
                let count = 3;
                setRecordingCountdown(count);
                
                const countdownInterval = setInterval(() => {
                    count -= 1;
                    if (count > 0) {
                        setRecordingCountdown(count);
                    } else {
                        clearInterval(countdownInterval);
                        setRecordingCountdown(null);
                        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'inactive') {
                            mediaRecorderRef.current.start(1000);
                            setRecordingActive(true);
                            setRecordingTimer(0);
                            recordingTimerRef.current = setInterval(() => {
                                setRecordingTimer(prev => prev + 1);
                            }, 1000);
                        }
                    }
                }, 1000);
            } catch (err) {
                stopRecordingCapture();
                console.error("Failed to start full meeting recording:", err);
                setAlertPopup({
                    show: true,
                    title: 'Recording Cancelled',
                    message: 'Select the current meeting tab or window to record the full meeting screen.'
                });
            }
        } else {
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
                recordingTimerRef.current = null;
            }
            setRecordingTimer(0);
            
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
            } else {
                stopRecordingCapture();
                setRecordingActive(false);
            }
        }
    };

    const handleCheckIn = async (newStatus) => {
        try {
            const endpoint = newStatus === 'checked_in' ? 'check-in' : 'check-out';
            const res = await api.post(`/collab-meetings/meeting/${meetingId}/${endpoint}`);
            if (res.data?.status === 'success') {
                setCheckInStatus(newStatus);
                // Reload meeting data to get updated participant check-in lists
                const detailsRes = await api.get(`/collab-meetings/meeting/${meetingId}`);
                if (detailsRes.data?.status === 'success') {
                    setMeetingDetails(detailsRes.data.data);
                }
            }
        } catch (err) {
            console.error("Failed to update check-in status:", err);
            setAlertPopup({
                show: true,
                title: 'Check-in Error',
                message: 'Failed to broadcast venue check-in status to room controller.'
            });
        }
    };



    const handleToggleHandRaise = async () => {
        const nextState = !handRaised;
        setHandRaised(nextState);
        const myUserId = String(user?.user_id || user?.id);
        const signalMsg = nextState ? `[SIGNAL]:HAND_RAISE_ON:${myUserId}:${localName}` : `[SIGNAL]:HAND_RAISE_OFF:${myUserId}:${localName}`;
        
        try {
            await api.post(`/collab-meetings/meeting/${meetingId}/chat`, {
                message: signalMsg,
                sender_name: localName
            });
            fetchChats(); // Force sync immediately
        } catch (err) {
            console.error("Failed to broadcast hand raise", err);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim()) return;
        const text = newMessage;
        setNewMessage('');
        try {
            await api.post(`/collab-meetings/meeting/${meetingId}/chat`, {
                message: text,
                sender_name: localName
            });
            fetchChats();
        } catch (err) {
            console.error("Failed to send chat message:", err);
        }
    };

    const handleShareMeeting = () => {
        const meetingLink = `${window.location.origin}/meetings?room=${meetingId}`;
        navigator.clipboard.writeText(meetingLink);
        setAlertPopup({
            show: true,
            title: 'Share Link Copied',
            message: `Meeting room link copied to clipboard successfully:\n${meetingLink}`
        });
    };



    const handleSendEmailInvite = async (e) => {
        e.preventDefault();
        if (!emailInviteTo || !emailInviteTo.includes('@')) {
            setAlertPopup({
                show: true,
                title: 'Invalid Email',
                message: 'Please enter a valid recipient email address.'
            });
            return;
        }

        setEmailInviteSending(true);
        try {
            const res = await api.post('/collab-meetings/send-email-invite', {
                to: emailInviteTo,
                subject: emailInviteSubject,
                body: emailInviteBody
            });

            if (res.data?.status === 'success') {
                setAlertPopup({
                    show: true,
                    title: 'Email Sent',
                    message: `Invitation successfully sent to ${emailInviteTo}!`
                });
                setShowEmailModal(false);
                setEmailInviteTo('');
            } else {
                setAlertPopup({
                    show: true,
                    title: 'Sending Failed',
                    message: res.data?.message || 'Failed to send email. Please try again.'
                });
            }
        } catch (err) {
            console.error('Failed to send email invite:', err);
            setAlertPopup({
                show: true,
                title: 'Error',
                message: err.response?.data?.message || 'An unexpected error occurred while sending the email.'
            });
        } finally {
            setEmailInviteSending(false);
        }
    };

    const handleStartRecording = async () => {
        try {
            recordedChunksRef.current = [];
            
            let displayStream;
            if (screenShareActive && screenStreamRef.current) {
                displayStream = screenStreamRef.current;
            } else if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
                try {
                    displayStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
                } catch (e) {
                    displayStream = streamRef.current;
                }
            } else {
                displayStream = streamRef.current;
            }

            if (!displayStream) {
                setAlertPopup({ show: true, title: 'Recording Error', message: 'No active video stream available to record.' });
                return;
            }

            const tracks = [...displayStream.getVideoTracks()];
            if (streamRef.current && streamRef.current.getAudioTracks().length > 0) {
                tracks.push(streamRef.current.getAudioTracks()[0]);
            }

            const combinedStream = new MediaStream(tracks);
            let options = { mimeType: 'video/webm;codecs=vp9,opus' };
            if (!MediaRecorder.isTypeSupported(options.mimeType)) {
                options = { mimeType: 'video/webm' };
            }

            const recorder = new MediaRecorder(combinedStream, options);
            recorder.ondataavailable = (event) => {
                if (event.data && event.data.size > 0) {
                    recordedChunksRef.current.push(event.data);
                }
            };

            recorder.onstop = () => {
                if (recordedChunksRef.current.length > 0) {
                    const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `Meeting_Recording_${meetingId || 'Video'}_${new Date().toISOString().split('T')[0]}.webm`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                    setAlertPopup({ show: true, title: 'Recording Saved', message: 'Meeting video recording has been saved to your computer Downloads folder.' });
                }
                setRecordingActive(false);
            };

            recorder.start(1000);
            mediaRecorderRef.current = recorder;
            setRecordingActive(true);
            setAlertPopup({ show: true, title: 'Recording Started', message: 'Meeting recording is active. Video will automatically download when stopped.' });
        } catch (err) {
            console.error("Failed to start recording", err);
            setAlertPopup({ show: true, title: 'Recording Error', message: 'Failed to start meeting recording.' });
        }
    };

    const handleStopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            try {
                mediaRecorderRef.current.stop();
            } catch (e) {}
        }
        setRecordingActive(false);
    };

    const handleEndMeeting = () => {
        setConfirmPopup({
            show: true,
            title: isHost ? 'End Meeting for All' : 'Leave Meeting',
            message: isHost 
                ? 'Are you sure you want to end this meeting? This will disconnect all participants immediately.'
                : 'Are you sure you want to leave this meeting?',
            onConfirm: async () => {
                setConfirmPopup({ show: false });
                if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                    try { mediaRecorderRef.current.stop(); } catch(e){}
                }
                if (transcript.length > 0 || aiSummary) {
                    handleDownloadAiMinutes();
                }
                try {
                    await api.post(`/collab-meetings/meeting/${meetingId}/leave`);
                } catch (err) {
                    console.error("Failed to update leave status on server", err);
                }
                if (onLeave) {
                    onLeave();
                }
            }
        });
    };

    const handleDownloadAttendance = () => {
        if (!participants || participants.length === 0) return;
        
        const csvRows = [];
        const companyName = "eNova Software and Hardware Solutions P Ltd.";
        const mTitle = meetingTitle || meetingDetails?.title || 'Collaborative Meeting';

        // Calculate Date
        const rawStart = meetingStartTime || meetingDetails?.start_time;
        const mDate = rawStart
            ? new Date(rawStart).toLocaleDateString('en-GB')
            : new Date().toLocaleDateString('en-GB');

        // Calculate Duration
        let mDuration = 'N/A';
        if (rawStart) {
            const startD = new Date(rawStart);
            if (!isNaN(startD.getTime())) {
                const endD = meetingDetails?.end_time ? new Date(meetingDetails.end_time) : new Date();
                const diffMs = Math.max(0, endD - startD);
                const diffMins = Math.floor(diffMs / 60000);
                const hrs = Math.floor(diffMins / 60);
                const mins = diffMins % 60;
                mDuration = hrs > 0 ? `${hrs} hr${hrs > 1 ? 's' : ''} ${mins} min${mins !== 1 ? 's' : ''}` : `${mins} min${mins !== 1 ? 's' : ''}`;
            }
        }

        const totalCount = participants.length;

        // Top Header Rows (Matching eNova Corporate Excel Report Format)
        csvRows.push([`"${companyName}"`]);
        csvRows.push([`"Meeting Attendance Report - ${mTitle.replace(/"/g, '""')}"`]);
        csvRows.push([`"Date: ${mDate} | Duration: ${mDuration} | Total Count: ${totalCount}"`]);
        csvRows.push([]); // Blank separator line

        // Column Headers (Name, Email, Join Time, End Time)
        csvRows.push(['Name', 'Email', 'Join Time', 'End Time'].map(h => `"${h}"`).join(','));
        
        participants.forEach(p => {
            const name = `"${(p.name || p.guest_email || p.email || 'Participant').replace(/"/g, '""')}"`;
            const email = `"${(p.email || p.guest_email || 'N/A').replace(/"/g, '""')}"`;
            
            // Format Join Time cleanly (12-hour AM/PM format)
            let joinTimeStr = 'N/A';
            const rawJoin = p.joined_at || p.join_time || p.created_at;
            if (rawJoin) {
                const jDate = new Date(rawJoin);
                if (!isNaN(jDate.getTime())) {
                    joinTimeStr = jDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
                } else {
                    joinTimeStr = String(rawJoin);
                }
            } else {
                joinTimeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
            }

            // Format End Time cleanly (12-hour AM/PM format)
            let endTimeStr = 'Active';
            const rawEnd = p.left_at || p.end_time || p.leave_time;
            if (rawEnd) {
                const eDate = new Date(rawEnd);
                if (!isNaN(eDate.getTime())) {
                    endTimeStr = eDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
                } else {
                    endTimeStr = String(rawEnd);
                }
            } else if (p.join_status === 'left' || p.join_status === 'ended') {
                endTimeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
            }

            csvRows.push([name, email, `"${joinTimeStr}"`, `"${endTimeStr}"`].join(','));
        });

        const csvString = csvRows.join('\n');
        const blob = new Blob(['\uFEFF' + csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Meeting_Attendance_${meetingId}_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleAddPeopleSearch = (query) => {
        setAddPeopleSearch(query);
        if (!query.trim()) {
            setAddPeopleSuggestions([]);
            return;
        }
        // Filter participants not yet in meeting + match query
        const q = query.toLowerCase();
        const suggestions = participants.filter(p => {
            const name = (p.name || p.email || '').toLowerCase();
            return name.includes(q);
        }).slice(0, 6);
        setAddPeopleSuggestions(suggestions);
    };

    const handleInvitePerson = (person) => {
        setAddPeopleInvited(prev => {
            if (prev.find(p => p.user_id === person.user_id)) return prev;
            return [...prev, person];
        });
        setAddPeopleSearch('');
        setAddPeopleSuggestions([]);
    };

    const handleCopyMeetingLink = () => {
        const link = `${window.location.origin}/meetings?room=${meetingId}`;
        navigator.clipboard.writeText(link).then(() => {
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2500);
        });
    };

    // Tile context menu helpers
    const openTileMenu = (e, userId) => {
        e.stopPropagation();
        setTileMenu({ userId, x: e.clientX, y: e.clientY });
    };

    const closeTileMenu = () => setTileMenu(null);

    const handleRemoveTile = (userId) => {
        setMinimisedTiles(prev => [...prev, userId]);
        closeTileMenu();
    };

    const handleMinimiseTile = (userId) => {
        setMinimisedTiles(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
        closeTileMenu();
    };

    const handlePinForMyself = (userId) => {
        setPinnedUserId(pinnedUserId === userId ? null : userId);
        setPinnedScope('myself');
        closeTileMenu();
    };

    const handlePinForEveryone = (userId) => {
        setPinnedUserId(pinnedUserId === userId ? null : userId);
        setPinnedScope('everyone');
        closeTileMenu();
    };

    const handleShowFullVideoToOthers = () => {
        setShowFullVideoForAll(prev => !prev);
        closeTileMenu();
    };

    const getInitials = (name) => {
        if (!name) return '?';
        return name.trim().charAt(0).toUpperCase();
    };

    const getAvatarColorClass = (name) => {
        const colors = [
            'bg-[#d93025]', // Red
            'bg-[#1a73e8]', // Blue
            'bg-[#188038]', // Green
            'bg-[#f29900]', // Yellow
            'bg-[#8e24aa]', // Purple
            'bg-[#007b83]', // Teal
            'bg-[#c2185b]'  // Pink
        ];
        if (!name) return colors[0];
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    };

    const localName = user?.name || (user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : 'You');

    // Filter remote participants to exclude local user and participants who have left
    const remoteParticipants = participants.filter(p => 
        String(p.user_id) !== String(user?.user_id || user?.id) && 
        (p.join_status === 'approved' || !p.join_status)
    );
    const activeRemoteParticipants = remoteParticipants; // Show active remote participants in video grid
    const totalFeeds = 1 + activeRemoteParticipants.length;

    // Helper to render the waiting screens layout
    const renderWaitingLayout = (contentCard) => {
        return (
            <div className="min-h-screen w-full bg-[#0a0a0b] flex flex-col items-center justify-center p-6 text-slate-100 font-sans relative overflow-hidden">
                <div className="w-full max-w-md bg-[#1c1c1f] border border-slate-700/60 rounded-3xl p-8 shadow-[0_0_40px_rgba(0,0,0,0.5)] relative z-10 hover:border-slate-600 transition-all duration-300">
                    <button 
                        onClick={onLeave} 
                        className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition-all cursor-pointer"
                        title="Exit Room"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    {contentCard}
                </div>
            </div>
        );
    };

    // ── Pre-Call (Green Room) ────────────────────────────────────────────────
    if (joinStatus === 'precall') {
        return (
            <div className="min-h-screen w-full bg-[#121214] flex flex-col lg:flex-row items-center justify-center p-6 gap-8">
                <div className="w-full max-w-2xl bg-black rounded-3xl overflow-hidden aspect-video relative border border-slate-800 shadow-2xl flex items-center justify-center group">
                    {camActive ? (
                        <video 
                            autoPlay 
                            playsInline 
                            muted 
                            className="w-full h-full object-cover scale-x-flipped"
                            ref={precallVideoRef}
                        />
                    ) : (
                        <div className="flex flex-col items-center gap-3">
                            <div className={`w-24 h-24 rounded-full ${getAvatarColorClass(user?.name || user?.first_name || 'User')} flex items-center justify-center text-white text-3xl font-bold shadow-lg uppercase`}>
                                {getInitials(user?.name || user?.first_name || 'User')}
                            </div>
                            <span className="text-slate-400 font-medium">Camera is off</span>
                        </div>
                    )}
                    
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4 z-10">
                        <button onClick={() => {
                            if (precallStreamRef.current && precallStreamRef.current.getAudioTracks().length > 0) {
                                precallStreamRef.current.getAudioTracks()[0].enabled = !micActive;
                            }
                            setMicActive(!micActive);
                        }} className={`w-12 h-12 rounded-full flex items-center justify-center transition-all shadow-lg ${micActive ? 'bg-[#3c4043] text-white hover:bg-[#4a4d51]' : 'bg-[#ea4335] text-white hover:bg-[#d93025]'}`}>
                            {micActive ? (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 0 0 6-6v-1.5m-6 7.5a6 6 0 0 1-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 0 1-3-3V4.5a3 3 0 1 1 6 0v8.25a3 3 0 0 1-3 3Z" /></svg>
                            ) : (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11.953 2.042a3 3 0 0 1 2.983 2.5l.064.958v4.5a3 3 0 0 1-2.983 2.5l-.064.958h-1.953a3 3 0 0 1-2.983-2.5l-.064-.958v-4.5a3 3 0 0 1 2.983-2.5l.064-.958h1.953ZM12 18.75a6 6 0 0 0 6-6v-1.5m-6 7.5a6 6 0 0 1-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5" /><line x1="2" y1="2" x2="22" y2="22" stroke="currentColor" strokeWidth="2" /></svg>
                            )}
                        </button>
                        <button onClick={() => {
                            if (camActive && precallStreamRef.current) {
                                precallStreamRef.current.getTracks().forEach(t => t.stop());
                                precallStreamRef.current = null;
                            }
                            setCamActive(!camActive);
                        }} className={`w-12 h-12 rounded-full flex items-center justify-center transition-all shadow-lg ${camActive ? 'bg-[#3c4043] text-white hover:bg-[#4a4d51]' : 'bg-[#ea4335] text-white hover:bg-[#d93025]'}`}>
                            {camActive ? (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" d="M15.75 10.5l4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" /></svg>
                            ) : (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" d="M15.75 10.5l4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" /><line x1="2" y1="2" x2="22" y2="22" stroke="currentColor" strokeWidth="2" /></svg>
                            )}
                        </button>
                    </div>
                </div>

                <div className="w-full lg:w-96 flex flex-col items-center lg:items-start text-center lg:text-left text-white">
                    <h1 className="text-3xl font-semibold mb-2">{meetingDetails?.title || 'Meeting'}</h1>
                    <p className="text-slate-400 mb-8 text-sm">
                        {meetingDetails?.building ? `Room: ${meetingDetails.building} ${meetingDetails.floor ? `- ${meetingDetails.floor}` : ''}` : ''}
                    </p>
                    
                    <div className="flex flex-col gap-4 mb-8 w-full max-w-sm bg-[#18181b] p-6 rounded-3xl border border-slate-800 shadow-xl">
                        <h3 className="text-xs font-black text-slate-300 uppercase tracking-widest mb-1 pl-1">Pre-join Checklist</h3>
                        
                        {/* Network Status */}
                        <div className={`flex items-center justify-between p-4 rounded-xl border ${precallNetworkStatus.includes('Good') ? 'bg-emerald-500/10 border-emerald-500/30' : precallNetworkStatus.includes('Fair') ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                            <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${precallNetworkStatus.includes('Good') ? 'bg-emerald-500/20 text-emerald-400' : precallNetworkStatus.includes('Fair') ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`}>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.288 15.038a5.25 5.25 0 0 1 7.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 0 1 1.06 0Z" /></svg>
                                </div>
                                <div>
                                    <p className="text-xs font-bold text-white">Network Connection</p>
                                    <p className={`text-[10.5px] font-bold ${precallNetworkStatus.includes('Good') ? 'text-emerald-400' : precallNetworkStatus.includes('Fair') ? 'text-yellow-400' : 'text-red-400'}`}>{precallNetworkStatus}</p>
                                </div>
                            </div>
                            {precallNetworkStatus.includes('Good') && <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" /></svg>}
                        </div>

                        {/* Audio Status */}
                        <div className={`flex items-center justify-between p-4 rounded-xl border ${!micActive ? 'bg-slate-800/50 border-slate-700/50' : audioVerified ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-yellow-500/10 border-yellow-500/30'}`}>
                            <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${!micActive ? 'bg-slate-700 text-slate-400' : audioVerified ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                    {micActive ? (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 0 0 6-6v-1.5m-6 7.5a6 6 0 0 1-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 0 1-3-3V4.5a3 3 0 1 1 6 0v8.25a3 3 0 0 1-3 3Z" /></svg>
                                    ) : (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11.953 2.042a3 3 0 0 1 2.983 2.5l.064.958v4.5a3 3 0 0 1-2.983 2.5l-.064.958h-1.953a3 3 0 0 1-2.983-2.5l-.064-.958v-4.5a3 3 0 0 1 2.983-2.5l.064-.958h1.953ZM12 18.75a6 6 0 0 0 6-6v-1.5m-6 7.5a6 6 0 0 1-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5" /><line x1="2" y1="2" x2="22" y2="22" stroke="currentColor" strokeWidth="2.5" /></svg>
                                    )}
                                </div>
                                <div>
                                    <p className="text-xs font-bold text-white">Microphone</p>
                                    <div className="flex items-center gap-2">
                                        <p className={`text-[10.5px] font-bold ${!micActive ? 'text-slate-400' : audioVerified ? 'text-emerald-400' : 'text-yellow-400'}`}>
                                            {!micActive ? 'Muted' : audioVerified ? 'Looks Good' : 'Testing Audio...'}
                                        </p>
                                        {micActive && (
                                            <div className="flex items-center gap-1 h-3 mt-0.5">
                                                <div className={`w-1 rounded-full transition-all duration-75 ${audioVerified ? 'bg-emerald-500' : 'bg-yellow-500'}`} style={{ height: Math.max(20, precallAudioLevel) + '%' }}></div>
                                                <div className={`w-1 rounded-full transition-all duration-75 delay-75 ${audioVerified ? 'bg-emerald-500' : 'bg-yellow-500'}`} style={{ height: Math.max(20, precallAudioLevel * 0.8) + '%' }}></div>
                                                <div className={`w-1 rounded-full transition-all duration-75 delay-150 ${audioVerified ? 'bg-emerald-500' : 'bg-yellow-500'}`} style={{ height: Math.max(20, precallAudioLevel * 0.6) + '%' }}></div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {!micActive ? (
                                <span className="text-[10px] font-bold text-slate-300 bg-slate-700 px-2 py-1 rounded-lg">OFF</span>
                            ) : audioVerified ? (
                                <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" /></svg>
                            ) : (
                                <svg className="w-5 h-5 text-yellow-500 animate-spin" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" /></svg>
                            )}
                        </div>

                        {/* Video Status */}
                        <div className={`flex items-center justify-between p-4 rounded-xl border ${!camActive ? 'bg-slate-800/50 border-slate-700/50' : cameraVerified ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-yellow-500/10 border-yellow-500/30'}`}>
                            <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${!camActive ? 'bg-slate-700 text-slate-400' : cameraVerified ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                    {camActive ? (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" /></svg>
                                    ) : (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" /><line x1="2" y1="2" x2="22" y2="22" stroke="currentColor" strokeWidth="2.5" /></svg>
                                    )}
                                </div>
                                <div>
                                    <p className="text-xs font-bold text-white">Camera</p>
                                    <p className={`text-[10.5px] font-bold ${!camActive ? 'text-slate-400' : cameraVerified ? 'text-emerald-400' : 'text-yellow-400'}`}>
                                        {!camActive ? 'Turned Off' : cameraVerified ? 'Looks Good' : 'Starting Camera...'}
                                    </p>
                                </div>
                            </div>
                            {!camActive ? (
                                <span className="text-[10px] font-bold text-slate-300 bg-slate-700 px-2 py-1 rounded-lg">OFF</span>
                            ) : cameraVerified ? (
                                <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" /></svg>
                            ) : (
                                <svg className="w-5 h-5 text-yellow-500 animate-spin" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" /></svg>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-4 w-full lg:w-auto mt-2">
                        <button 
                            onClick={() => {
                                if (precallStreamRef.current) {
                                    precallStreamRef.current.getTracks().forEach(t => t.stop());
                                    precallStreamRef.current = null;
                                }
                                executeJoinMeeting();
                            }}
                            disabled={!(cameraVerified && audioVerified)}
                            className={`font-semibold py-3 px-8 rounded-full transition-all w-full sm:w-auto text-lg flex items-center justify-center gap-2 ${(cameraVerified && audioVerified) ? 'bg-main hover:bg-main/90 text-white shadow-[0_0_15px_rgba(var(--main-color-rgb),0.4)] hover:scale-[1.02]' : 'bg-slate-700 text-slate-400 cursor-not-allowed'}`}
                        >
                            {meetingDetails?.created_by === String(user?.user_id || user?.id) ? 'Join now' : 'Ask to join'}
                        </button>
                        <button 
                            onClick={() => {
                                if (precallStreamRef.current) {
                                    precallStreamRef.current.getTracks().forEach(t => t.stop());
                                    precallStreamRef.current = null;
                                }
                                if (onLeave) onLeave();
                                else window.location.href = '/ticket';
                            }}
                            className="font-semibold py-3 px-8 rounded-full transition-all w-full sm:w-auto text-lg border border-slate-700 text-slate-300 hover:bg-slate-800"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ── Loading state ──────────────────────────────────────────────────────────
    if (joinStatus === null) {
        return renderWaitingLayout(
            <div className="flex flex-col items-center gap-6 py-6 text-center">
                <span className="w-12 h-12 rounded-full border-3 border-main border-t-transparent animate-spin"></span>
                <div>
                    <h3 className="text-lg font-bold text-white">Connecting to eNova Meet</h3>
                    <p className="text-xs text-slate-400 mt-1">Establishing secure media node connection...</p>
                </div>
            </div>
        );
    }

    // ── Completed screen ───────────────────────────────────────────────────────
    if (joinStatus === 'completed') {
        return renderWaitingLayout(
            <div className="flex flex-col items-center gap-6 py-4 text-center">
                <div className="w-16 h-16 rounded-full bg-main/10 border border-main/20 flex items-center justify-center">
                    <svg className="w-8 h-8 text-main" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                </div>
                <div>
                    <h2 className="text-xl font-bold text-white mb-1">Meeting Finished</h2>
                    <p className="text-xs text-slate-400 max-w-xs leading-relaxed">This meeting has ended and is no longer active.</p>
                </div>
                <button onClick={onLeave} className="w-full text-xs bg-main hover:bg-mainHover text-white py-3.5 rounded-xl font-bold transition-all shadow-md hover:shadow-main/20">
                    Back to Dashboard
                </button>
            </div>
        );
    }

    // ── Rejected screen ────────────────────────────────────────────────────────
    if (joinStatus === 'rejected') {
        return renderWaitingLayout(
            <div className="flex flex-col items-center gap-6 py-4 text-center">
                <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                    <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </div>
                <div>
                    <h2 className="text-xl font-bold text-white mb-1">Entry Declined</h2>
                    <p className="text-xs text-slate-400 max-w-xs leading-relaxed">The host has declined your request to join this meeting room.</p>
                </div>
                <button onClick={onLeave} className="w-full text-xs bg-main hover:bg-mainHover text-white py-3.5 rounded-xl font-bold transition-all shadow-md hover:shadow-main/20">
                    Return to Dashboard
                </button>
            </div>
        );
    }

    // ── Not Started (Too Early) ────────────────────────────────────────────────
    if (joinStatus === 'not_started') {
        const formattedTime = meetingStartTime ? new Date(meetingStartTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'the scheduled time';
        return renderWaitingLayout(
            <div className="flex flex-col items-center gap-6 py-4 text-center">
                <div className="w-16 h-16 rounded-full bg-main/10 border border-main/20 flex items-center justify-center">
                    <svg className="w-8 h-8 text-main" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <div>
                    <h2 className="text-xl font-bold text-white mb-1">Meeting hasn't started yet</h2>
                    <p className="text-xs text-slate-400 max-w-xs leading-relaxed">
                        This meeting is scheduled to begin at <strong className="text-slate-200">{formattedTime}</strong>. Please wait here; you will join automatically when it starts.
                    </p>
                </div>
                <div className="flex gap-3 w-full">
                    <button onClick={onLeave} className="flex-grow text-xs bg-slate-800 hover:bg-slate-700 text-white py-3.5 rounded-xl font-bold transition-all border border-slate-700">
                        Leave Room
                    </button>
                    <button onClick={handleCopyMeetingLink} className="flex-shrink-0 bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 rounded-xl px-4 py-3.5 transition-all shadow-sm relative group" title="Copy Meeting Link">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
                        </svg>
                        {linkCopied && (
                            <span className="absolute -top-10 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[10px] px-2.5 py-1.5 rounded-lg shadow-md whitespace-nowrap z-50">
                                Link Copied!
                            </span>
                        )}
                    </button>
                </div>
            </div>
        );
    }

    // ── Waiting Room (pending approval) ────────────────────────────────────────
    if (joinStatus === 'pending') {
        return renderWaitingLayout(
            <div className="flex flex-col items-center gap-6 py-4 text-center">
                <div className="relative w-16 h-16">
                    <div className="w-16 h-16 rounded-full border-2 border-main/30 animate-ping absolute inset-0"></div>
                    <div className="w-16 h-16 rounded-full bg-main/10 border border-main/20 flex items-center justify-center relative">
                        <svg className="w-8 h-8 text-main" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
                        </svg>
                    </div>
                </div>
                <div>
                    <h2 className="text-xl font-bold text-white mb-1">Waiting for Host Approval</h2>
                    <p className="text-xs text-slate-400 max-w-xs leading-relaxed">
                        Your join request has been sent to the host. You'll enter automatically once they approve you.
                    </p>
                </div>
                
                <div className="w-full bg-[#121214] border border-slate-800 rounded-xl px-4 py-3 flex items-center justify-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                    <span className="text-[11px] text-slate-400 font-semibold">
                        Waiting for: <span className="text-white font-bold">{meetingTitle || 'Meeting Room'}</span>
                    </span>
                </div>

                <button onClick={onLeave} className="text-xs text-slate-500 hover:text-white underline underline-offset-2 transition-colors">
                    Cancel and Leave
                </button>
            </div>
        );
    }

    return (
        <div className="flex flex-col bg-[#050505] text-slate-100 font-sans w-full h-screen overflow-hidden justify-between select-none relative">
            
            {/* Recording Countdown Overlay */}
            {recordingCountdown !== null && (
                <div className="absolute inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-md pointer-events-none transition-opacity duration-300">
                    <div className="w-24 h-24 rounded-full bg-red-600 flex items-center justify-center shadow-[0_0_50px_rgba(220,38,38,0.8)] animate-[ping_1s_ease-in-out_infinite]">
                        <span className="text-white text-6xl font-black tabular-nums drop-shadow-2xl">{recordingCountdown}</span>
                    </div>
                </div>
            )}
            

            {/* Host: Pending Join Requests Floating Card */}
            {isHost && pendingRequests.length > 0 && (
                <div className="absolute top-20 left-4 right-4 sm:left-1/2 sm:-translate-x-1/2 sm:right-auto z-[60] bg-[#121214]/95 backdrop-blur border border-amber-500/30 shadow-2xl shadow-amber-950/40 px-5 py-4 rounded-2xl flex flex-col gap-3 sm:min-w-[340px] max-w-md animate-fadeIn">
                    <div className="flex items-center justify-between gap-4 relative">
                        <div className="flex items-center gap-2 relative">
                            <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-ping absolute"></span>
                            <span className="w-2.5 h-2.5 rounded-full bg-amber-500 relative"></span>
                            <p className="text-xs font-black text-amber-400 uppercase tracking-wider ml-1">
                                {pendingRequests.length} participant{pendingRequests.length > 1 ? 's' : ''} waiting
                            </p>
                        </div>
                        {pendingRequests.length > 1 && (
                            <button
                                onClick={handleApproveAllJoin}
                                className="text-[10px] bg-emerald-600 hover:bg-emerald-500 text-white px-2.5 py-1.5 rounded-lg font-black transition-all shadow-lg shadow-emerald-600/20 flex items-center gap-1"
                            >
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                Admit All
                            </button>
                        )}
                    </div>
                    <div className="flex flex-col gap-2 max-h-48 overflow-y-auto pr-1">
                        {pendingRequests.map(req => (
                            <div key={req.user_id} className="flex items-center justify-between bg-[#1a1a1c] border border-slate-800 rounded-xl px-3 py-2 gap-3">
                                <div className="flex items-center gap-3 min-w-0">
                                    <span className="w-8 h-8 rounded-full bg-amber-500/20 text-amber-400 text-xs font-black flex-shrink-0 flex items-center justify-center uppercase">
                                        {(req.name || req.email || '?').charAt(0)}
                                    </span>
                                    <span className="text-xs font-bold text-white truncate">
                                        {req.name?.trim() || req.email || 'Guest'}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handleApproveJoin(req.user_id, 'reject')}
                                        className="text-[10px] bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-1.5 rounded-lg font-bold transition-all"
                                    >
                                        Deny
                                    </button>
                                    <button
                                        onClick={() => handleApproveJoin(req.user_id, 'approve')}
                                        className="text-[10px] bg-amber-500 hover:bg-amber-400 text-amber-950 px-3 py-1.5 rounded-lg font-black transition-all shadow-lg shadow-amber-500/20"
                                    >
                                        Admit
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Content Body split */}
            <div className="flex flex-grow overflow-hidden relative">
                
                {/* Main conference card layout */}
                <main className="flex-grow p-6 flex flex-col justify-between items-center overflow-y-auto w-full relative">
                    
                    {/* Header info (Floating overlay scoped to main video area) */}
                    <header className="absolute top-0 left-0 w-full flex justify-between items-start bg-transparent p-4 z-40 pointer-events-none">
                        <div className="flex items-center gap-3 pointer-events-auto">
                            <span className="bg-red-500/80 backdrop-blur text-white shadow-lg text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                                Live Room
                            </span>
                            {recordingActive && (
                                <span className="bg-[#18181a]/80 backdrop-blur border border-red-500/30 text-white shadow-lg text-[10px] font-bold px-3 py-1 rounded-full tracking-wider flex items-center gap-1.5 transition-all">
                                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                                    REC {Math.floor(recordingTimer / 60).toString().padStart(2, '0')}:{(recordingTimer % 60).toString().padStart(2, '0')}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2 pointer-events-auto">
                            <div className="relative">
                                <button 
                                    onClick={() => setShowShareMenu(!showShareMenu)}
                                    className="text-xs bg-[#18181a]/80 backdrop-blur border border-slate-700 text-slate-300 hover:text-white px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 hover:bg-slate-700 shadow-lg"
                                    title="Share meeting room link"
                                >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
                                    </svg>
                                    <span className="hidden sm:inline">Share Link</span>
                                </button>
                                
                                {showShareMenu && (
                                    <div className="absolute right-0 mt-2 w-48 bg-[#121214] border border-slate-800 rounded-xl shadow-2xl p-2 z-30 space-y-1 animate-fadeIn">
                                        <button
                                            onClick={() => {
                                                const meetingLink = `${window.location.origin}/meetings?room=${meetingId}`;
                                                navigator.clipboard.writeText(meetingLink);
                                                setAlertPopup({
                                                    show: true,
                                                    title: 'Link Copied',
                                                    message: 'Meeting room link copied to clipboard successfully!'
                                                });
                                                setShowShareMenu(false);
                                            }}
                                            className="w-full text-left px-3 py-2 rounded-lg text-xs font-semibold text-slate-300 hover:bg-slate-700 hover:text-white flex items-center gap-2 transition-all"
                                        >
                                            <svg className="w-4 h-4 text-main" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5A3.375 3.375 0 006.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0015 2.25h-1.5a2.251 2.251 0 00-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5H4.875c-.621 0-1.125.504-1.125 1.125v9c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V16.5a9 9 0 00-9-9z" />
                                            </svg>
                                            Copy Link
                                        </button>
                                        
                                        <a
                                            href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`Join my meeting: ${window.location.origin}/meetings?room=${meetingId}`)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={() => setShowShareMenu(false)}
                                            className="w-full text-left px-3 py-2 rounded-lg text-xs font-semibold text-slate-300 hover:bg-slate-700 hover:text-white flex items-center gap-2 transition-all"
                                        >
                                            <svg className="w-4 h-4 text-green-500 fill-current" viewBox="0 0 24 24">
                                                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.739-1.456L0 24zm6.59-4.846c1.6.95 3.188 1.449 4.825 1.451 5.436 0 9.86-4.37 9.864-9.799.002-2.63-1.023-5.101-2.885-6.965C16.528 1.977 14.07 .953 11.45 1.951c-5.44 0-9.866 4.372-9.87 9.802 0 1.714.47 3.393 1.357 4.869l-.993 3.629 3.702-.977zm11.582-6.825c-.323-.16-.1.084-.862-.488-.3-.21-.5-.07-.862.08-.362.15-.5.23-.742.5-.24.26-.5.42-.862.26-.358-.15-1.502-.55-2.863-1.758-1.06-1.01-1.782-2.27-1.992-2.63-.2-.36-.02-.56.16-.72.16-.15.3-.35.5-.5.15-.15.2-.25.3-.42.1-.17.05-.33-.02-.48-.07-.15-.62-1.48-.862-2.06-.24-.58-.5-.5-.68-.5-.18-.01-.38-.01-.58-.01-.2 0-.52.07-.8.38-.28.3-.92.9-.92 2.2s.95 2.56 1.08 2.74c.13.18 1.87 2.85 4.54 4l.98.37c.64.21 1.22.18 1.68.11.52-.08 1.6-.66 1.82-1.28.23-.61.23-1.14.16-1.25-.07-.11-.26-.21-.58-.37z"/>
                                            </svg>
                                            WhatsApp
                                        </a>
                                        
                                        <a
                                            href={`mailto:?subject=${encodeURIComponent('Join my collaborative meeting')}&body=${encodeURIComponent(`Here is the meeting room link:\n${window.location.origin}/meetings?room=${meetingId}`)}`}
                                            onClick={() => setShowShareMenu(false)}
                                            className="w-full text-left px-3 py-2 rounded-lg text-xs font-semibold text-slate-300 hover:bg-slate-700 hover:text-white flex items-center gap-2 transition-all"
                                        >
                                            <svg className="w-4 h-4 text-indigo-400" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                                            </svg>
                                            Email Invitation
                                        </a>
                                    </div>
                                )}
                            </div>
                            <button 
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className={`text-xs px-3 py-1.5 rounded-lg border transition-all flex items-center gap-1.5 shadow-lg relative ${sidebarOpen ? 'bg-red-600 border-red-600 text-white shadow-red-900/30 font-bold' : 'bg-[#18181a]/80 backdrop-blur border-slate-700 text-slate-300 hover:text-white hover:bg-slate-700'}`}
                                title="Toggle AI & Chat Panel"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                                <span className="inline font-extrabold">{sidebarOpen ? 'Close Panel' : 'Sidebar'}</span>
                                {!sidebarOpen && (unreadMessages > 0 || unreadPollsQa > 0) && (
                                    <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full shadow-lg border border-white/20 animate-pulse">
                                        {(unreadMessages + unreadPollsQa) > 9 ? '9+' : (unreadMessages + unreadPollsQa)}
                                    </span>
                                )}
                            </button>
                        </div>
                    </header>
                    
                    {/* Top Floating Active Raised Hands Notification Banner */}
                    {Object.keys(raisedHands).length > 0 && (
                        <div className="w-full flex flex-col items-center gap-2 mb-4 z-30 animate-fadeIn">
                            {Object.entries(raisedHands).map(([uid, uName]) => (
                                <div key={uid} className="bg-gradient-to-r from-amber-500 via-amber-600 to-yellow-500 text-white font-black text-xs px-5 py-2.5 rounded-full shadow-2xl border-2 border-yellow-300 flex items-center justify-between gap-4 max-w-xl w-full animate-bounce">
                                    <div className="flex items-center gap-2.5">
                                        <span className="text-lg">🖐️</span>
                                        <span><strong className="underline decoration-yellow-200 font-extrabold text-sm">{typeof uName === 'string' && uName !== 'true' ? uName : 'Participant'}</strong> raised their hand!</span>
                                    </div>
                                    <button 
                                        onClick={() => {
                                            const signalMsg = `[SIGNAL]:HAND_RAISE_OFF:${uid}:${uName}`;
                                            api.post(`/collab-meetings/meeting/${meetingId}/chat`, { message: signalMsg, sender_name: localName }).catch(() => {});
                                            setRaisedHands(prev => {
                                                const updated = {...prev};
                                                delete updated[uid];
                                                return updated;
                                            });
                                        }}
                                        className="bg-black/30 hover:bg-black/50 text-white text-[10px] font-bold px-3 py-1 rounded-full border border-white/30 transition-all shrink-0 cursor-pointer"
                                    >
                                        Lower Hand
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    
                    {/* Venue / Meeting Type details banner (Hidden for pure online to match Google Meet Edge-to-Edge video) */}
                    {meetingDetails && meetingDetails.meeting_type !== 'online' && (
                        <div className="w-full bg-[#202124] border border-slate-700 rounded-2xl p-4 mb-4 shadow-xl flex flex-wrap items-center justify-between gap-4 z-10">
                            {meetingDetails.meeting_type === 'physical' && (
                                <>
                                    <div className="flex items-center gap-3">
                                        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-main/10 text-main">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                                        </span>
                                        <div className="text-left">
                                            <h4 className="text-xs font-extrabold text-white">Physical Venue: {meetingDetails.room_name || 'Conference Room'}</h4>
                                            <p className="text-[10px] text-slate-400 font-semibold">{meetingDetails.building || 'Main Office'} • Floor {meetingDetails.floor || '1'} • Max Capacity: {meetingDetails.seating_capacity || 'N/A'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className={`text-[10px] font-extrabold uppercase px-2.5 py-1 rounded-full ${
                                            checkInStatus === 'checked_in' ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20' : 
                                            checkInStatus === 'checked_out' ? 'bg-red-500/15 text-red-400 border border-red-500/20' : 
                                            'bg-slate-800 text-slate-400 border border-slate-700'
                                        }`}>
                                            {checkInStatus ? checkInStatus.replace('_', ' ') : 'Not Checked In'}
                                        </span>
                                        <button
                                            onClick={async () => {
                                                try {
                                                    const res = await api.post(`/collab-meetings/meeting/${meetingId}/check-in`);
                                                    if (res.data?.status === 'success') {
                                                        setCheckInStatus('checked_in');
                                                    }
                                                } catch (err) { console.error(err); }
                                            }}
                                            disabled={checkInStatus === 'checked_in'}
                                            className="text-[10px] font-bold bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded-lg transition-all"
                                        >
                                            Check-in
                                        </button>
                                        <button
                                            onClick={async () => {
                                                try {
                                                    const res = await api.post(`/collab-meetings/meeting/${meetingId}/check-out`);
                                                    if (res.data?.status === 'success') {
                                                        setCheckInStatus('checked_out');
                                                    }
                                                } catch (err) { console.error(err); }
                                            }}
                                            disabled={checkInStatus !== 'checked_in'}
                                            className="text-[10px] font-bold bg-red-650 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded-lg transition-all"
                                        >
                                            Check-out
                                        </button>
                                    </div>
                                </>
                            )}

                            {isHybrid && (
                                <>
                                    <div className="flex items-center gap-3">
                                        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-600/10 text-purple-400 font-extrabold text-xs">
                                            {isZoom ? 'Zoom' : 'Meet'}
                                        </span>
                                        <div className="text-left">
                                            <h4 className="text-xs font-extrabold text-white">Hybrid Broadcast: {isZoom ? 'Zoom Meetings' : 'Google Meet'}</h4>
                                            <p className="text-[10px] text-slate-400 font-semibold truncate max-w-sm">
                                                URL: {meetingDetails.external_meeting_url ? (
                                                    <a href={meetingDetails.external_meeting_url} target="_blank" rel="noopener noreferrer" className="text-main hover:underline">
                                                        {meetingDetails.external_meeting_url}
                                                    </a>
                                                ) : (
                                                    <span className="text-red-400">Not configured (API scheduling failed)</span>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {meetingDetails.meeting_passcode && (
                                            <span className="text-[10px] text-slate-350 font-bold bg-slate-800 px-2 py-1 rounded">Passcode: {meetingDetails.meeting_passcode}</span>
                                        )}
                                        <a 
                                            href={meetingDetails.external_meeting_url || '#'} 
                                            onClick={(e) => {
                                                if (!meetingDetails.external_meeting_url) {
                                                    e.preventDefault();
                                                    setAlertPopup({
                                                        show: true,
                                                        title: 'No Meeting Link',
                                                        message: isHost 
                                                            ? 'Zoom/Meet link is missing. Please enter a manual link in the section below first.'
                                                            : 'Zoom/Meet link is missing. Please wait for the host to update the manual meeting URL.'
                                                    });
                                                }
                                            }}
                                            target="_blank" 
                                            rel="noopener noreferrer" 
                                            className={`text-[10px] font-bold px-3 py-1.5 rounded-lg transition-all ${
                                                meetingDetails.external_meeting_url
                                                    ? 'bg-main hover:bg-mainHover text-white'
                                                    : 'bg-slate-800 border border-slate-700 text-slate-500 cursor-not-allowed'
                                            }`}
                                        >
                                            Launch Call
                                        </a>
                                        <button
                                            onClick={handleSyncMeetingData}
                                            disabled={isSyncing}
                                            className={`text-[10px] font-bold bg-slate-800 hover:bg-slate-700 text-slate-200 px-3 py-1.5 rounded-lg transition-all ${
                                                isSyncing ? 'opacity-50 cursor-not-allowed' : ''
                                            }`}
                                        >
                                            {isSyncing ? 'Syncing...' : 'Sync Call Data'}
                                        </button>
                                    </div>
                                </>
                            )}

                            {meetingDetails.meeting_type === 'online' && (
                                <>
                                    <div className="flex items-center gap-3">
                                        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600/10 text-emerald-400">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                                        </span>
                                        <div className="text-left">
                                            <h4 className="text-xs font-extrabold text-white">Online Video Meeting Room</h4>
                                            <p className="text-[10px] text-slate-400 font-semibold">LiveKit Node channel: `meeting-{meetingId}` • latency: 12ms</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 text-slate-400 text-[10px] font-bold">
                                        <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-ping"></span> LiveKit Connected</span>
                                        <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-blue-500"></span> Speech Engine Active</span>
                                    </div>
                                </>
                            )}
                        </div>
                    )}



                    {(() => {
                        if (meetingDetails && meetingDetails.meeting_type !== 'online') {
                            
                            return (
                                <div className="w-full h-full flex flex-col md:flex-row gap-6 overflow-hidden select-none">
                                    {/* Left: Main details and quick action block */}
                                    <div className="flex-grow md:w-3/5 bg-[#121214]/65 border border-slate-800/60 rounded-3xl p-8 backdrop-blur shadow-2xl flex flex-col justify-between overflow-y-auto">
                                        <div className="space-y-6">
                                            {/* Header Section */}
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                                        isPhysical ? 'bg-emerald-500/10 text-emerald-455 border border-emerald-500/20' : 'bg-purple-500/10 text-purple-400 border border-purple-500/20'
                                                    }`}>
                                                        <span className={`w-1.5 h-1.5 rounded-full ${isPhysical ? 'bg-emerald-500 animate-pulse' : 'bg-purple-500 animate-pulse'}`}></span>
                                                        {isPhysical ? 'Physical Workspace' : isGoogleMeet ? 'Hybrid Google Meet' : 'Hybrid Zoom Stream'}
                                                    </span>
                                                    <h2 className="text-2xl font-black text-white mt-3 tracking-tight">{meetingDetails.title}</h2>
                                                    <p className="text-xs text-slate-400 font-semibold mt-1">Room ID: {meetingDetails.id} • Scheduled Slot</p>
                                                </div>
                                                
                                                {/* Status badge */}
                                                <div className="bg-[#18181b] border border-slate-800 rounded-xl px-4 py-2 text-right">
                                                    <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Status</span>
                                                    <span className="text-xs text-emerald-400 font-extrabold capitalize">{meetingDetails.status || 'Scheduled'}</span>
                                                </div>
                                            </div>

                                            {/* Provider Failure / Missing Link Warning Banner */}
                                            {isHybrid && (!meetingDetails.external_meeting_url || meetingDetails.status === 'pending_provider') && (
                                                <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-5 space-y-4">
                                                    <div className="flex items-start gap-3">
                                                        <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                        </svg>
                                                        <div className="space-y-1 text-left">
                                                            <h4 className="text-xs font-black text-red-400 uppercase tracking-wider">Zoom/Meet Scheduling Failed</h4>
                                                            <p className="text-[11px] text-slate-350 font-semibold leading-relaxed">
                                                                The external provider API rejected the scheduling request (HTTP 400).
                                                                {isHost ? ' You can enter a manual Zoom/Meet link below, or convert this session into a standard online room.' : ' Please wait for the host to provide a manual meeting link or update the room configuration.'}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {isHost && (
                                                        <div className="space-y-3 pt-3 border-t border-red-500/25">
                                                            <div className="text-left">
                                                                <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">Manual Meeting URL</label>
                                                                <div className="flex gap-2">
                                                                    <input 
                                                                        type="url" 
                                                                        placeholder="https://zoom.us/j/... or https://meet.google.com/..." 
                                                                        id="manualUrlInput"
                                                                        className="flex-grow bg-[#09090b] border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:border-red-500 outline-none"
                                                                    />
                                                                    <button 
                                                                        onClick={async () => {
                                                                            const val = document.getElementById('manualUrlInput')?.value?.trim();
                                                                            if (!val) {
                                                                                setAlertPopup({
                                                                                    show: true,
                                                                                    title: 'Invalid URL',
                                                                                    message: 'Please enter a valid URL.'
                                                                                });
                                                                                return;
                                                                            }
                                                                            try {
                                                                                const updateRes = await api.post(`/collab-meetings/meeting/${meetingId}/update-url`, {
                                                                                    external_meeting_url: val
                                                                                });
                                                                                if (updateRes.data?.status === 'success') {
                                                                                    setAlertPopup({
                                                                                        show: true,
                                                                                        title: 'Success',
                                                                                        message: 'Manual link updated successfully!'
                                                                                    });
                                                                                    loadMeetingData();
                                                                                }
                                                                            } catch (err) {
                                                                                console.error('Failed to update URL:', err);
                                                                                setAlertPopup({
                                                                                    show: true,
                                                                                    title: 'Update Failed',
                                                                                    message: err.response?.data?.message || 'Failed to update meeting link.'
                                                                                });
                                                                            }
                                                                        }}
                                                                        className="bg-red-650 hover:bg-red-600 text-white font-bold px-4 py-2 rounded-xl transition-all text-xs"
                                                                    >
                                                                        Save URL
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center justify-between pt-2">
                                                                <span className="text-[10px] text-slate-450 font-bold">Alternative:</span>
                                                                <button 
                                                                    onClick={() => {
                                                                        setConfirmPopup({
                                                                            show: true,
                                                                            title: 'Convert Meeting Room',
                                                                            message: 'Convert this to an eNova Online Video Room? This will activate the built-in video chat interface.',
                                                                            onConfirm: async () => {
                                                                                setConfirmPopup({ show: false, message: '', title: '', onConfirm: null });
                                                                                try {
                                                                                    const updateRes = await api.post(`/collab-meetings/meeting/${meetingId}/update-url`, {
                                                                                        meeting_type: 'online'
                                                                                    });
                                                                                    if (updateRes.data?.status === 'success') {
                                                                                        setAlertPopup({
                                                                                            show: true,
                                                                                            title: 'Conversion Successful',
                                                                                            message: 'Successfully converted to Online Room!'
                                                                                        });
                                                                                        loadMeetingData();
                                                                                    }
                                                                                } catch (err) {
                                                                                    console.error('Failed to convert meeting type:', err);
                                                                                    setAlertPopup({
                                                                                        show: true,
                                                                                        title: 'Conversion Failed',
                                                                                        message: 'Failed to convert meeting type.'
                                                                                    });
                                                                                }
                                                                            }
                                                                        });
                                                                    }}
                                                                    className="bg-[#18181b] hover:bg-slate-800 border border-slate-800 text-slate-200 px-3.5 py-2 rounded-xl font-bold transition-all text-xs"
                                                                >
                                                                    Switch to eNova Video Room
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Details Grid */}
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-slate-800/40">
                                                {isPhysical && (
                                                    <>
                                                        <div className="bg-[#18181b]/50 border border-slate-800/50 rounded-2xl p-4">
                                                            <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Building & Floor</span>
                                                            <span className="text-sm text-white font-extrabold block mt-1">{meetingDetails.building || 'Main HQ'} • Floor {meetingDetails.floor || '1st'}</span>
                                                        </div>
                                                        <div className="bg-[#18181b]/50 border border-slate-800/50 rounded-2xl p-4">
                                                            <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Venue Name & Capacity</span>
                                                            <span className="text-sm text-white font-extrabold block mt-1">{meetingDetails.room_name || 'Meeting Room'} ({meetingDetails.seating_capacity || 0} seats)</span>
                                                        </div>
                                                    </>
                                                )}
                                                
                                                {isHybrid && (
                                                    <>
                                                        <div className="bg-[#18181b]/50 border border-slate-800/50 rounded-2xl p-4 col-span-2">
                                                            <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Meeting URL Link</span>
                                                            <a 
                                                                href={meetingDetails.external_meeting_url} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer" 
                                                                className="text-xs text-main font-bold block mt-1 hover:underline truncate"
                                                            >
                                                                {meetingDetails.external_meeting_url || 'No link provided'}
                                                            </a>
                                                        </div>
                                                        <div className="bg-[#18181b]/50 border border-slate-800/50 rounded-2xl p-4">
                                                            <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Passcode</span>
                                                            <span className="text-sm text-white font-mono font-bold block mt-1">{meetingDetails.meeting_passcode || 'None'}</span>
                                                        </div>
                                                    </>
                                                )}
                                                
                                                <div className="bg-[#18181b]/50 border border-slate-800/50 rounded-2xl p-4">
                                                    <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Start Time</span>
                                                    <span className="text-xs text-slate-200 font-semibold block mt-1">
                                                        {new Date(meetingDetails.start_time).toLocaleString()}
                                                    </span>
                                                </div>
                                                <div className="bg-[#18181b]/50 border border-slate-800/50 rounded-2xl p-4">
                                                    <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">End Time</span>
                                                    <span className="text-xs text-slate-200 font-semibold block mt-1">
                                                        {new Date(meetingDetails.end_time).toLocaleString()}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* AI Summary / Agenda Block */}
                                            <div className="bg-purple-950/10 border border-purple-900/20 rounded-2xl p-5 space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <h4 className="text-xs font-black text-purple-400 uppercase tracking-widest flex items-center gap-1.5">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364.364l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
                                                        AI Meeting Insights & Agenda
                                                    </h4>
                                                </div>
                                                <p className="text-xs text-slate-300 leading-relaxed font-semibold">
                                                    {meetingDetails.ai_summary || "No automated AI insights generated yet. Insights will sync automatically as users broadcast updates."}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Action buttons at bottom */}
                                        <div className="flex flex-wrap items-center gap-4 mt-6 pt-6 border-t border-slate-800/40">
                                            {isPhysical && (
                                                <>
                                                    <button 
                                                        onClick={() => handleCheckIn('checked_in')}
                                                        className={`flex-grow sm:flex-grow-0 flex items-center justify-center gap-2 text-xs font-black px-6 py-3.5 rounded-xl border shadow-lg transition-all ${
                                                            checkInStatus === 'checked_in' 
                                                                ? 'bg-emerald-600/10 border-emerald-500/20 text-emerald-450 cursor-default' 
                                                                : 'bg-emerald-650 hover:bg-emerald-600 border-emerald-600 text-white shadow-emerald-950/20'
                                                        }`}
                                                        disabled={checkInStatus === 'checked_in'}
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                                        {checkInStatus === 'checked_in' ? 'Checked In' : 'Check-in to Venue'}
                                                    </button>
                                                    
                                                    {checkInStatus === 'checked_in' && (
                                                        <button 
                                                            onClick={() => handleCheckIn('checked_out')}
                                                            className="flex-grow sm:flex-grow-0 flex items-center justify-center gap-2 text-xs font-black bg-red-650 hover:bg-red-600 border border-red-600 text-white px-6 py-3.5 rounded-xl shadow-lg shadow-red-950/20 transition-all"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                                                            Check-out Room
                                                        </button>
                                                    )}
                                                </>
                                            )}

                                            {isHybrid && (
                                                <>
                                                    <a 
                                                        href={meetingDetails.external_meeting_url || '#'}
                                                        onClick={(e) => {
                                                            if (!meetingDetails.external_meeting_url) {
                                                                e.preventDefault();
                                                                setAlertPopup({
                                                                    show: true,
                                                                    title: 'No Meeting Link',
                                                                    message: isHost 
                                                                        ? 'Zoom/Meet link is missing. Please enter a manual link in the section above first.'
                                                                        : 'Zoom/Meet link is missing. Please wait for the host to update the manual meeting URL.'
                                                                });
                                                            }
                                                        }}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={`flex-grow sm:flex-grow-0 flex items-center justify-center gap-2 text-xs font-black px-6 py-3.5 rounded-xl shadow-lg transition-all ${
                                                            meetingDetails.external_meeting_url 
                                                                ? 'bg-red-650 hover:bg-red-600 border border-red-600 text-white shadow-red-950/20'
                                                                : 'bg-slate-800 border border-slate-700 text-slate-550 cursor-not-allowed'
                                                        }`}
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                                        Launch {isGoogleMeet ? 'Google Meet' : 'Zoom Call'}
                                                    </a>
                                                    
                                                    <button 
                                                        onClick={handleSyncMeetingData}
                                                        disabled={isSyncing}
                                                        className={`flex-grow sm:flex-grow-0 flex items-center justify-center gap-2 text-xs font-black bg-[#18181b] hover:bg-slate-800 border border-slate-800 text-slate-355 px-6 py-3.5 rounded-xl transition-all ${
                                                            isSyncing ? 'opacity-50 cursor-not-allowed' : ''
                                                        }`}
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 7.89M9 11l3-3 3 3m-3-3v12" /></svg>
                                                        {isSyncing ? 'Syncing...' : 'Sync Broadcast Logs'}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Right: Participant Activity List */}
                                    <div className="w-full md:w-2/5 bg-[#121214]/65 border border-slate-800/60 rounded-3xl p-6 backdrop-blur shadow-2xl flex flex-col justify-between overflow-hidden">
                                        <div className="space-y-4 flex-grow overflow-y-auto">
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-xs font-black text-white uppercase tracking-wider">Workspace Attendees</h3>
                                                <span className="text-[10px] bg-slate-800 text-slate-400 font-extrabold px-2 py-0.5 rounded">
                                                    {participants.length} total
                                                </span>
                                            </div>

                                            <div className="space-y-3">
                                                {participants.map((part) => {
                                                    const displayName = part.name?.trim() || part.guest_email || part.email || 'Participant';
                                                    const isYou = String(part.user_id) === String(user?.user_id || user?.id);
                                                    return (
                                                        <div 
                                                            key={part.id} 
                                                            className="flex items-center justify-between p-3 rounded-xl bg-[#18181b]/50 border border-slate-850/40 hover:bg-[#18181b]/80 transition-all"
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <div className={`w-9 h-9 rounded-xl ${getAvatarColorClass(displayName)} flex items-center justify-center text-white text-xs font-black uppercase shadow`}>
                                                                    {getInitials(displayName)}
                                                                </div>
                                                                <div className="text-left">
                                                                    <span className="text-xs text-white font-extrabold block">
                                                                        {displayName} {isYou ? '(You)' : ''}
                                                                    </span>
                                                                    <span className="text-[9px] text-slate-500 font-semibold uppercase tracking-wider">
                                                                        {part.role === 'host' ? 'Host' : 'Participant'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Checked-in status for physical, else connected */}
                                                            {isPhysical ? (
                                                                <span className={`text-[9px] font-black px-2 py-0.5 rounded ${
                                                                    part.check_in_status === 'checked_in'
                                                                        ? 'bg-emerald-500/10 text-emerald-450 border border-emerald-500/10'
                                                                        : 'bg-slate-800 text-slate-400'
                                                                }`}>
                                                                    {part.check_in_status === 'checked_in' ? 'Checked In' : 'Pending'}
                                                                </span>
                                                            ) : (
                                                                <span className="flex h-2 w-2 relative">
                                                                    {isYou || remoteStreams[String(part.user_id)] ? (
                                                                        <>
                                                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-450 opacity-75"></span>
                                                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                                                                        </>
                                                                    ) : (
                                                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-slate-600"></span>
                                                                    )}
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        }

                        const myIdStr = String(user?.user_id || user?.id || '');
                        const rawRemoteParticipants = participants.filter(p => String(p.user_id) !== myIdStr);

                        // Google Meet Priority Sorting:
                        // 1. Raised hands (comes to top of Page 1 immediately)
                        // 2. Video Active
                        // 3. Audio / Default
                        const activeRemoteParticipants = [...rawRemoteParticipants].sort((a, b) => {
                            const aId = String(a.user_id);
                            const bId = String(b.user_id);
                            
                            const aHand = !!raisedHands[aId];
                            const bHand = !!raisedHands[bId];
                            if (aHand && !bHand) return -1;
                            if (!aHand && bHand) return 1;

                            const aStream = remoteStreams[aId];
                            const bStream = remoteStreams[bId];
                            const aCam = (aStream && aStream.getVideoTracks().some(t => t.readyState === 'live')) || a.is_camera_on == 1 || a.is_camera_on === '1';
                            const bCam = (bStream && bStream.getVideoTracks().some(t => t.readyState === 'live')) || b.is_camera_on == 1 || b.is_camera_on === '1';
                            if (aCam && !bCam) return -1;
                            if (!aCam && bCam) return 1;

                            return 0;
                        });

                        const remoteParticipants = activeRemoteParticipants;
                        const remotePresenter = remoteParticipants.find(p => p.is_screen_sharing == 1 || p.is_screen_sharing === '1');
                        const localPresenter = screenShareActive;
                        const isPinnedActive = pinnedUserId !== null;
                        const isPresentationActive = localPresenter || !!remotePresenter || isPinnedActive;
                        
                        let presenterUserId = null;
                        let presenterName = '';
                        let isMainPinned = false;

                        if (localPresenter) {
                            presenterUserId = String(user?.user_id || user?.id);
                            presenterName = localName;
                        } else if (remotePresenter) {
                            presenterUserId = String(remotePresenter.user_id);
                            presenterName = remotePresenter.name?.trim() || remotePresenter.guest_email || remotePresenter.email || 'Participant';
                        } else if (isPinnedActive) {
                            isMainPinned = true;
                            presenterUserId = String(pinnedUserId);
                            if (presenterUserId === String(user?.user_id || user?.id)) {
                                presenterName = localName;
                            } else {
                                const pinnedPart = participants.find(p => String(p.user_id) === presenterUserId);
                                presenterName = pinnedPart ? (pinnedPart.name?.trim() || pinnedPart.guest_email || pinnedPart.email || 'Participant') : 'Participant';
                            }
                        }

                        if (isPresentationActive) {
                            return (
                                <div className="w-full flex-grow flex flex-col lg:flex-row gap-4 overflow-hidden min-h-0">
                                    {/* Main: Large Screen Share / Pinned View - fills all space */}
                                    <div className="flex-grow bg-[#202124] rounded-3xl overflow-hidden border border-slate-850 shadow-2xl relative flex items-center justify-center group transition-all duration-300 min-h-0">
                                        {presenterUserId === String(user?.user_id || user?.id) ? (
                                            (camActive && cameraAvailable) || screenShareActive ? (
                                                <video 
                                                    ref={el => {
                                                        videoRef.current = el;
                                                        if (el) {
                                                            const expectedStream = screenShareActive ? screenStreamRef.current : streamRef.current;
                                                            if (expectedStream && el.srcObject !== expectedStream) {
                                                                el.srcObject = expectedStream;
                                                            }
                                                            el.onloadedmetadata = () => { el.play().catch(e => console.warn(e)); };
                                                            if (el.paused && expectedStream) el.play().catch(e => console.warn(e));
                                                        }
                                                    }}
                                                    autoPlay 
                                                    playsInline 
                                                    muted 
                                                    className={`w-full h-full ${screenShareActive ? 'object-contain' : 'object-cover scale-x-flipped'}`} 
                                                />
                                            ) : (
                                                <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#202124]">
                                                    <div className={`w-16 h-16 rounded-full ${getAvatarColorClass(localName)} flex items-center justify-center text-white text-2xl font-bold shadow-lg uppercase`}>
                                                        {getInitials(localName)}
                                                    </div>
                                                    <span className="text-[10px] text-slate-650 mt-2 font-medium">Camera Disabled</span>
                                                </div>
                                            )
                                        ) : (
                                            <>
                                                {remoteStreams[presenterUserId] && (
                                                    <video
                                                        key={presenterUserId}
                                                        autoPlay
                                                        playsInline
                                                        className={`w-full h-full object-contain ${
                                                            remoteStreams[presenterUserId].getVideoTracks().some(track => track.readyState === 'live') || 
                                                            remotePresenter?.is_screen_sharing == 1 || 
                                                            remotePresenter?.is_screen_sharing === '1' 
                                                                ? 'opacity-100' 
                                                                : 'opacity-0 pointer-events-none absolute inset-0'
                                                        }`}
                                                        ref={el => {
                                                            if (el && remoteStreams[presenterUserId]) {
                                                                if (el.srcObject !== remoteStreams[presenterUserId]) {
                                                                    el.srcObject = remoteStreams[presenterUserId];
                                                                }
                                                                if (el.paused) {
                                                                    el.play().catch(e => console.warn("Failed to play presenter video:", e));
                                                                }
                                                            }
                                                        }}
                                                    />
                                                )}
                                                {(!remotePresenter && (!remoteStreams[presenterUserId] || !remoteStreams[presenterUserId].getVideoTracks().some(track => track.readyState === 'live'))) && (
                                                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#202124] gap-3">
                                                        <div className={`w-16 h-16 rounded-full ${getAvatarColorClass(presenterName)} flex items-center justify-center text-white text-2xl font-bold shadow-lg uppercase`}>
                                                            {getInitials(presenterName)}
                                                        </div>
                                                        <span className="text-[10px] text-slate-500 font-semibold">
                                                            {remoteStreams[presenterUserId] ? 'Camera Disabled' : 'Connecting video…'}
                                                        </span>
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {/* Picture-in-picture presenter face video bubble in right top (local user camera preview) */}
                                        {localPresenter && camActive && cameraAvailable && (
                                            <div className="absolute top-4 right-4 w-32 md:w-40 aspect-video rounded-xl overflow-hidden border border-slate-750/80 shadow-2xl z-10 bg-[#0d0d0f] scale-x-flipped">
                                                <video 
                                                    ref={cameraPreviewRef}
                                                    autoPlay 
                                                    playsInline 
                                                    muted 
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                        )}

                                        {/* Stop presenting button */}
                                        {localPresenter && (
                                            <button 
                                                onClick={handleToggleScreenShare}
                                                className="absolute top-4 left-4 bg-red-650 hover:bg-red-650 text-white text-[10px] font-bold px-3.5 py-2 rounded-full shadow-lg flex items-center gap-1.5 z-10 transition-all border border-red-600/30"
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.8" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                                Stop Presenting
                                            </button>
                                        )}

                                        {/* Unpin button */}
                                        {isMainPinned && (
                                            <button 
                                                onClick={() => setPinnedUserId(null)}
                                                className="absolute top-4 left-4 bg-slate-800 hover:bg-slate-700 text-white text-[10px] font-bold px-3.5 py-2 rounded-full shadow-lg flex items-center gap-1.5 z-10 transition-all border border-slate-700"
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                                Unpin Video
                                            </button>
                                        )}

                                        <div className="absolute bottom-3 left-3 flex gap-2">
                                            <div className="bg-black/60 backdrop-blur px-2.5 py-1 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5 shadow-md">
                                                {isMainPinned && (
                                                    <svg className="w-3 h-3 text-emerald-500" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/>
                                                    </svg>
                                                )}
                                                {presenterName} {isMainPinned ? '(Pinned)' : "'s Screen"} {localPresenter ? '(You)' : ''}
                                            </div>
                                            {(localPresenter || presenterUserId === String(user?.user_id || user?.id)) && handRaised && (
                                                <div className="bg-yellow-500/90 backdrop-blur px-2.5 py-1 rounded-lg text-xs font-bold text-white flex items-center gap-1.5 shadow-md animate-pulse">
                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" /></svg>
                                                    Hand Raised
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Right thumbnail strip: only shown when sidebar is NOT open, to avoid double-panel gap */}
                                    {!sidebarOpen && (
                                        <div className="w-full lg:w-44 flex flex-wrap lg:flex-col gap-3 pb-2 shrink-0 justify-center">
                                            {presenterUserId !== String(user?.user_id || user?.id) && (
                                                <div className="w-[calc(50%-6px)] sm:w-36 lg:w-full aspect-video bg-[#202124] rounded-xl overflow-hidden border border-slate-850 shadow-md relative flex items-center justify-center flex-shrink-0">
                                                    {(camActive && cameraAvailable) || screenShareActive ? (
                                                        <video 
                                                             ref={el => {
                                                                 videoRef.current = el;
                                                                 if (el) {
                                                                     const expectedStream = screenShareActive ? screenStreamRef.current : streamRef.current;
                                                                     if (el.srcObject !== expectedStream) el.srcObject = expectedStream;
                                                                     if (el.paused && expectedStream) el.play().catch(e => console.warn(e));
                                                                 }
                                                             }}
                                                             autoPlay 
                                                             playsInline 
                                                             muted 
                                                             className={`w-full h-full ${screenShareActive ? 'object-contain' : 'object-cover scale-x-flipped'}`} 
                                                         />
                                                    ) : (
                                                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#202124]">
                                                            <div className={`w-10 h-10 rounded-full ${getAvatarColorClass(localName)} flex items-center justify-center text-white text-sm font-bold shadow-md uppercase`}>
                                                                {getInitials(localName)}
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="absolute bottom-1.5 left-1.5 bg-black/60 backdrop-blur px-1.5 py-0.5 rounded text-[10px] font-semibold text-white">
                                                        {localName} (You)
                                                    </div>
                                                    <div className="absolute bottom-1.5 right-1.5 flex gap-1">
                                                        {handRaised && (
                                                            <div className="w-4 h-4 rounded-full bg-yellow-500/90 flex items-center justify-center text-white shadow-md">
                                                                <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" /></svg>
                                                            </div>
                                                        )}
                                                        {!micActive && (
                                                            <div className="w-4 h-4 rounded-full bg-red-650 flex items-center justify-center text-white shadow-md">
                                                                <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.42 2.72 6.36 6 6.82V21h2v-3.18c.9-.13 1.76-.49 2.53-1.02l2.32 2.32 1.27-1.27L4.27 3z"/></svg>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                            {activeRemoteParticipants.map(part => {
                                                const pid = String(part.user_id);
                                                if (pid === presenterUserId) return null;
                                                const stream = remoteStreams[pid];
                                                const displayName = (part.name?.trim()) || part.guest_email || part.email || 'Participant';
                                                const isMuted = part.is_muted == 1 || part.is_muted === '1';
                                                const hasVideoTrack = !!stream?.getVideoTracks().some(track => track.readyState === 'live' && !track.muted);
                                                const isCamOn = hasVideoTrack || part.is_camera_on == 1 || part.is_camera_on === '1';
                                                return (
                                                    <div 
                                                        key={part.id} 
                                                        onClick={() => handlePinForMyself(part.user_id)}
                                                        className="w-[calc(50%-6px)] sm:w-36 lg:w-full aspect-video bg-[#202124] rounded-xl overflow-hidden border border-slate-850 shadow-md relative flex items-center justify-center flex-shrink-0 cursor-pointer hover:border-main transition-colors duration-200"
                                                    >
                                                        {stream && (
                                                            <video 
                                                                key={pid} 
                                                                autoPlay 
                                                                playsInline 
                                                                className={`absolute inset-0 w-full h-full object-cover ${isCamOn ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} 
                                                                ref={el => { 
                                                                    if (el && stream) { 
                                                                        if (el.srcObject !== stream) el.srcObject = stream; 
                                                                        if (el.paused) el.play().catch(e => console.warn("Failed to play thumbnail video:", e)); 
                                                                    } 
                                                                }} 
                                                            />
                                                        )}
                                                        {!isCamOn && (
                                                            <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#202124] gap-1.5">
                                                                <div className={`w-10 h-10 rounded-full ${getAvatarColorClass(displayName)} flex items-center justify-center text-white text-sm font-bold shadow-md uppercase`}>
                                                                    {getInitials(displayName)}
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div className="absolute bottom-1.5 left-1.5 bg-black/60 backdrop-blur px-1.5 py-0.5 rounded text-[10px] font-semibold text-white">
                                                            {displayName}
                                                        </div>
                                                        <div className="absolute bottom-1.5 right-1.5 flex gap-1">
                                                            {raisedHands[pid] && (
                                                                <div className="w-4 h-4 rounded-full bg-yellow-500/90 flex items-center justify-center text-white shadow-md">
                                                            <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" /></svg>
                                                                </div>
                                                            )}
                                                            {isMuted && (
                                                                <div className="w-4 h-4 rounded-full bg-red-600 flex items-center justify-center shadow-md">
                                                                    <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.42 2.72 6.36 6 6.82V21h2v-3.18c.9-.13 1.76-.49 2.53-1.02l2.32 2.32 1.27-1.27L4.27 3z"/></svg>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                             );
                         }
 
                        // Normal non-presenting Grid View
                        const isLocalSpeaking = micActive;
                        const totalPages = Math.ceil(activeRemoteParticipants.length / MAX_PER_PAGE) || 1;
                         
                         return (
                             <div className="w-full flex-grow flex flex-col items-center justify-center min-h-0">
                                 <div className={
                                     totalFeeds === 1 
                                         ? "w-full flex-grow max-w-4xl flex justify-center items-center" 
                                         : totalFeeds === 2 
                                             ? "w-full flex-grow max-w-5xl relative flex justify-center items-center"
                                             : "w-full flex-grow max-w-6xl grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6 place-content-center"
                                 }>
                                     {/* Feed 1: User Feed Card */}
                                     <div className={
                                         `${totalFeeds === 1 
                                             ? "w-full max-w-4xl aspect-video h-[72vh] bg-[#202124] rounded-3xl overflow-hidden shadow-2xl relative flex items-center justify-center group transition-all duration-300"
                                             : totalFeeds === 2
                                                 ? "absolute bottom-6 right-6 w-48 md:w-64 aspect-video bg-[#0d0d0f] rounded-2xl overflow-hidden border-2 shadow-2xl z-20 flex items-center justify-center group transition-all duration-300"
                                                 : "w-full aspect-video bg-[#202124] rounded-2xl overflow-hidden shadow-xl relative flex items-center justify-center group transition-all duration-300"
                                         } ${isLocalSpeaking ? 'border-2 border-blue-500 ring-4 ring-blue-500/70 shadow-[0_0_30px_rgba(59,130,246,0.8)]' : 'border border-slate-850'}`
                                     }>
                                         {camActive && cameraAvailable ? (
                                             <video 
                                                 ref={el => {
                                                     videoRef.current = el;
                                                     if (el) {
                                                         const expectedStream = screenShareActive ? screenStreamRef.current : streamRef.current;
                                                         if (el.srcObject !== expectedStream) el.srcObject = expectedStream;
                                                         if (el.paused && expectedStream) el.play().catch(e => console.warn(e));
                                                     }
                                                 }}
                                                 autoPlay 
                                                 playsInline 
                                                 muted 
                                                 className="w-full h-full object-cover scale-x-flipped" 
                                             />
                                         ) : (
                                             <div className="absolute inset-0 flex items-center justify-center bg-[#202124]">
                                                 <div className={`w-20 h-20 sm:w-28 sm:h-28 rounded-full ${getAvatarColorClass(localName)} flex items-center justify-center text-white text-2xl sm:text-4xl font-black shadow-2xl uppercase border-4 border-white/10 ring-4 ring-white/5`}>
                                                     {getInitials(localName)}
                                                 </div>
                                             </div>
                                         )}
                                     </div>

                                     {/* Feeds 2+: Remote Participants (Paginated) */}
                                     {activeRemoteParticipants.slice(currentPage * MAX_PER_PAGE, (currentPage + 1) * MAX_PER_PAGE).map(part => {
                                         const pid = String(part.user_id);
                                         const stream = remoteStreams[pid];
                                         const displayName = (part.name?.trim()) || part.guest_email || part.email || 'Participant';
                                         const isMuted = part.is_muted == 1 || part.is_muted === '1';
                                         const hasVideoTrack = !!stream?.getVideoTracks().some(track => track.readyState === 'live' && !track.muted);
                                         const isSharingScreen = part.is_screen_sharing == 1 || part.is_screen_sharing === '1';
                                         const isCamOn = hasVideoTrack || part.is_camera_on == 1 || part.is_camera_on === '1' || isSharingScreen;
                                         
                                         const isRemoteSpeaking = !isMuted;

                                         return (
                                             <div 
                                                 key={part.id} 
                                                 onClick={() => handlePinForMyself(part.user_id)}
                                                 className={
                                                     `${totalFeeds === 2
                                                         ? "w-full aspect-video max-h-[72vh] bg-[#202124] rounded-3xl overflow-hidden shadow-2xl relative flex items-center justify-center group transition-all duration-300 cursor-pointer hover:border-main transition-colors duration-250"
                                                         : "w-full aspect-video bg-[#202124] rounded-2xl overflow-hidden shadow-xl relative flex items-center justify-center group transition-all duration-300 cursor-pointer hover:border-main transition-colors duration-250"
                                                     } ${isRemoteSpeaking ? 'border-2 border-blue-500 ring-4 ring-blue-500/70 shadow-[0_0_30px_rgba(59,130,246,0.8)]' : 'border border-slate-850'}`
                                                 }
                                             >
                                                {stream && (
                                                    <video
                                                        key={pid}
                                                        autoPlay
                                                        playsInline
                                                        className={`absolute inset-0 w-full h-full ${isSharingScreen ? 'object-contain' : 'object-cover'} ${isCamOn ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                                                        ref={el => {
                                                            if (el && stream) {
                                                                if (el.srcObject !== stream) el.srcObject = stream;
                                                                if (el.paused) el.play().catch(e => console.warn("Failed to play grid video:", e));
                                                            }
                                                        }}
                                                    />
                                                )}
                                                {!isCamOn && (
                                                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#202124] gap-2">
                                                        <div className={`w-14 h-14 rounded-full ${getAvatarColorClass(displayName)} flex items-center justify-center text-white text-xl font-bold shadow-lg uppercase`}>
                                                            {getInitials(displayName)}
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur px-2.5 py-1 rounded-lg text-xs font-semibold text-white">
                                                    {displayName}
                                                </div>
                                                <div className="absolute bottom-3 right-3 flex gap-1">
                                                    {raisedHands[pid] && (
                                                        <div className="w-6 h-6 rounded-full bg-yellow-500/90 flex items-center justify-center text-white shadow-md">
                                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" /></svg>
                                                        </div>
                                                    )}
                                                    {isMuted && (
                                                        <div className="w-6 h-6 rounded-full bg-red-650 flex items-center justify-center text-white text-xs shadow-md">
                                                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.42 2.72 6.36 6 6.82V21h2v-3.18c.9-.13 1.76-.49 2.53-1.02l2.32 2.32 1.27-1.27L4.27 3z"/></svg>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="absolute top-3 right-3">
                                                    <span className={`w-2 h-2 rounded-full inline-block ${stream ? 'bg-emerald-500 animate-pulse' : 'bg-amber-400 animate-pulse'}`}></span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Google Meet style pagination bar for 50+ members */}
                                {totalPages > 1 && (
                                    <div className="w-full flex items-center justify-center gap-3 mt-4 z-20 shrink-0">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setCurrentPage(prev => Math.max(0, prev - 1)); }}
                                            disabled={currentPage === 0}
                                            className="px-4 py-2 rounded-xl bg-[#202124] hover:bg-[#3c4043] disabled:opacity-30 disabled:cursor-not-allowed border border-slate-700 text-white text-xs font-bold transition-all flex items-center gap-1.5 shadow-lg cursor-pointer"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                                            Previous Page
                                        </button>
                                        <span className="text-xs font-extrabold text-slate-200 px-4 py-2 bg-[#18181b] rounded-xl border border-slate-800 shadow-inner">
                                            Page {currentPage + 1} of {totalPages} ({participants.length} total members)
                                        </span>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setCurrentPage(prev => Math.min(totalPages - 1, prev + 1)); }}
                                            disabled={currentPage >= totalPages - 1}
                                            className="px-4 py-2 rounded-xl bg-[#202124] hover:bg-[#3c4043] disabled:opacity-30 disabled:cursor-not-allowed border border-slate-700 text-white text-xs font-bold transition-all flex items-center gap-1.5 shadow-lg cursor-pointer"
                                        >
                                            Next Page
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* Whiteboard Overlay */}
                    {whiteboardActive && (
                        <div className="absolute inset-0 z-20 bg-[#121214]/95 flex flex-col items-center justify-center p-4 backdrop-blur-md animate-fadeIn">
                            {/* Toolbar Header */}
                            <div className="w-full max-w-5xl bg-slate-900 border border-slate-700/80 rounded-t-2xl p-3 flex flex-wrap items-center justify-between gap-3 shadow-xl">
                                {/* Left: Title & Tools */}
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className="text-white font-black text-sm flex items-center gap-2 mr-2">
                                        <svg className="w-5 h-5 text-main" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/></svg>
                                        Collaborative Whiteboard
                                    </h3>

                                    {/* Tool Selector: Marker, Highlighter, Eraser */}
                                    <div className="flex items-center bg-slate-800 p-1 rounded-xl border border-slate-700 gap-1">
                                        <button
                                            onClick={() => setWbTool('marker')}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-extrabold flex items-center gap-1.5 transition-all ${wbTool === 'marker' ? 'bg-[#c41230] text-white shadow-md' : 'text-slate-300 hover:bg-slate-700 hover:text-white'}`}
                                            title="Pen / Marker"
                                        >
                                            <span>✏️ Marker</span>
                                        </button>

                                        <button
                                            onClick={() => { 
                                                setWbTool('highlighter'); 
                                                if (wbColor === '#000000') setWbColor('#facc15'); 
                                            }}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-extrabold flex items-center gap-1.5 transition-all ${wbTool === 'highlighter' ? 'bg-amber-500 text-white shadow-md' : 'text-slate-300 hover:bg-slate-700 hover:text-white'}`}
                                            title="Highlighter"
                                        >
                                            <span>🖍️ Highlighter</span>
                                        </button>

                                        <button
                                            onClick={() => setWbTool('eraser')}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-extrabold flex items-center gap-1.5 transition-all ${wbTool === 'eraser' ? 'bg-pink-600 text-white shadow-md' : 'text-slate-300 hover:bg-slate-700 hover:text-white'}`}
                                            title="Eraser"
                                        >
                                            <span>🧽 Eraser</span>
                                        </button>
                                    </div>

                                    {/* Color Palette (Active when NOT eraser) */}
                                    {wbTool !== 'eraser' && (
                                        <div className="flex items-center bg-slate-800 p-1.5 rounded-xl border border-slate-700 gap-1.5">
                                            {[
                                                { color: '#000000', label: 'Black' },
                                                { color: '#ef4444', label: 'Red' },
                                                { color: '#3b82f6', label: 'Blue' },
                                                { color: '#10b981', label: 'Green' },
                                                { color: '#facc15', label: 'Yellow' },
                                                { color: '#a855f7', label: 'Purple' },
                                                { color: '#ffffff', label: 'White' }
                                            ].map(item => (
                                                <button
                                                    key={item.color}
                                                    onClick={() => setWbColor(item.color)}
                                                    className={`w-6 h-6 rounded-full border transition-all ${wbColor === item.color ? 'scale-110 border-white ring-2 ring-blue-500 shadow-md' : 'border-slate-600 hover:scale-105'}`}
                                                    style={{ backgroundColor: item.color }}
                                                    title={item.label}
                                                />
                                            ))}
                                            <input
                                                type="color"
                                                value={wbColor}
                                                onChange={(e) => setWbColor(e.target.value)}
                                                className="w-6 h-6 rounded-full border-0 cursor-pointer bg-transparent"
                                                title="Custom Color"
                                            />
                                        </div>
                                    )}

                                    {/* Line Thickness Selector (Whiteboard Circle Dot Style) */}
                                    <div className="flex items-center bg-slate-800 p-1 rounded-xl border border-slate-700 gap-1.5">
                                        <span className="text-[10px] text-slate-400 font-extrabold uppercase px-1.5">Thickness:</span>
                                        {[
                                            { size: 2, label: 'Fine (2px)', dotSize: 'w-1.5 h-1.5' },
                                            { size: 5, label: 'Medium (5px)', dotSize: 'w-2.5 h-2.5' },
                                            { size: 12, label: 'Thick (12px)', dotSize: 'w-3.5 h-3.5' },
                                            { size: 24, label: 'Extra Thick (24px)', dotSize: 'w-4.5 h-4.5' }
                                        ].map(item => (
                                            <button
                                                key={item.size}
                                                onClick={() => setWbSize(item.size)}
                                                title={item.label}
                                                className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all ${
                                                    wbSize === item.size 
                                                        ? 'bg-main text-white ring-2 ring-main/50 shadow-md scale-105' 
                                                        : 'hover:bg-slate-700 text-slate-400'
                                                }`}
                                            >
                                                <span className={`rounded-full bg-current ${item.dotSize}`}></span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                 {/* Right Actions: Clear, Save, Close */}
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => {
                                            allStrokesRef.current = [];
                                            pendingStrokesRef.current = [];
                                            if (canvasRef.current) {
                                                const ctx = canvasRef.current.getContext('2d');
                                                ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                                                ctx.fillStyle = '#ffffff';
                                                ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                                            }
                                            api.post(`/collab-meetings/meeting/${meetingId}/chat`, {
                                                sender_name: user?.first_name || 'User',
                                                message: '[SIGNAL]:WB_CLEAR'
                                            }).catch(() => {});
                                        }}
                                        className="bg-slate-800 text-slate-200 px-3 py-1.5 rounded-xl border border-slate-700 hover:bg-amber-600 hover:text-white transition-all text-xs font-bold flex items-center gap-1.5"
                                        title="Clear Canvas"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                                        Clear
                                    </button>

                                    <button
                                        onClick={() => {
                                            if (!canvasRef.current) return;
                                            const link = document.createElement('a');
                                            link.download = `Whiteboard_${meetingId}_${Date.now()}.png`;
                                            link.href = canvasRef.current.toDataURL('image/png');
                                            link.click();
                                        }}
                                        className="bg-slate-800 text-slate-200 px-3 py-1.5 rounded-xl border border-slate-700 hover:bg-emerald-600 hover:text-white transition-all text-xs font-bold flex items-center gap-1.5"
                                        title="Save Whiteboard Image"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
                                        Save
                                    </button>

                                    <button 
                                        onClick={() => {
                                            setWhiteboardActive(false);
                                            api.post(`/collab-meetings/meeting/${meetingId}/chat`, {
                                                sender_name: user?.first_name || 'User',
                                                message: '[SIGNAL]:WB_CLOSE'
                                            }).catch(() => {});
                                        }}
                                        className="bg-slate-800 text-slate-300 px-3 py-1.5 rounded-xl border border-slate-700 hover:bg-red-500 hover:text-white transition-all text-xs font-bold"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>

                            {/* Canvas Element */}
                            <div className="w-full max-w-5xl bg-white border-2 border-slate-700 rounded-b-2xl overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.5)]">
                                {(() => {
                                    const getCanvasPos = (e) => {
                                        if (!canvasRef.current) return { x: 0, y: 0 };
                                        const rect = canvasRef.current.getBoundingClientRect();
                                        const scaleX = canvasRef.current.width / rect.width;
                                        const scaleY = canvasRef.current.height / rect.height;
                                        const evt = e.touches && e.touches.length > 0 ? e.touches[0] : e;
                                        return {
                                            x: (evt.clientX - rect.left) * scaleX,
                                            y: (evt.clientY - rect.top) * scaleY
                                        };
                                    };

                                    const redrawCanvas = () => {
                                        if (!canvasRef.current) return;
                                        const ctx = canvasRef.current.getContext('2d');
                                        ctx.fillStyle = '#ffffff';
                                        ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);

                                        allStrokesRef.current.forEach(stroke => {
                                            ctx.save();
                                            if (stroke.tool === 'eraser' || stroke.isEraser) {
                                                ctx.globalCompositeOperation = 'destination-out';
                                                ctx.lineWidth = stroke.lineWidth || 20;
                                                ctx.lineCap = 'round';
                                                ctx.lineJoin = 'round';
                                            } else if (stroke.tool === 'highlighter') {
                                                ctx.globalCompositeOperation = 'source-over';
                                                ctx.globalAlpha = stroke.opacity || 0.35;
                                                ctx.strokeStyle = stroke.color || '#facc15';
                                                ctx.lineWidth = stroke.lineWidth || 18;
                                                ctx.lineCap = 'square';
                                                ctx.lineJoin = 'miter';
                                            } else {
                                                ctx.globalCompositeOperation = 'source-over';
                                                ctx.globalAlpha = stroke.opacity || 1.0;
                                                ctx.strokeStyle = stroke.color || '#000000';
                                                ctx.lineWidth = stroke.lineWidth || 4;
                                                ctx.lineCap = 'round';
                                                ctx.lineJoin = 'round';
                                            }
                                            ctx.beginPath();
                                            ctx.moveTo(stroke.x0, stroke.y0);
                                            ctx.lineTo(stroke.x1, stroke.y1);
                                            ctx.stroke();
                                            ctx.closePath();
                                            ctx.restore();
                                        });
                                    };

                                    const drawSegment = (startPos, endPos) => {
                                        if (!canvasRef.current) return;
                                        const ctx = canvasRef.current.getContext('2d');
                                        const tool = wbToolRef.current;
                                        const color = wbColorRef.current;
                                        const size = wbSizeRef.current;

                                        ctx.save();

                                        let strokeObj;
                                        if (tool === 'eraser') {
                                            ctx.globalCompositeOperation = 'destination-out';
                                            ctx.lineWidth = Math.max(size * 3, 20);
                                            ctx.lineCap = 'round';
                                            ctx.lineJoin = 'round';
                                            ctx.beginPath();
                                            ctx.moveTo(startPos.x, startPos.y);
                                            ctx.lineTo(endPos.x, endPos.y);
                                            ctx.stroke();
                                            ctx.closePath();
                                            strokeObj = { x0: startPos.x, y0: startPos.y, x1: endPos.x, y1: endPos.y, isEraser: true, tool: 'eraser', lineWidth: Math.max(size * 3, 20) };
                                        } else if (tool === 'highlighter') {
                                            ctx.globalCompositeOperation = 'source-over';
                                            ctx.globalAlpha = 0.35;
                                            const activeColor = color === '#000000' ? '#facc15' : color;
                                            ctx.strokeStyle = activeColor;
                                            ctx.lineWidth = Math.max(size * 2.5, 18);
                                            ctx.lineCap = 'square';
                                            ctx.lineJoin = 'miter';
                                            ctx.beginPath();
                                            ctx.moveTo(startPos.x, startPos.y);
                                            ctx.lineTo(endPos.x, endPos.y);
                                            ctx.stroke();
                                            ctx.closePath();
                                            strokeObj = { x0: startPos.x, y0: startPos.y, x1: endPos.x, y1: endPos.y, color: activeColor, tool: 'highlighter', opacity: 0.35, lineWidth: Math.max(size * 2.5, 18) };
                                        } else {
                                            // Marker / Pen
                                            ctx.globalCompositeOperation = 'source-over';
                                            ctx.globalAlpha = 1.0;
                                            ctx.strokeStyle = color;
                                            ctx.lineWidth = Math.max(size, 3);
                                            ctx.lineCap = 'round';
                                            ctx.lineJoin = 'round';
                                            ctx.beginPath();
                                            ctx.moveTo(startPos.x, startPos.y);
                                            ctx.lineTo(endPos.x, endPos.y);
                                            ctx.stroke();
                                            ctx.closePath();
                                            strokeObj = { x0: startPos.x, y0: startPos.y, x1: endPos.x, y1: endPos.y, color: color, tool: 'marker', opacity: 1.0, lineWidth: Math.max(size, 3) };
                                        }

                                        allStrokesRef.current.push(strokeObj);
                                        pendingStrokesRef.current.push(strokeObj);
                                        ctx.restore();
                                    };

                                    return (
                                        <canvas
                                            ref={(node) => {
                                                canvasRef.current = node;
                                                if (node) {
                                                    redrawCanvas();
                                                }
                                            }}
                                            width={1000}
                                            height={600}
                                            style={{ touchAction: 'none' }}
                                            className={`w-full h-[60vh] md:h-[70vh] block ${wbTool === 'eraser' ? 'cursor-cell' : 'cursor-crosshair'}`}
                                            onMouseDown={(e) => {
                                                isDrawingRef.current = true;
                                                const pos = getCanvasPos(e);
                                                lastPosRef.current = pos;
                                                drawSegment(pos, pos);
                                            }}
                                            onMouseMove={(e) => {
                                                if (!isDrawingRef.current) return;
                                                const newPos = getCanvasPos(e);
                                                drawSegment(lastPosRef.current, newPos);
                                                lastPosRef.current = newPos;
                                            }}
                                            onMouseUp={() => { isDrawingRef.current = false; }}
                                            onMouseLeave={() => { isDrawingRef.current = false; }}
                                            onTouchStart={(e) => {
                                                isDrawingRef.current = true;
                                                const pos = getCanvasPos(e);
                                                lastPosRef.current = pos;
                                                drawSegment(pos, pos);
                                            }}
                                            onTouchMove={(e) => {
                                                if (!isDrawingRef.current) return;
                                                const newPos = getCanvasPos(e);
                                                drawSegment(lastPosRef.current, newPos);
                                                lastPosRef.current = newPos;
                                            }}
                                            onTouchEnd={() => { isDrawingRef.current = false; }}
                                            onTouchCancel={() => { isDrawingRef.current = false; }}
                                        />
                                    );
                                })()}
                            </div>
                        </div>
                    )}
                </main>

                {/* Right Side Drawer - Details, Chat, Participants & AI Panels */}
                {sidebarOpen && (
                    <>
                    {/* Mobile backdrop - tap outside to close sidebar */}
                    <div
                        className="fixed inset-x-0 top-14 bottom-0 z-20 bg-black/60 backdrop-blur-sm lg:hidden"
                        onClick={() => setSidebarOpen(false)}
                        aria-hidden="true"
                    />
                    <aside className="fixed lg:static top-14 bottom-0 right-0 z-30 lg:z-10 w-full sm:w-80 lg:w-80 bg-[#202124] border-l border-slate-700 flex flex-col justify-between shrink-0 shadow-2xl">
                        <div className="flex border-b border-slate-700 bg-[#1e1f22] p-1.5 overflow-x-auto gap-1 items-center shadow-inner">
                            <button 
                                onClick={() => setActiveTab('ai')}
                                className={`flex-grow py-2 px-2 rounded-lg text-[11px] font-extrabold transition-all whitespace-nowrap ${activeTab === 'ai' ? 'bg-red-600 text-white shadow-md' : 'text-slate-100 hover:text-white hover:bg-slate-700'}`}
                            >
                                AI Pilot
                            </button>
                            <button 
                                onClick={() => { setActiveTab('chat'); setUnreadMessages(0); }}
                                className={`flex-grow py-2 px-2 rounded-lg text-[11px] font-extrabold transition-all whitespace-nowrap relative ${activeTab === 'chat' ? 'bg-red-600 text-white shadow-md' : 'text-slate-100 hover:text-white hover:bg-slate-700'}`}
                            >
                                Chat
                                {unreadMessages > 0 && (
                                    <span className="ml-1 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.2 rounded-full shadow">
                                        {unreadMessages > 9 ? '9+' : unreadMessages}
                                    </span>
                                )}
                            </button>
                            <button 
                                onClick={() => { setActiveTab('polls'); setUnreadPollsQa(0); }}
                                className={`flex-grow py-2 px-2 rounded-lg text-[11px] font-extrabold transition-all whitespace-nowrap relative ${activeTab === 'polls' ? 'bg-amber-500 text-slate-950 shadow-md' : 'text-amber-300 hover:text-white hover:bg-slate-700'}`}
                            >
                                Polls
                                {unreadPollsQa > 0 && (
                                    <span className="ml-1 bg-amber-400 text-slate-950 text-[9px] font-black px-1.5 py-0.2 rounded-full shadow">
                                        {unreadPollsQa > 9 ? '9+' : unreadPollsQa}
                                    </span>
                                )}
                            </button>
                            <button 
                                onClick={() => setActiveTab('participants')}
                                className={`flex-grow py-2 px-2 rounded-lg text-[11px] font-extrabold transition-all whitespace-nowrap ${activeTab === 'participants' ? 'bg-red-600 text-white shadow-md' : 'text-slate-100 hover:text-white hover:bg-slate-700'}`}
                            >
                                Room ({participants.length})
                            </button>
                            <button 
                                onClick={() => setActiveTab('details')}
                                className={`flex-grow py-2 px-2 rounded-lg text-[11px] font-extrabold transition-all whitespace-nowrap ${activeTab === 'details' ? 'bg-red-600 text-white shadow-md' : 'text-slate-100 hover:text-white hover:bg-slate-700'}`}
                            >
                                Info
                            </button>
                            <button 
                                onClick={() => setSidebarOpen(false)}
                                className="px-2.5 py-1.5 text-[11px] font-extrabold text-slate-300 hover:text-white bg-slate-800 hover:bg-red-600 rounded-lg border border-slate-700 hover:border-red-600 transition-all flex items-center gap-1 shrink-0 cursor-pointer"
                                title="Close Side Panel"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                                <span>Close</span>
                            </button>
                        </div>

                        {/* Mobile PiP Video Preview – only visible on mobile (hidden on lg+) */}
                        <div className="lg:hidden border-b border-slate-900/50 bg-[#080809] px-3 py-2 flex items-center gap-3 shrink-0">
                            {/* Local video thumbnail */}
                            <div className="relative rounded-xl overflow-hidden bg-[#111113] border border-slate-800/60 shrink-0"
                                 style={{ width: 96, height: 64 }}>
                                <video
                                    ref={el => {
                                        if (el && streamRef.current && el.srcObject !== streamRef.current) {
                                            el.srcObject = streamRef.current;
                                        }
                                    }}
                                    autoPlay
                                    muted
                                    playsInline
                                    className={`w-full h-full object-cover ${!camActive ? 'opacity-0' : ''}`}
                                />
                                {!camActive && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-[#111113]">
                                        <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                                        </svg>
                                    </div>
                                )}
                                {/* Recording dot */}
                                {recordingActive && (
                                    <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500 animate-pulse"/>
                                )}
                            </div>

                            {/* Status indicators */}
                            <div className="flex-grow min-w-0">
                                <p className="text-[10px] font-extrabold text-white truncate">
                                    {screenShareActive ? 'Screen Sharing' : 'Your Camera'}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                    <span className={`flex items-center gap-1 text-[9px] font-bold ${micActive ? 'text-emerald-400' : 'text-red-400'}`}>
                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                            {micActive
                                                ? <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
                                                : <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.42 2.72 6.36 6 6.82V21h2v-3.18c.9-.13 1.76-.49 2.53-1.02l2.32 2.32 1.27-1.27L4.27 3z"/>
                                            }
                                        </svg>
                                        {micActive ? 'Mic On' : 'Muted'}
                                    </span>
                                    <span className={`flex items-center gap-1 text-[9px] font-bold ${camActive ? 'text-emerald-400' : 'text-red-400'}`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${camActive ? 'bg-emerald-400' : 'bg-red-400'}`}/>
                                        {camActive ? 'Cam On' : 'Cam Off'}
                                    </span>
                                    <span className="text-[9px] text-slate-500 font-semibold">
                                        {participants.length} in room
                                    </span>
                                </div>
                            </div>

                            {/* Tap to expand back */}
                            <button
                                onClick={() => setSidebarOpen(false)}
                                className="shrink-0 p-1.5 rounded-lg bg-slate-800/60 hover:bg-slate-700 transition-colors"
                                title="Back to full meeting view"
                            >
                                <svg className="w-3.5 h-3.5 text-slate-300" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"/>
                                </svg>
                            </button>
                        </div>

                        {/* Drawer Inner Body */}
                        <div className="flex-grow overflow-y-auto p-4 text-left">
                            {activeTab === 'chat' && (
                                <div className="flex flex-col h-full justify-between">
                                    <div className="space-y-4 overflow-y-auto max-h-[50vh]">
                                        {messages.map((msg, i) => (
                                            <div key={i} className={`flex flex-col ${msg.sender === 'You' || msg.sender === localName ? 'items-end' : 'items-start'}`}>
                                                <span className="text-[10px] text-slate-500 mb-0.5">{msg.sender} • {msg.time}</span>
                                                <div className={`p-2.5 rounded-xl text-xs max-w-[85%] ${msg.sender === 'You' || msg.sender === localName ? 'bg-main text-white' : 'bg-[#1a1a1c] text-slate-200'}`}>
                                                    {msg.text}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <form onSubmit={handleSendMessage} className="mt-4 flex gap-2">
                                        <input 
                                            type="text" 
                                            placeholder="Type a message..." 
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            className="flex-grow bg-[#1a1a1c] border border-slate-800 text-xs px-3 py-2 rounded-xl outline-none focus:border-main text-white"
                                        />
                                        <button type="submit" className="bg-main text-white px-3 rounded-xl hover:bg-mainHover transition-colors">
                                            Send
                                        </button>
                                    </form>
                                </div>
                            )}

                            {activeTab === 'ai' && (
                                <div className="space-y-4">
                                    {/* Speech Recognition Language Selector */}
                                    <div className="flex items-center justify-between bg-[#16161a] p-2.5 px-3 rounded-xl border border-slate-800 text-xs shadow-inner">
                                        <span className="text-[11px] font-extrabold text-slate-300 flex items-center gap-1.5">
                                            <svg className="w-3.5 h-3.5 text-main" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 21l5.25-11.25L21 21m-9-3h7.5M3 5.621a24.12 24.12 0 018.823-.422m0 0a24.115 24.115 0 014.48.58m-4.48-.58V3m-8.823.421a24.088 24.088 0 00-3.197 1.857m25.32 0a24.08 24.08 0 01-3.197 1.857m0 0a24.103 24.103 0 01-3.197-1.857" /></svg>
                                            Speech Lang:
                                        </span>
                                        <select
                                            value={speechLang}
                                            onChange={(e) => setSpeechLang(e.target.value)}
                                            className="bg-[#222228] text-white text-[11px] font-extrabold py-1 px-2 rounded-lg border border-slate-700 focus:outline-none focus:border-main cursor-pointer transition-all"
                                        >
                                            <option value="ta-IN">🇮🇳 Tamil (தமிழ்)</option>
                                            <option value="en-IN">🇮🇳 Tanglish / English</option>
                                            <option value="en-US">🇺🇸 English (US)</option>
                                        </select>
                                    </div>

                                    <div className="bg-main/5 border border-main/10 rounded-xl p-3.5 text-xs text-main font-bold">
                                        <h4 className="font-bold flex items-center gap-1.5 mb-1 text-slate-250">
                                            <span className={`w-1.5 h-1.5 rounded-full ${micActive ? 'bg-main animate-ping' : 'bg-slate-500'}`}></span>
                                            AI Pilot Active
                                        </h4>
                                        {micActive ? 'Listening for speech and building live AI minutes.' : 'Mic is muted. Live AI minutes paused.'}
                                    </div>

                                    {/* Action button or loading state */}
                                    {aiProcessing ? (
                                        <div className="bg-slate-900/60 p-4 rounded-xl border border-slate-800 text-center space-y-2">
                                            <span className="inline-block w-5 h-5 border-2 border-main border-t-transparent rounded-full animate-spin"></span>
                                            <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider animate-pulse">Speech recognition generating minutes...</p>
                                        </div>
                                    ) : !aiSummary ? (
                                        <button 
                                            onClick={handleTriggerAiProcessing}
                                            className="w-full bg-main hover:bg-mainHover text-white text-xs font-bold py-2.5 px-4 rounded-xl transition-all shadow-md shadow-main/10 flex items-center justify-center gap-1.5"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 21l8.982-11.795m-9 0L14 3L8.018 14.805z" /></svg>
                                            Generate AI Minutes
                                        </button>
                                    ) : (
                                        <div className="space-y-4">
                                            <div className="bg-[#18181b]/80 border border-slate-800 p-3.5 rounded-xl text-left space-y-2">
                                                <h4 className="text-[10px] font-extrabold uppercase tracking-wider text-main">AI Minutes Summary</h4>
                                                <p className="text-xs text-slate-300 leading-relaxed font-medium">{aiSummary}</p>
                                            </div>

                                            {aiActionItems.length > 0 && (
                                                <div className="bg-[#18181b]/80 border border-slate-800 p-3.5 rounded-xl text-left space-y-2">
                                                    <h4 className="text-[10px] font-extrabold uppercase tracking-wider text-main">Action Items</h4>
                                                    <ul className="space-y-2">
                                                        {aiActionItems.map((item, index) => (
                                                            <li key={index} className="flex items-start gap-2 text-xs text-slate-355">
                                                                <input type="checkbox" defaultChecked className="mt-0.5 rounded border-slate-700 bg-slate-900 text-main focus:ring-main focus:ring-offset-slate-900" />
                                                                <span>{item}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <h4 className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Live Transcript</h4>
                                            <div className="flex items-center gap-1.5">
                                                {transcript.length > 0 && (
                                                    <button
                                                        onClick={handleDownloadAiMinutes}
                                                        title="Download Note / Transcript"
                                                        className="text-main hover:text-white hover:bg-main border border-main/30 rounded-lg p-1 transition-all"
                                                    >
                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="space-y-3.5">
                                            {(() => {
                                                const groups = [];
                                                transcript.forEach(item => {
                                                    const lastGroup = groups[groups.length - 1];
                                                    if (lastGroup && lastGroup.speaker === item.speaker) {
                                                        lastGroup.texts.push({ text: item.text, translatedText: item.translatedText, time: item.time });
                                                    } else {
                                                        groups.push({
                                                            speaker: item.speaker,
                                                            texts: [{ text: item.text, translatedText: item.translatedText, time: item.time }]
                                                        });
                                                    }
                                                });
                                                return groups.length > 0 ? (
                                                    groups.map((group, groupIdx) => (
                                                        <div key={`${group.speaker}-${groupIdx}`} className="border-l-2 border-main/40 pl-3 py-2 bg-[#121214]/45 p-2 rounded-r-lg space-y-1.5 shadow-sm">
                                                            <span className="text-[10px] text-main font-black tracking-wide uppercase block">{group.speaker}</span>
                                                            <div className="space-y-2">
                                                                {group.texts.map((t, idx) => (
                                                                    <div key={idx} className="relative">
                                                                        <p className="text-xs text-slate-350 leading-relaxed mb-0">
                                                                            {transliterateToPhoneticEnglish(t.text)}
                                                                            {t.translatedText && t.translatedText !== t.text && (
                                                                                <span className="block mt-1 text-[11px] text-slate-400 italic border-l border-slate-800 pl-2">
                                                                                    {t.translatedText}
                                                                                </span>
                                                                            )}
                                                                        </p>
                                                                        <span className="text-[8px] text-slate-550 block text-right mt-0.5">{t.time}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="bg-[#121214]/40 border border-slate-900/60 rounded-xl p-3 text-xs text-slate-500">
                                                        Speak with the mic enabled to generate live transcript lines.
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            )}

                    {activeTab === 'polls' && (
                        <div className="space-y-4 h-full flex flex-col">
                            <div className="flex bg-slate-800/50 rounded-lg p-1 mb-2">
                                <button
                                    onClick={() => setActiveQaTab('polls')}
                                    className={`flex-1 text-xs py-1.5 rounded-md font-semibold transition-all ${activeQaTab === 'polls' ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-400 hover:text-slate-300'}`}
                                >
                                    Polls
                                </button>
                                <button
                                    onClick={() => setActiveQaTab('qa')}
                                    className={`flex-1 text-xs py-1.5 rounded-md font-semibold transition-all ${activeQaTab === 'qa' ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-400 hover:text-slate-300'}`}
                                >
                                    Q&A
                                </button>
                            </div>

                            {activeQaTab === 'polls' ? (
                                <div className="flex-1 overflow-y-auto space-y-4 pr-1">
                                    {/* Action Header (Shown ONLY when polls exist and creator form is closed) */}
                                    {Object.keys(polls).length > 0 && !showCreatePollForm && (
                                        <div className="flex items-center justify-between bg-[#141418] p-3 rounded-2xl border border-slate-800 shadow-md">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-black text-white uppercase tracking-wider">Live Polls</span>
                                                <span className="text-[10px] bg-slate-800 text-slate-300 font-extrabold px-2 py-0.5 rounded-full border border-slate-700">
                                                    {Object.keys(polls).length}
                                                </span>
                                            </div>
                                            <button
                                                onClick={() => setShowCreatePollForm(true)}
                                                className="bg-main hover:bg-mainHover text-white text-xs font-extrabold px-3 py-1.5 rounded-xl shadow-md transition-all flex items-center gap-1.5 group"
                                                title="Create another poll"
                                            >
                                                <svg className="w-3.5 h-3.5 text-white group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                                </svg>
                                                <span>+ Create Poll</span>
                                            </button>
                                        </div>
                                    )}

                                    {/* Expanded Poll Creator Form */}
                                    {showCreatePollForm && (
                                        <div className="bg-[#141418] p-4 rounded-2xl border border-slate-700/80 space-y-3.5 shadow-2xl animate-fadeIn">
                                            <div className="flex items-center justify-between border-b border-slate-800 pb-2.5">
                                                <h4 className="text-xs font-black text-white flex items-center gap-2 uppercase tracking-wider">
                                                    <span className="w-5 h-5 rounded-full bg-main text-white flex items-center justify-center text-xs font-black shadow-sm">+</span>
                                                    Create a Poll
                                                </h4>
                                                <button
                                                    onClick={() => setShowCreatePollForm(false)}
                                                    className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors"
                                                    title="Close Creator"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </div>

                                            <div className="space-y-1.5">
                                                <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">Poll Question</label>
                                                <textarea
                                                    rows="2"
                                                    placeholder="Ask a question (e.g. Which topic should we review next?)..."
                                                    className="w-full bg-[#1c1c22] border border-slate-700/70 rounded-xl p-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-main focus:ring-1 focus:ring-main/40 transition-all resize-none font-medium"
                                                    value={newPoll.question}
                                                    onChange={(e) => setNewPoll({...newPoll, question: e.target.value})}
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">Options</label>
                                                {newPoll.options.map((opt, idx) => (
                                                    <div key={idx} className="flex items-center gap-2">
                                                        <div className="relative flex-1">
                                                            <input
                                                                type="text"
                                                                placeholder={`Option ${idx + 1}`}
                                                                className="w-full bg-[#1c1c22] border border-slate-700/70 rounded-xl py-2 px-3 pl-8 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-main focus:ring-1 focus:ring-main/40 transition-all font-medium"
                                                                value={opt}
                                                                onChange={(e) => {
                                                                    const opts = [...newPoll.options];
                                                                    opts[idx] = e.target.value;
                                                                    setNewPoll({...newPoll, options: opts});
                                                                }}
                                                            />
                                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] font-extrabold text-slate-500">{idx + 1}.</span>
                                                        </div>
                                                        {newPoll.options.length > 2 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const opts = newPoll.options.filter((_, i) => i !== idx);
                                                                    setNewPoll({...newPoll, options: opts});
                                                                }}
                                                                className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-800/80 rounded-xl transition-colors shrink-0"
                                                                title="Remove Option"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                                                                </svg>
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="flex items-center justify-between pt-1">
                                                <button
                                                    type="button"
                                                    disabled={newPoll.options.length >= 6}
                                                    onClick={() => {
                                                        if (newPoll.options.length < 6) {
                                                            setNewPoll({...newPoll, options: [...newPoll.options, '']});
                                                        }
                                                    }}
                                                    className="text-[11px] text-main hover:text-mainHover disabled:opacity-40 disabled:cursor-not-allowed font-extrabold flex items-center gap-1 transition-colors"
                                                >
                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
                                                    Add Option ({newPoll.options.length}/6)
                                                </button>

                                                <div className="flex gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setNewPoll({ question: '', options: ['', ''] });
                                                            setShowCreatePollForm(false);
                                                        }}
                                                        className="px-3 py-1.5 text-xs font-bold text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            if (!newPoll.question.trim()) {
                                                                setAlertPopup({
                                                                    show: true,
                                                                    title: 'Missing Question',
                                                                    message: 'Please enter a poll question before launching.'
                                                                });
                                                                return;
                                                            }
                                                            const validOptions = newPoll.options.map(o => o.trim()).filter(Boolean);
                                                            if (validOptions.length < 2) {
                                                                setAlertPopup({
                                                                    show: true,
                                                                    title: 'Incomplete Poll',
                                                                    message: 'Please enter at least 2 non-empty options before launching.'
                                                                });
                                                                return;
                                                            }
                                                            const payload = {
                                                                id: `poll_${Date.now()}`,
                                                                question: newPoll.question.trim(),
                                                                options: validOptions,
                                                                creator: getSpeakerName(),
                                                                created_at: new Date().toISOString(),
                                                                status: 'active',
                                                                votes: {}
                                                            };
                                                            api.post(`/collab-meetings/meeting/${meetingId}/chat`, {
                                                                sender_name: getSpeakerName(),
                                                                message: `[SIGNAL]:POLL_CREATE:${JSON.stringify(payload)}`
                                                            }).catch(() => {});
                                                            
                                                            setPolls(prev => ({
                                                                ...prev,
                                                                [payload.id]: payload
                                                            }));

                                                            setNewPoll({ question: '', options: ['', ''] });
                                                            setShowCreatePollForm(false);

                                                            setAlertPopup({
                                                                show: true,
                                                                title: 'Poll Launched! 🎉',
                                                                message: `"${payload.question}" is now live! All meeting participants can vote.`
                                                            });
                                                        }}
                                                        className="bg-main hover:bg-mainHover text-white text-xs px-4 py-1.5 rounded-xl font-extrabold transition-all shadow-md shadow-main/20 flex items-center gap-1.5"
                                                    >
                                                        <span>Launch Poll</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Active Polls List */}
                                    <div className="space-y-4">
                                        {Object.values(polls).reverse().map((poll) => {
                                            const totalVotes = Object.values(poll.votes || {}).length;
                                            const myVote = (poll.votes || {})[getSpeakerName()];
                                            const isClosed = poll.status === 'closed';
                                            const isCreator = poll.creator === getSpeakerName() || isHost;

                                            return (
                                                <div key={poll.id} className="bg-[#141418] p-4 rounded-2xl border border-slate-800 space-y-3.5 shadow-xl transition-all hover:border-slate-700">
                                                    <div className="flex items-start justify-between gap-2 border-b border-slate-800/80 pb-2.5">
                                                        <div className="space-y-1 pr-2">
                                                            <div className="flex items-center gap-2">
                                                                {isClosed ? (
                                                                    <span className="text-[9px] bg-slate-800 text-slate-400 font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full border border-slate-700">Closed</span>
                                                                ) : (
                                                                    <span className="text-[9px] bg-emerald-500/10 text-emerald-400 font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full border border-emerald-500/20 flex items-center gap-1">
                                                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping"></span>
                                                                        Live Voting
                                                                    </span>
                                                                )}
                                                                {poll.creator && (
                                                                    <span className="text-[10px] text-slate-400 font-medium">by <strong className="text-slate-300">{poll.creator}</strong></span>
                                                                )}
                                                            </div>
                                                            <h4 className="text-xs font-bold text-slate-100 leading-snug">{poll.question}</h4>
                                                        </div>

                                                        {/* Host / Creator Actions */}
                                                        {isCreator && (
                                                            <div className="flex items-center gap-1 shrink-0">
                                                                {!isClosed && (
                                                                    <button
                                                                        onClick={() => {
                                                                            setPolls(prev => ({
                                                                                ...prev,
                                                                                [poll.id]: { ...prev[poll.id], status: 'closed' }
                                                                            }));
                                                                            api.post(`/collab-meetings/meeting/${meetingId}/chat`, {
                                                                                sender_name: getSpeakerName(),
                                                                                message: `[SIGNAL]:POLL_END:${JSON.stringify({ pollId: poll.id })}`
                                                                            }).catch(() => {});
                                                                        }}
                                                                        className="px-2 py-1 text-[10px] bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold rounded-lg transition-colors"
                                                                        title="End Voting"
                                                                    >
                                                                        End Poll
                                                                    </button>
                                                                )}
                                                                <button
                                                                    onClick={() => {
                                                                        setPolls(prev => {
                                                                            const copy = { ...prev };
                                                                            delete copy[poll.id];
                                                                            return copy;
                                                                        });
                                                                        api.post(`/collab-meetings/meeting/${meetingId}/chat`, {
                                                                            sender_name: getSpeakerName(),
                                                                            message: `[SIGNAL]:POLL_DELETE:${JSON.stringify({ pollId: poll.id })}`
                                                                        }).catch(() => {});
                                                                    }}
                                                                    className="p-1 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                                                    title="Delete Poll"
                                                                >
                                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="space-y-2.5">
                                                        {poll.options.map((opt, optIdx) => {
                                                            const optVotes = Object.values(poll.votes || {}).filter(v => v === optIdx).length;
                                                            const percent = totalVotes === 0 ? 0 : Math.round((optVotes / totalVotes) * 100);
                                                            const votersForOpt = Object.entries(poll.votes || {})
                                                                .filter(([_, choiceIdx]) => choiceIdx === optIdx)
                                                                .map(([voterName]) => voterName);
                                                            const isMyChoice = myVote === optIdx;

                                                            return (
                                                                <div key={optIdx} className="space-y-1.5">
                                                                    <button 
                                                                        disabled={isClosed}
                                                                        onClick={() => {
                                                                            if (isClosed) return;
                                                                            setPolls(prev => {
                                                                                const curr = prev[poll.id];
                                                                                if (!curr) return prev;
                                                                                const newVotes = { ...(curr.votes || {}) };
                                                                                newVotes[getSpeakerName()] = optIdx;
                                                                                return { ...prev, [poll.id]: { ...curr, votes: newVotes } };
                                                                            });

                                                                            api.post(`/collab-meetings/meeting/${meetingId}/chat`, {
                                                                                sender_name: getSpeakerName(),
                                                                                message: `[SIGNAL]:POLL_VOTE:${JSON.stringify({pollId: poll.id, optIdx, user: getSpeakerName()})}`
                                                                            });
                                                                        }}
                                                                        className={`w-full relative rounded-xl overflow-hidden border transition-all text-left group ${
                                                                            isMyChoice 
                                                                                ? 'border-main bg-main/10 shadow-sm shadow-main/10' 
                                                                                : 'bg-[#1a1a20] border-slate-800 hover:border-slate-600'
                                                                        } ${isClosed ? 'cursor-default opacity-90' : 'cursor-pointer'}`}
                                                                    >
                                                                        <div 
                                                                            className={`absolute top-0 left-0 bottom-0 transition-all duration-500 ${isMyChoice ? 'bg-main/30' : 'bg-slate-700/30'}`} 
                                                                            style={{width: `${percent}%`}}
                                                                        ></div>
                                                                        <div className="relative z-10 flex justify-between items-center p-2.5 text-xs">
                                                                            <div className="flex items-center gap-2.5 text-slate-200 font-medium pr-2">
                                                                                {isMyChoice ? (
                                                                                    <div className="w-4 h-4 rounded-full bg-main flex items-center justify-center shrink-0">
                                                                                        <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                                                                                    </div>
                                                                                ) : (
                                                                                    <div className="w-4 h-4 border-2 border-slate-600 rounded-full shrink-0 group-hover:border-slate-400 transition-colors"></div>
                                                                                )}
                                                                                <span className={isMyChoice ? 'font-bold text-white' : ''}>{opt}</span>
                                                                            </div>
                                                                            <div className="text-right shrink-0">
                                                                                <span className={`text-[11px] font-black ${isMyChoice ? 'text-main' : 'text-slate-400'}`}>{percent}%</span>
                                                                                <span className="text-[10px] text-slate-500 font-semibold ml-1.5">({optVotes})</span>
                                                                            </div>
                                                                        </div>
                                                                    </button>

                                                                    {/* Voter badges */}
                                                                    {votersForOpt.length > 0 && (
                                                                        <div className="flex flex-wrap items-center gap-1.5 pl-2">
                                                                            <span className="text-[9px] text-slate-500 font-bold uppercase tracking-wider">Voted:</span>
                                                                            {votersForOpt.map((voter, vIdx) => (
                                                                                <span key={vIdx} className="bg-amber-500/10 border border-amber-500/20 text-amber-300 font-bold text-[9px] px-2 py-0.5 rounded-full flex items-center gap-1">
                                                                                    <span className="w-1 h-1 rounded-full bg-amber-400"></span>
                                                                                    {voter}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>

                                                    <div className="flex items-center justify-between pt-1 border-t border-slate-800/60 text-[10px] text-slate-400">
                                                        <span>{isClosed ? 'Poll concluded' : 'Click option to cast/change vote'}</span>
                                                        <span className="font-bold text-slate-300">{totalVotes} {totalVotes === 1 ? 'vote' : 'votes'} total</span>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {Object.keys(polls).length === 0 && !showCreatePollForm && (
                                            <div className="text-center py-10 px-4 space-y-3 bg-[#121214]/60 border border-slate-800/80 rounded-2xl">
                                                <div className="w-12 h-12 bg-main/10 text-main rounded-2xl flex items-center justify-center mx-auto shadow-inner">
                                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
                                                    </svg>
                                                </div>
                                                <div className="space-y-1">
                                                    <h4 className="text-xs font-black text-white">No Active Polls</h4>
                                                    <p className="text-[11px] text-slate-400 max-w-[200px] mx-auto leading-relaxed">
                                                        Launch a quick poll to collect real-time votes and decisions from attendees.
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={() => setShowCreatePollForm(true)}
                                                    className="px-4 py-2 bg-main hover:bg-mainHover text-white text-xs font-bold rounded-xl shadow-md shadow-main/20 transition-all inline-flex items-center gap-1.5"
                                                >
                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                                    </svg>
                                                    Create First Poll
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex-1 flex flex-col overflow-hidden">
                                    <div className="flex-1 overflow-y-auto space-y-3 pb-2">
                                        {qaList.map((qa, qIdx) => (
                                            <div key={qa.id || qIdx} className="bg-[#121214]/60 p-2.5 rounded-xl border border-slate-900/40 flex gap-3">
                                                <div className="flex flex-col items-center gap-1 text-slate-500">
                                                    <button className="hover:text-main transition-colors"><svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7"/></svg></button>
                                                    <span className="text-[10px] font-bold">{qa.upvotes || 0}</span>
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-1.5 mb-1">
                                                        <span className="text-[10px] font-bold text-main">{qa.user}</span>
                                                        <span className="text-[9px] text-slate-500">{qa.time}</span>
                                                    </div>
                                                    <p className="text-xs text-slate-300 leading-relaxed">{qa.text}</p>
                                                </div>
                                            </div>
                                        ))}
                                        {qaList.length === 0 && (
                                            <div className="text-center text-xs text-slate-500 py-4">No questions asked yet.</div>
                                        )}
                                    </div>
                                    <form 
                                        className="mt-auto flex gap-2"
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            if (newQaText.trim()) {
                                                const payload = {
                                                    id: `qa_${Date.now()}`,
                                                    user: getSpeakerName(),
                                                    text: newQaText.trim(),
                                                    time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                                                    upvotes: 0
                                                };
                                                api.post(`/collab-meetings/meeting/${meetingId}/chat`, {
                                                    sender_name: getSpeakerName(),
                                                    message: `[SIGNAL]:QA_NEW:${JSON.stringify(payload)}`
                                                });
                                                setNewQaText('');
                                            }
                                        }}
                                    >
                                        <input 
                                            type="text" 
                                            placeholder="Ask a question..."
                                            className="flex-grow bg-[#121214]/80 border border-slate-800 rounded-xl py-2.5 px-3 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-main/50"
                                            value={newQaText}
                                            onChange={(e) => setNewQaText(e.target.value)}
                                        />
                                        <button type="submit" className="bg-main text-white px-3 text-xs rounded-xl hover:bg-mainHover transition-colors shrink-0">
                                            Send
                                        </button>
                                    </form>
                                </div>
                            )}
                        </div>
                    )}

                            {activeTab === 'participants' && (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-[11px] font-extrabold uppercase tracking-widest text-slate-300">Active Participants</h3>
                                        {isHost && (
                                            <div className="flex items-center gap-1.5">
                                                <button 
                                                    onClick={handleMuteAllParticipants}
                                                    className="text-[10px] bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-500/30 px-2 py-1 rounded transition-all flex items-center gap-1 font-bold"
                                                    title="Mute all participants"
                                                >
                                                    Mute All
                                                </button>
                                                <button 
                                                    onClick={handleDownloadAttendance}
                                                    className="text-[10px] bg-emerald-600 hover:bg-emerald-500 text-white px-2.5 py-1 rounded transition-all flex items-center gap-1 font-bold shadow-md hover:shadow-emerald-500/20"
                                                >
                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"/></svg>
                                                    CSV
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <div className="space-y-2.5 overflow-y-auto max-h-[50vh]">
                                        {participants.map((p) => (
                                            <div key={p.id} className="flex items-center justify-between bg-[#121214]/60 p-2.5 rounded-xl border border-slate-900/40">
                                                <div className="flex items-center gap-2.5">
                                                    {p.avatar ? (
                                                        <img src={p.avatar} className="w-7 h-7 rounded-full object-cover" alt={p.name || p.email} />
                                                    ) : (
                                                        <div className={`w-7 h-7 rounded-full ${getAvatarColorClass(p.name || p.email)} flex items-center justify-center text-white text-xs font-bold uppercase`}>
                                                            {getInitials(p.name || p.email)}
                                                        </div>
                                                    )}
                                                    <div>
                                                        <span className="text-xs font-semibold text-slate-250 block truncate max-w-[90px]">
                                                            {p.name || p.guest_email || p.email || 'Participant'}
                                                        </span>
                                                        <span className="text-[9px] text-slate-500 capitalize block">{p.role || 'Attendee'}</span>
                                                    </div>
                                                </div>
                                                
                                                <div className="flex items-center gap-1.5">
                                                    {/* Host Action: Mute Individual */}
                                                    {isHost && String(p.user_id) !== String(user?.user_id || user?.id) && (
                                                        <>
                                                            <button
                                                                onClick={() => {
                                                                    api.post(`/collab-meetings/meeting/${meetingId}/chat`, { message: `[SIGNAL]:MUTE_USER:${p.user_id}` }).catch(() => {});
                                                                }}
                                                                className="p-1.5 rounded transition-colors text-slate-400 hover:text-red-400 hover:bg-slate-800"
                                                                title="Mute participant"
                                                            >
                                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                                                    <line x1="2" y1="2" x2="22" y2="22" strokeLinecap="round" strokeWidth="2" />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    if (window.confirm(`Are you sure you want to remove ${p.name || p.email || 'this participant'} from the meeting?`)) {
                                                                        api.post(`/collab-meetings/meeting/${meetingId}/chat`, { message: `[SIGNAL]:KICK_USER:${p.user_id}` }).catch(() => {});
                                                                    }
                                                                }}
                                                                className="p-1.5 rounded transition-colors text-slate-400 hover:text-red-500 hover:bg-red-500/10"
                                                                title="Remove from meeting"
                                                            >
                                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6" />
                                                                </svg>
                                                            </button>
                                                        </>
                                                    )}
                                                    
                                                    {/* Pin Button */}
                                                    <button
                                                        onClick={() => setPinnedUserId(pinnedUserId === p.user_id ? null : p.user_id)}
                                                        className={`p-1 rounded transition-colors ${pinnedUserId === p.user_id ? 'text-emerald-500 bg-emerald-500/10' : 'text-slate-500 hover:text-white hover:bg-slate-800'}`}
                                                        title={pinnedUserId === p.user_id ? 'Unpin participant' : 'Pin participant'}
                                                    >
                                                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                                            <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/>
                                                        </svg>
                                                    </button>

                                                    {/* Mute Button (Host can toggle, others view static) */}
                                                    {isHost ? (
                                                        <button
                                                            onClick={() => handleMuteParticipant(p.user_id, !(p.is_muted == 1 || p.is_muted === '1'))}
                                                            className={`p-1 rounded transition-colors ${(p.is_muted == 1 || p.is_muted === '1') ? 'text-red-500 bg-red-500/10' : 'text-slate-500 hover:text-white hover:bg-slate-800'}`}
                                                            title={(p.is_muted == 1 || p.is_muted === '1') ? 'Unmute participant' : 'Mute participant'}
                                                        >
                                                            {(p.is_muted == 1 || p.is_muted === '1') ? (
                                                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                                                    <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.42 2.72 6.36 6 6.82V21h2v-3.18c.9-.13 1.76-.49 2.53-1.02l2.32 2.32 1.27-1.27L4.27 3z"/>
                                                                </svg>
                                                            ) : (
                                                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                                                    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
                                                                </svg>
                                                            )}
                                                        </button>
                                                    ) : (
                                                        <span className={`p-1 rounded ${(p.is_muted == 1 || p.is_muted === '1') ? 'text-red-500' : 'text-slate-500'}`} title={(p.is_muted == 1 || p.is_muted === '1') ? 'Muted' : 'Microphone active'}>
                                                            {(p.is_muted == 1 || p.is_muted === '1') ? (
                                                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                                                    <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.42 2.72 6.36 6 6.82V21h2v-3.18c.9-.13 1.76-.49 2.53-1.02l2.32 2.32 1.27-1.27L4.27 3z"/>
                                                                </svg>
                                                            ) : (
                                                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                                                    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
                                                                </svg>
                                                            )}
                                                        </span>
                                                    )}

                                                    {/* Remove Button (Host only) */}
                                                    {isHost && String(p.user_id) !== String(user?.user_id || user?.id) && (
                                                        <button
                                                            onClick={() => handleRemoveParticipant(p.user_id)}
                                                            className="p-1 rounded transition-colors text-slate-500 hover:text-white hover:bg-red-600"
                                                            title="Remove participant"
                                                        >
                                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                                                            </svg>
                                                        </button>
                                                    )}

                                                    <span className="text-[10px] text-slate-500 font-medium shrink-0">
                                                        {String(p.user_id) === String(user?.user_id || user?.id) ? '(You)' : ''}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {activeTab === 'details' && (
                                <div className="space-y-4">
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Meeting Details</h3>
                                    <div className="bg-[#121214]/60 p-4 rounded-xl border border-slate-900/40 space-y-3">
                                        <div>
                                            <span className="text-[9px] text-slate-500 block uppercase font-semibold">Title</span>
                                            <span className="text-xs font-bold text-slate-200">{meetingTitle || "Collaboration Session"}</span>
                                        </div>
                                        <div>
                                            <span className="text-[9px] text-slate-500 block uppercase font-semibold">Room ID</span>
                                            <span className="text-xs text-slate-350">{meetingId}</span>
                                        </div>
                                        <div>
                                            <span className="text-[9px] text-slate-500 block uppercase font-semibold">Join Link</span>
                                            <div className="flex gap-1.5 mt-1 bg-[#1a1a1c] p-2 rounded-lg border border-slate-800/80">
                                                <input 
                                                    type="text" 
                                                    readOnly 
                                                    value={`${window.location.origin}/meetings?room=${meetingId}`} 
                                                    className="bg-transparent text-[10px] text-slate-400 select-all outline-none flex-grow"
                                                />
                                                <button 
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(`${window.location.origin}/meetings?room=${meetingId}`);
                                                        setAlertPopup({
                                                            show: true,
                                                            title: 'Link Copied',
                                                            message: 'Meeting room join link copied to clipboard successfully!'
                                                        });
                                                    }}
                                                    className="text-[9px] bg-main text-white px-2 py-1 rounded hover:bg-mainHover transition-colors"
                                                >
                                                    Copy
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </aside>
                    </>
                )}
            </div>

            {/* Bottom toolbar styled like Google Meet */}
            <footer className="bg-[#202124] px-2 md:px-4 py-3 md:py-4 flex flex-wrap lg:flex-nowrap items-center justify-center lg:justify-between z-10 w-full min-h-[80px] gap-y-3 gap-x-2">
                
                {/* Left group: Meeting Info (Time & Code) */}
                <div className="hidden lg:flex items-center gap-3 w-1/4 min-w-[150px] text-white">
                    <span className="text-sm font-medium tracking-wide truncate max-w-[150px] lg:max-w-[200px]">
                        {meetingDetails?.title || meetingTitle || 'Meeting'}
                    </span>
                    <div className="hidden lg:block w-px h-4 bg-slate-600"></div>
                    <span className="hidden lg:block text-sm font-medium text-slate-300">
                        {String(meetingId || '').substring(0,10) || 'Live'}
                    </span>
                </div>

                {/* Center controls */}
                {meetingDetails?.meeting_type !== 'physical' && (
                    <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 w-full lg:w-auto">
                        {/* Mic toggler */}
                        <div className="shrink-0">
                            <button 
                                onClick={() => setMicActive(!micActive)}
                                className={`flex items-center justify-center w-[44px] h-[44px] rounded-full transition-all ${micActive ? 'bg-[#3c4043] hover:bg-[#4a4e51] text-white' : 'bg-[#ea4335] hover:bg-[#d93025] text-white shadow-md'}`}
                                title={micActive ? 'Turn off microphone' : 'Turn on microphone'}
                            >
                                {micActive ? (
                                    <svg className="w-[22px] h-[22px]" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"/></svg>
                                ) : (
                                    <svg className="w-[22px] h-[22px]" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3zM3 3l18 18"/></svg>
                                )}
                            </button>
                        </div>

                        {/* Camera toggler */}
                        {meetingDetails?.meeting_type === 'online' && (
                            <div className="shrink-0">
                                <button 
                                    onClick={() => setCamActive(!camActive)}
                                    className={`flex items-center justify-center w-[44px] h-[44px] rounded-full transition-all ${camActive ? 'bg-[#3c4043] hover:bg-[#4a4e51] text-white' : 'bg-[#ea4335] hover:bg-[#d93025] text-white shadow-md'}`}
                                    title={camActive ? 'Turn off camera' : 'Turn on camera'}
                                >
                                    {camActive ? (
                                        <svg className="w-[22px] h-[22px]" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
                                    ) : (
                                        <svg className="w-[22px] h-[22px]" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z M3 3l18 18"/></svg>
                                    )}
                                </button>
                            </div>
                        )}

                        {/* Raise Hand */}
                        <div className="shrink-0">
                            <button 
                                onClick={handleToggleHandRaise}
                                className={`flex items-center justify-center w-[44px] h-[44px] rounded-full transition-all ${handRaised ? 'bg-yellow-500/20 text-yellow-500' : 'bg-[#3c4043] hover:bg-[#4a4e51] text-white'}`}
                                title={handRaised ? "Lower hand" : "Raise hand"}
                            >
                                <svg className="w-[22px] h-[22px]" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" /></svg>
                            </button>
                        </div>

                        {/* Share Screen (Online only) */}
                        {meetingDetails?.meeting_type === 'online' && (
                            <div className="shrink-0">
                                <button 
                                    onClick={handleToggleScreenShare}
                                    className={`flex items-center justify-center w-[44px] h-[44px] rounded-full transition-all ${screenShareActive ? 'bg-blue-300/20 text-blue-300' : 'bg-[#3c4043] hover:bg-[#4a4e51] text-white'}`}
                                    title="Present now"
                                >
                                    <svg className="w-[22px] h-[22px]" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4-4m0 0L8 12m4-4v12" /></svg>
                                </button>
                            </div>
                        )}

                        {/* Whiteboard Button */}
                        <div className="relative group shrink-0">
                            <button
                                onClick={() => {
                                    const nextState = !whiteboardActive;
                                    setWhiteboardActive(nextState);
                                    api.post(`/collab-meetings/meeting/${meetingId}/chat`, {
                                        sender_name: user?.first_name || 'User',
                                        message: nextState ? '[SIGNAL]:WB_OPEN' : '[SIGNAL]:WB_CLOSE'
                                    }).catch(() => {});
                                }}
                                className={`flex items-center justify-center w-[44px] h-[44px] rounded-full transition-all ${whiteboardActive ? 'bg-blue-500 text-white shadow-[0_0_15px_rgba(59,130,246,0.5)]' : 'bg-[#3c4043] hover:bg-[#4a4e51] text-white'}`}
                                title="Collaborative Whiteboard"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/></svg>
                            </button>
                        </div>

                        {/* Polls & Q&A Button */}
                        <div className="relative group shrink-0">
                            <button
                                onClick={() => { setSidebarOpen(true); setActiveTab('polls'); setUnreadPollsQa(0); }}
                                className={`flex items-center justify-center w-[44px] h-[44px] rounded-full transition-all relative ${activeTab === 'polls' && sidebarOpen ? 'bg-blue-300/20 text-blue-300' : 'bg-[#3c4043] hover:bg-[#4a4e51] text-white'}`}
                                title="Polls & Q&A"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 13h2.625M3 18h2.625M3 8h2.625m2.25-5.25v2.25m0 7.5v2.25m0 7.5v2.25M11.25 10.5h9.75a2.25 2.25 0 002.25-2.25v-1.5a2.25 2.25 0 00-2.25-2.25h-9.75a2.25 2.25 0 00-2.25 2.25v1.5a2.25 2.25 0 002.25 2.25zm0 10.5h9.75a2.25 2.25 0 002.25-2.25v-1.5a2.25 2.25 0 00-2.25-2.25h-9.75a2.25 2.25 0 00-2.25 2.25v1.5a2.25 2.25 0 002.25 2.25z" />
                                </svg>
                                {unreadPollsQa > 0 && (
                                    <span className="absolute -top-0.5 -right-0.5 bg-red-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-lg flex items-center justify-center min-w-[16px] animate-pulse">
                                        {unreadPollsQa > 9 ? '9+' : unreadPollsQa}
                                    </span>
                                )}
                            </button>
                        </div>

                        {/* More Options Button */}
                        <div className="relative shrink-0">
                            <button 
                                onClick={() => setShowMoreMenu(!showMoreMenu)}
                                className={`flex items-center justify-center w-[44px] h-[44px] rounded-full transition-all ${showMoreMenu ? 'bg-[#4a4e51] text-white' : 'bg-[#3c4043] hover:bg-[#4a4e51] text-white'}`}
                                title="More options"
                            >
                                <svg className="w-[22px] h-[22px]" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"/></svg>
                            </button>
                            {showMoreMenu && (
                                <div className="absolute bottom-16 right-0 w-52 bg-[#121214] border border-slate-800/80 rounded-xl shadow-2xl py-1 z-20 text-xs text-left">
                                    <button 
                                        onClick={() => {
                                            setShowMoreMenu(false);
                                            if (recordingActive) {
                                                handleStopRecording();
                                            } else {
                                                handleStartRecording();
                                            }
                                        }}
                                        className="w-full px-4 py-2 hover:bg-slate-800 text-slate-300 font-semibold flex items-center gap-2"
                                    >
                                        <span className={`w-2.5 h-2.5 rounded-full ${recordingActive ? 'bg-red-500 animate-ping' : 'bg-slate-400'}`}></span>
                                        {recordingActive ? 'Stop & Download Recording' : 'Start Video Recording'}
                                    </button>
                                    <button 
                                        onClick={() => { setSidebarOpen(true); setActiveTab('details'); setShowMoreMenu(false); }}
                                        className="w-full px-4 py-2 hover:bg-slate-800 text-slate-300 font-semibold border-t border-slate-900/60"
                                    >
                                        Meeting Information
                                    </button>
                                    <button 
                                        onClick={() => {
                                            const peerCount = Object.keys(peerConnectionsRef.current || {}).length;
                                            const diagnosticMsg = [
                                                `• Network Status: ${navigator.onLine ? 'Online (🟢)' : 'Offline (🔴)'}`,
                                                `• Microphone: ${micActive ? 'Active (🟢)' : 'Muted / Disabled (⚪)'}`,
                                                `• Camera: ${camActive ? 'Active (🟢)' : 'Disabled / Off (⚪)'}`,
                                                `• Screen Share: ${screenShareActive ? 'Presenting (🟢)' : 'Inactive (⚪)'}`,
                                                `• Connected Peers: ${peerCount} active peer connection(s)`
                                            ].join('\n');
                                            setAlertPopup({
                                                show: true,
                                                title: 'System Diagnostics & Help',
                                                message: diagnosticMsg
                                            });
                                            setShowMoreMenu(false);
                                        }}
                                        className="w-full px-4 py-2 hover:bg-slate-800 text-slate-300 font-semibold border-t border-slate-900/60"
                                    >
                                        Troubleshoot & Help
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* End Call (Red Pill) */}
                        <button 
                            onClick={handleEndMeeting}
                            className="flex items-center justify-center px-6 h-[44px] rounded-full transition-all bg-[#ea4335] hover:bg-[#d93025] text-white shadow-md ml-1 sm:ml-3"
                            title={isHost ? 'End Meeting' : 'Leave Meeting'}
                        >
                            <svg className="w-[24px] h-[24px]" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/>
                            </svg>
                        </button>
                    </div>
                )}

                {/* Right group: Secondary Controls (Details, Chat, Participants, AI, Record, Host) */}
                <div className="flex flex-wrap items-center justify-center lg:justify-end gap-2 w-full lg:w-1/4 lg:min-w-[200px] text-white">
                    {/* Meeting Details (Info Icon) */}
                    <button 
                        onClick={() => {
                            if (sidebarOpen && activeTab === 'details') setSidebarOpen(false);
                            else { setSidebarOpen(true); setActiveTab('details'); }
                        }} 
                        className={`p-2.5 rounded-full transition-colors shrink-0 ${activeTab === 'details' && sidebarOpen ? 'text-blue-300 bg-blue-300/10' : 'text-white hover:bg-slate-700/50'}`}
                        title="Meeting details"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    </button>
                    
                    {/* Participants (People Icon) */}
                    <button 
                        onClick={() => {
                            if (sidebarOpen && activeTab === 'participants') setSidebarOpen(false);
                            else { setSidebarOpen(true); setActiveTab('participants'); }
                        }} 
                        className={`p-2.5 rounded-full transition-colors shrink-0 ${activeTab === 'participants' && sidebarOpen ? 'text-blue-300 bg-blue-300/10' : 'text-white hover:bg-slate-700/50'}`}
                        title="People"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                    </button>

                    {/* Chat (Bubble Icon) */}
                    <button 
                        onClick={() => {
                            if (sidebarOpen && activeTab === 'chat') setSidebarOpen(false);
                            else { setSidebarOpen(true); setActiveTab('chat'); setUnreadMessages(0); }
                        }} 
                        className={`p-2.5 rounded-full transition-colors relative shrink-0 ${activeTab === 'chat' && sidebarOpen ? 'text-blue-300 bg-blue-300/10' : 'text-white hover:bg-slate-700/50'}`}
                        title="Chat with everyone"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                        {unreadMessages > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 bg-red-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-lg flex items-center justify-center min-w-[16px] animate-pulse">
                                {unreadMessages > 9 ? '9+' : unreadMessages}
                            </span>
                        )}
                    </button>
                    
                    {/* Polls & Q&A */}
                    <button 
                        onClick={() => {
                            if (sidebarOpen && activeTab === 'polls') setSidebarOpen(false);
                            else { setSidebarOpen(true); setActiveTab('polls'); setUnreadPollsQa(0); }
                        }} 
                        className={`p-2.5 rounded-full transition-colors relative shrink-0 ${activeTab === 'polls' && sidebarOpen ? 'text-amber-300 bg-amber-300/10' : 'text-slate-200 hover:bg-slate-700/50'}`}
                        title="Polls & Q&A"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                        {unreadPollsQa > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 bg-amber-500 text-slate-950 font-black text-[9px] px-1.5 py-0.5 rounded-full shadow-lg flex items-center justify-center min-w-[16px] animate-pulse">
                                {unreadPollsQa > 9 ? '9+' : unreadPollsQa}
                            </span>
                        )}
                    </button>

                    {/* AI / Activities */}
                    <button 
                        onClick={() => {
                            if (sidebarOpen && activeTab === 'ai') setSidebarOpen(false);
                            else { setSidebarOpen(true); setActiveTab('ai'); }
                        }} 
                        className={`p-2.5 rounded-full transition-colors relative shrink-0 ${activeTab === 'ai' && sidebarOpen ? 'text-blue-300 bg-blue-300/10' : 'text-fuchsia-400 hover:bg-fuchsia-400/10'}`}
                        title="AI Actions"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                    </button>

                    {/* Extra Right Controls */}
                    {meetingDetails?.meeting_type === 'online' && (
                        <>
                            <button 
                                onClick={handleToggleRecording}
                                className={`flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded border transition-all ml-2 ${recordingActive ? 'bg-red-500/10 border-red-500 text-red-500 animate-pulse shadow-[0_0_8px_rgba(239,68,68,0.4)]' : 'bg-transparent border-slate-600 text-slate-300 hover:bg-slate-700/50 hover:text-white'}`}
                                title="Record Meeting"
                            >
                                <span className={`w-2 h-2 rounded-full ${recordingActive ? 'bg-red-500' : 'bg-slate-400'}`}></span>
                                {recordingActive ? (
                                    <span>REC {Math.floor(recordingTimer / 60).toString().padStart(2, '0')}:{(recordingTimer % 60).toString().padStart(2, '0')}</span>
                                ) : (
                                    <span>Record</span>
                                )}
                            </button>
                            {isHost && (
                                <div className="relative">
                                    <button 
                                        onClick={() => setShowHostMenu(!showHostMenu)}
                                        className={`p-2.5 rounded-full transition-colors ml-1 shrink-0 ${showHostMenu ? 'bg-slate-700/80 text-blue-300' : 'text-white hover:bg-slate-700/50'}`}
                                        title="Host controls"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                                    </button>
                                    
                                    {showHostMenu && (
                                        <div className="absolute bottom-12 right-0 w-56 bg-[#121214] border border-slate-800/80 rounded-xl shadow-2xl py-2 z-30 text-xs text-left">
                                            <div className="px-4 py-2 border-b border-slate-800/80 mb-1">
                                                <h4 className="font-bold text-slate-300 uppercase tracking-wider text-[10px]">Host Actions</h4>
                                            </div>
                                            
                                            <button 
                                                onClick={() => {
                                                    api.post(`/collab-meetings/meeting/${meetingId}/chat`, { message: '[SIGNAL]:MUTE_ALL' }).catch(() => {});
                                                    setAlertPopup({ show: true, title: 'Participants Muted', message: 'All participants have been forcefully muted.' });
                                                    setShowHostMenu(false);
                                                }}
                                                className="w-full px-4 py-2.5 hover:bg-slate-800 flex items-center justify-between text-slate-300 font-semibold"
                                            >
                                                <span>Mute All Participants</span>
                                                <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /><line x1="1" y1="1" x2="23" y2="23" strokeWidth="2" /></svg>
                                            </button>
                                            
                                            <button 
                                                onClick={() => {
                                                    setMeetingLocked(!meetingLocked);
                                                    setAlertPopup({ show: true, title: meetingLocked ? 'Meeting Unlocked' : 'Meeting Locked', message: meetingLocked ? 'New participants can now join.' : 'No one else can join this meeting.' });
                                                    setShowHostMenu(false);
                                                }}
                                                className="w-full px-4 py-2.5 hover:bg-slate-800 flex items-center justify-between text-slate-300 font-semibold"
                                            >
                                                <span>{meetingLocked ? 'Unlock Meeting' : 'Lock Meeting'}</span>
                                                {meetingLocked ? (
                                                    <svg className="w-4 h-4 text-emerald-450" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" /></svg>
                                                ) : (
                                                    <svg className="w-4 h-4 text-red-450" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </footer>

            {/* Custom Alert Popup (Dark themed to match Live Room style) */}
            {alertPopup.show && (
                <div className="fixed inset-0 bg-[#000000]/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
                    <div className="bg-[#121214] rounded-2xl p-6 max-w-sm w-full shadow-2xl border border-slate-800 text-left space-y-4">
                        <div className={`flex items-center gap-3 ${
                            (alertPopup.title?.toLowerCase().includes('copied') || alertPopup.title?.toLowerCase().includes('success'))
                                ? 'text-emerald-450'
                                : 'text-red-505'
                        }`}>
                            {(alertPopup.title?.toLowerCase().includes('copied') || alertPopup.title?.toLowerCase().includes('success')) ? (
                                <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            ) : (
                                <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            )}
                            <h3 className="text-base font-extrabold text-white">{alertPopup.title}</h3>
                        </div>
                        <p className="text-xs text-slate-300 leading-relaxed font-semibold whitespace-pre-wrap select-all">
                            {alertPopup.message}
                        </p>
                        <button 
                            onClick={() => setAlertPopup({ show: false, message: '', title: 'Notification' })}
                            className="bg-main hover:bg-mainHover text-white font-bold w-full py-2.5 rounded-xl transition-all text-xs text-center shadow-md shadow-main/15"
                        >
                            Okay
                        </button>
                    </div>
                </div>
            )}


            {/* Custom Confirm Popup */}
            {confirmPopup.show && (
                <div className="fixed inset-0 bg-[#000000]/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-fadeIn">
                    <div className="bg-[#121214] rounded-2xl p-6 max-w-sm w-full shadow-2xl border border-slate-800 text-left space-y-4">
                        <div className="flex items-center gap-3 text-red-500">
                            <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                            <h3 className="text-base font-extrabold text-white">{confirmPopup.title}</h3>
                        </div>
                        <p className="text-xs text-slate-300 leading-relaxed font-semibold whitespace-pre-wrap select-all">
                            {confirmPopup.message}
                        </p>
                        <div className="flex gap-3 mt-4">
                            <button 
                                onClick={() => setConfirmPopup({ show: false, message: '', title: '', onConfirm: null })}
                                className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-bold py-2.5 rounded-xl transition-all text-xs text-center"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={() => { if (confirmPopup.onConfirm) confirmPopup.onConfirm(); }}
                                className="flex-1 bg-main hover:bg-mainHover text-white font-bold py-2.5 rounded-xl transition-all text-xs text-center shadow-md shadow-main/15"
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Email Invitation Modal */}
            {showEmailModal && (
                <div className="fixed inset-0 bg-[#000000]/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-fadeIn">
                    <form 
                        onSubmit={handleSendEmailInvite}
                        className="bg-[#121214] rounded-2xl p-6 max-w-md w-full shadow-2xl border border-slate-800 text-left space-y-4"
                    >
                        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                            <h3 className="text-sm font-extrabold text-white flex items-center gap-2">
                                <svg className="w-4 h-4 text-indigo-400" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25 2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                                </svg>
                                Send Email Invitation
                            </h3>
                            <button 
                                type="button"
                                onClick={() => setShowEmailModal(false)}
                                className="text-slate-400 hover:text-white transition-colors"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="space-y-3">
                            <div>
                                <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">Recipient Email</label>
                                <input
                                    type="email"
                                    required
                                    value={emailInviteTo}
                                    onChange={(e) => setEmailInviteTo(e.target.value)}
                                    placeholder="e.g. colleague@example.com"
                                    className="w-full bg-[#18181a] border border-slate-700 rounded-lg text-xs text-white px-3 py-2.5 outline-none focus:border-main focus:ring-1 focus:ring-main transition-all font-semibold"
                                />
                            </div>

                            <div>
                                <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">Subject</label>
                                <input
                                    type="text"
                                    required
                                    value={emailInviteSubject}
                                    onChange={(e) => setEmailInviteSubject(e.target.value)}
                                    className="w-full bg-[#18181a] border border-slate-700 rounded-lg text-xs text-white px-3 py-2.5 outline-none focus:border-main focus:ring-1 focus:ring-main transition-all font-semibold"
                                />
                            </div>

                            <div>
                                <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">Message Body</label>
                                <textarea
                                    required
                                    rows="4"
                                    value={emailInviteBody}
                                    onChange={(e) => setEmailInviteBody(e.target.value)}
                                    className="w-full bg-[#18181a] border border-slate-700 rounded-lg text-xs text-white px-3 py-2.5 outline-none focus:border-main focus:ring-1 focus:ring-main transition-all font-semibold resize-none"
                                />
                            </div>
                        </div>

                        <div className="flex gap-2 justify-end pt-2 border-t border-slate-800">
                            <button 
                                type="button"
                                onClick={() => setShowEmailModal(false)}
                                className="px-4 py-2 text-xs font-bold rounded-full text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit"
                                disabled={emailInviteSending}
                                className="px-5 py-2 text-xs font-bold rounded-full bg-main text-white hover:bg-mainHover disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-main/20 flex items-center gap-1.5"
                            >
                                {emailInviteSending ? 'Sending...' : 'Send Invitation'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Google Meet style join/leave floating toast notification */}
            {joinLeaveToast && (
                <div className="fixed bottom-20 left-6 z-[200] bg-[#1f1f23]/95 border border-slate-700/60 text-white px-4 py-2.5 rounded-2xl shadow-[0_10px_30px_rgba(0,0,0,0.5)] backdrop-blur flex items-center gap-3 animate-fadeIn transition-all pointer-events-none">
                    <span className={`w-2.5 h-2.5 rounded-full ${joinLeaveToast.type === 'joined' ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`}></span>
                    <span className="text-xs font-bold font-sans">
                        {joinLeaveToast.name} <span className="font-semibold text-slate-300">{joinLeaveToast.type === 'joined' ? 'joined the call' : 'left the call'}</span>
                    </span>
                </div>
            )}

            <style dangerouslySetInnerHTML={{ __html: `
                .scale-x-flipped {
                    transform: scaleX(-1);
                }
                @keyframes floatUp {
                    0% {
                        transform: translateY(0) scale(0.6);
                        opacity: 0;
                    }
                    15% {
                        opacity: 1;
                        transform: translateY(-40px) scale(1.3);
                    }
                    100% {
                        transform: translateY(-420px) scale(0.9);
                        opacity: 0;
                    }
                }
                .animate-float-up {
                    animation: floatUp 2s cubic-bezier(0.08, 0.8, 0.16, 1) forwards;
                }
            ` }} />
        </div>
    );
};

export default CollabMeetingRoom;
